import type {
  AcceptInvitationPreview,
  AcceptInvitationRequest,
  AddProjectMemberRequest,
  AdminCreateUserRequest,
  AdminUpdateUserRequest,
  AdminUser,
  ApproveUserRequest,
  ForgotPasswordRequest,
  ResetPasswordRequest,
  RoadmapMetrics,
  BudgetItem,
  BudgetSummary,
  CreateBudgetItemRequest,
  UpdateBudgetItemRequest,
  ApprovalAction,
  ApprovalEntityType,
  ApproveMeasurementRequest,
  AuthSession,
  BoletimMedicao,
  CronogramaGranularity,
  CronogramaReport,
  DreReport,
  ActivityFeed,
  AlertsSummary,
  ApprovalsBatchRequest,
  ApprovalsBatchResponse,
  ApprovalsInbox,
  FluxoCaixaSummary,
  PortfolioSummary,
  SupplierScorecard,
  GenerateSupplierPortalTokenResponse,
  CancelPurchaseOrderRequest,
  ChangeOrder,
  ChangePasswordRequest,
  Comment,
  CommentEntityType,
  CommentVisibility,
  CreateChangeOrderRequest,
  CreateCommentRequest,
  CreateDailyLogRequest,
  CreateExternalLinkRequest,
  CreateExternalLinkResponse,
  CreateIssueRequest,
  CreateMeasurementRequest,
  CreateOwnerInvitationRequest,
  CreateOwnerInvitationResponse,
  CreatePaymentRequest,
  CreateProjectRequest,
  CreatePurchaseOrderRequest,
  CreatePurchaseRequestRequest,
  CreateReceiptRequest,
  CreateStageDependencyRequest,
  CreateStageRequest,
  CreateSupplierRequest,
  CreateTimelineEventRequest,
  DailyLog,
  DecideChangeOrderRequest,
  DecisionPurchaseRequestRequest,
  ExternalLink,
  ExternalLinkProjectView,
  GoogleSignInRequest,
  Issue,
  IssueApprovalTokenRequest,
  IssueApprovalTokenResponse,
  IssueSeverity,
  IssueStatus,
  LoginRequest,
  Measurement,
  NfeIngestRequest,
  NfeIngestResult,
  NfeMetadata,
  CreateShareLinkRequest,
  CreateShareLinkResponse,
  ListShareLinksResponse,
  RevokeShareLinkResponse,
  ResolveShareLinkResponse,
  SharedMedicaoPayload,
  SharedDrePayload,
  CreateSupplierCatalogItemRequest,
  UpdateSupplierCatalogItemRequest,
  ListSupplierCatalogResponse,
  SupplierCatalogItem,
  CurvaAbcReport,
  CreatePunchListItemRequest,
  UpdatePunchListItemRequest,
  ListPunchListResponse,
  PunchListItem,
  CreateLaborEntryRequest,
  UpdateLaborEntryRequest,
  ListLaborResponse,
  LaborEntry,
  CreateRncItemRequest,
  UpdateRncItemRequest,
  ListRncResponse,
  RncItem,
  CreateFvsTemplateRequest,
  UpdateFvsTemplateRequest,
  ListFvsTemplatesResponse,
  FvsTemplate,
  CreateFvsInspectionRequest,
  UpdateFvsInspectionRequest,
  ListFvsInspectionsResponse,
  FvsInspection,
  ExecutivoReport,
  ExecutivoPeriod,
  AdminShowcase,
  CreateShowcaseRequest,
  UpdateShowcaseRequest,
  ShowcaseLead,
  ApproveShowcaseLeadRequest,
  ShowcaseDocument,
  ShowcaseDocumentAccessLevel,
  ShowcaseDocumentChecklist,
  ShowcaseDocumentKind,
  ShowcaseDocumentTier,
  UploadShowcaseDocumentRequest,
  OwnerInvitation,
  Payment,
  Photo,
  Project,
  ProjectAttachment,
  ProjectMember,
  ProgressSnapshot,
  PurchaseOrder,
  PurchaseOrderRetention,
  PurchaseRequest,
  Receipt,
  RegisterRequest,
  ReleasePurchaseOrderRetentionRequest,
  RequestAttachmentUploadRequest,
  RequestUploadRequest,
  RequestUploadResponse,
  SettlePaymentRequest,
  StageDependency,
  Supplier,
  TimelineEvent,
  TimeviewResponse,
  UpdateIssueRequest,
  UpdateProfileRequest,
  UpdateProjectMemberRequest,
  UpdateProjectRequest,
  UpdateStageRequest,
  UpdateSupplierRequest,
  UseApprovalTokenRequest,
  WorkStage,
  // Wave 351-B/C — Unified Inbox DTO surface. Imported here so the
  // `api.getInbox()` helper returns the canonical shape and never
  // hand-rolls a parallel response interface.
  InboxResponseDTO,
  InboxTab,
  // W152 · P0-B — buyer access page (gated docs + public showcase).
  GatedDocument,
  PublicShowcase,
} from '@builder/shared';
import { useAuth } from '../store/auth.store';
import type { ServerChecklistPreview } from '../lib/trena-server-checklist';

// Wave 6 — kept local on the web side until we move into @builder/shared
// in the next consolidation pass. The shape here mirrors what the API
// returns from src/services/documents.service.ts.
export type DocType =
  | 'art'
  | 'alvara_construcao'
  | 'alvara_funcionamento'
  | 'licenca_ambiental'
  | 'licenca_municipal'
  | 'cnpj_cartao'
  | 'contrato_social'
  | 'seguro_obra'
  | 'apolice_rc'
  | 'laudo_sondagem'
  | 'projeto_estrutural'
  | 'projeto_arquitetonico'
  | 'projeto_hidraulico'
  | 'projeto_eletrico'
  | 'projeto_incendio'
  | 'habite_se'
  | 'aso'
  | 'pcmso'
  | 'pgr'
  | 'custom';

export type DocStatus =
  | 'missing'
  | 'pending_upload'
  | 'active'
  | 'expiring'
  | 'expired'
  | 'na';

export interface ProjectDocument {
  id: string;
  projectId: string;
  docType: DocType;
  title: string;
  description: string | null;
  issuingAuthority: string | null;
  documentNumber: string | null;
  issuedOn: string | null;
  expiresOn: string | null;
  responsibleUserId: string | null;
  attachmentId: string | null;
  status: DocStatus;
  notes: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  expiringSoon: boolean;
  daysUntilExpiry: number | null;
}

export interface ProjectDocumentRequirement {
  id: string;
  projectId: string;
  docType: DocType;
  requiredBy: string | null;
  notes: string | null;
  createdAt: string;
  satisfied: boolean;
  currentDocumentId: string | null;
}

export interface CreateDocumentInput {
  docType: DocType;
  title: string;
  description?: string | null;
  issuingAuthority?: string | null;
  documentNumber?: string | null;
  issuedOn?: string | null;
  expiresOn?: string | null;
  responsibleUserId?: string | null;
  attachmentId?: string | null;
  notes?: string | null;
}

export type UpdateDocumentInput = Partial<Omit<CreateDocumentInput, 'docType'>>;

// ─────────────────────────────────────── Maps (Wave geo · 2026-05-21)
//
// Wraps the backend Google Maps proxy at `/v1/maps/*`. Lives in client.ts
// so every page can call api.mapsGeocode/api.mapsDistance without an extra
// import. The static-map endpoint (`/v1/maps/static`) is consumed via a
// blob fetch in `components/maps/StaticMapThumbnail.tsx` — no client method
// here because we need to keep the Bearer token off the `<img src>`.

export interface MapsGeocodeResult {
  lat: number;
  lng: number;
  placeId: string;
  addressNormalized: string;
  cep: string | null;
  city: string | null;
  state: string | null;
}

export interface MapsDistanceRequest {
  origin: { lat: number; lng: number };
  destinations: Array<{ lat: number; lng: number }>;
}

export interface MapsDistanceRow {
  destinationIndex: number;
  distanceMeters: number | null;
  durationSeconds: number | null;
  status: 'OK' | 'NOT_FOUND' | 'ZERO_RESULTS';
}

// ─────────────────────────────────────── Weather (Wave 36 · 2026-05-22)
//
// Mirrors `apps/api/src/services/google-weather.service.ts`. Backend
// degrades to `null` / `[]` when key missing or upstream fails — UI must
// hide the chip and never error. `condition` enum matches one-for-one
// with the service mapper.
export type WeatherCondition =
  | 'clear'
  | 'cloudy'
  | 'rain_light'
  | 'rain_heavy'
  | 'storm'
  | 'snow'
  | 'fog'
  | 'unknown';

export interface WeatherCurrent {
  tempC: number;
  feelsLikeC: number;
  description: string;
  condition: WeatherCondition;
  precipitationMm: number;
  humidity: number;
  windKmh: number;
  observedAt: string;
}

export interface WeatherDayForecast {
  date: string;
  condition: WeatherCondition;
  description: string;
  tempMinC: number;
  tempMaxC: number;
  precipitationMm: number;
  precipitationProbability: number;
  windKmhMax: number;
  uvIndexMax: number;
}

export interface WeatherObservedDay {
  date: string;
  tempMinC: number;
  tempMaxC: number;
  precipitationMm: number;
  condition: WeatherCondition;
  description: string;
}

// ─────────────────────────────────────── Trena PM questions (Wave 29.4)
//
// The reflection pass in extract-pdf produces structured questions for the
// PM (sistema construtivo, acabamento, etc) and a "didNotUnderstand" list
// the user can dismiss after they've cadastrado manually. Backend route:
//   GET    /v1/projects/:pid/trena/estimates/:eid/pm-questions
//   PATCH  /v1/projects/:pid/trena/estimates/:eid/pm-questions/:qid
//   DELETE /v1/projects/:pid/trena/estimates/:eid/pm-questions/did-not-understand/:section

export type PmQuestionCategory =
  | 'sistema_construtivo'
  | 'acabamento'
  | 'estrutura'
  | 'instalacao'
  | 'ambiente'
  | 'outro';

export interface PmQuestionOption {
  value: string;
  label: string;
  impact?: string;
}

export interface PmQuestion {
  id: string;
  question: string;
  options: PmQuestionOption[];
  /**
   * Wave 29.4 questions carry rich evidence (quote + section). Wave 30.2
   * confidence-engine questions are rule-based and have no doc evidence —
   * so `evidence` is now optional (default to {} on the rendering side).
   */
  evidence?: { quote: string; docSection?: string };
  category: PmQuestionCategory;
  budgetImpact?: string;
  defaultIfSkipped?: { value: string; warning: string };
  /**
   * Wave 30.2 · distinguishes Wave 29.4 reflection questions (LLM, default
   * when undefined) from confidence-engine rule-based questions.
   */
  source?: 'llm-reflection' | 'confidence-engine';
  /**
   * Wave 30.2 · only present on `source: 'confidence-engine'`. Estimated
   * half-band reduction (cents) if the PM answers any option. UI surfaces
   * a "Reduz ± R$ X" badge.
   */
  estimatedBandReductionCents?: number;
  /**
   * Wave 30.2 · only present on `source: 'confidence-engine'`. The
   * assertion_key to POST to /confidence/assertion when answered.
   */
  assertionKey?: string;
}

export type DidNotUnderstandKind =
  | 'tabela_complexa'
  | 'ambiguidade'
  | 'ilegivel'
  | 'fora_de_escopo'
  | 'valor_suspeito'
  | 'outro';

export interface DidNotUnderstand {
  section: string;
  reason: string;
  suggestedAction: string;
  kind: DidNotUnderstandKind;
  quote?: string;
  /**
   * WAVE 29.4 bug fix · 2026-05-22 · PM-provided free-text answer to the
   * data this section asks for. When present, the panel shows the response
   * inline + a "change answer" affordance instead of the empty fill-data CTA.
   */
  pmResponse?: string;
  /** ISO timestamp of the PATCH that wrote `pmResponse`. */
  respondedAt?: string;
}

export interface AnsweredPmQuestion {
  question: PmQuestion;
  answer: string;
  answeredAt: string;
}

export interface PmQuestionsResponse {
  pendingQuestions: PmQuestion[];
  answeredQuestions: AnsweredPmQuestion[];
  didNotUnderstand: DidNotUnderstand[];
  hasOpenQuestions: boolean;
}

/**
 * Wave 329a — runtime invariant for the Danielli-class bug.
 *
 * The Danielli incident happened because a build was performed with
 * `VITE_API_BASE="https://api.builder.nullscience.ai/v1"`. Every
 * `request()` call already prefixes `/v1/...`, so the final URL was
 * `https://api.builder.nullscience.ai/v1/v1/...` → 404 on every endpoint
 * → the entire web app silently dead until the next build.
 *
 * This guard sanitizes any leftover `/v1` suffix at runtime so stale
 * deploys can't keep the bug alive, and screams loudly in console so
 * the next operator notices instead of debugging blind.
 *
 * The same sanitization runs in `vite.config.ts` at build time. This
 * is the second line of defense.
 */
function deriveApiBase(): string {
  const raw = (import.meta.env.VITE_API_BASE ?? '').trim();
  let cleaned = raw.replace(/\/+$/, '');
  if (/\/v1$/.test(cleaned)) {
    // eslint-disable-next-line no-console
    console.error(
      `[builder] CRITICAL: VITE_API_BASE ended with /v1 ("${raw}"). ` +
        `The client adds /v1 itself, so this would 404 every request. ` +
        `Stripping defensively. Fix the deploy env to avoid this in the next build.`,
    );
    cleaned = cleaned.replace(/\/v1$/, '');
  }
  return cleaned;
}
export const API_BASE = deriveApiBase();

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * P0 fix 2026-05-02 — RDO photo+save reported "Token de acesso ausente"
 * mid-session. Root cause family: the request wrapper never short-circuited
 * when `useAuth.token` was null/empty, and on 401 it only cleared state
 * without redirecting. The user got stuck staring at a confusing message
 * with no path forward. This helper centralizes "session is gone" handling
 * — clear the store, then redirect to /login (preserving the current URL
 * as a `next` query param so the user lands back where they were after
 * re-auth).
 *
 * Idempotent: if we're already on /login the redirect is skipped so we
 * don't loop. Uses `window.location.assign` (not `replace`) so the
 * previous page sits in history — handy if the user comes back via
 * back-button after logging in.
 */
function bounceToLogin(): void {
  // W123.1 PR2 · 2026-05-25 — if the backend already told us the site is
  // in maintenance, do NOT bounce to /login on a 401. The 401 in that
  // case is a side-effect of the 503 sweep (PR1 returns 503 + header
  // X-Maintenance: 1 for any path outside the whitelist, but auth-only
  // routes can degrade to 401 first depending on order). Without this
  // gate the user would hit /login → request /v1/auth/me → 503 →
  // refresh-loop in PMs sessions. The router-level redirect to
  // /maintenance takes over instead.
  if (
    typeof window !== 'undefined' &&
    (window as unknown as { __BUILDER_MAINTENANCE__?: boolean }).__BUILDER_MAINTENANCE__
  ) {
    return;
  }
  try {
    useAuth.getState().clear();
  } catch {
    /* zustand not ready in tests; safe to ignore */
  }
  if (typeof window === 'undefined') return;
  const here = window.location.pathname + window.location.search;
  if (window.location.pathname === '/login') return;
  const next = encodeURIComponent(here);
  window.location.assign(`/login?next=${next}&reason=session_expired`);
}

/**
 * W123.1 PR2 · 2026-05-25 — central handler for the X-Maintenance: 1
 * header that PR1 (API middleware) adds to every 503 response when the
 * site is in maintenance mode. Sets a global flag the router reads at
 * render time AND dispatches a `builder:maintenance` CustomEvent so
 * already-mounted components (e.g. OwnerProjectDetailPage banner) can
 * react without a remount. Idempotent: if the flag is already set, we
 * don't redispatch.
 *
 * W123.1 hotfix · 2026-05-25 — defesa em profundidade. O header
 * `X-Maintenance` é cross-origin e o browser SÓ entrega ao JS quando a
 * resposta inclui `Access-Control-Expose-Headers` listando o header
 * (corrigido em apps/api/src/middleware/cors.ts no mesmo deploy). Se
 * por qualquer motivo o header não chegar (proxy antigo, edge cache,
 * CDN strip), caímos no body JSON `{ error: 'maintenance' }` — esse
 * sempre passa por CORS porque é o próprio payload da resposta.
 * Usamos `res.clone()` pra não consumir o stream que o caller vai ler
 * logo abaixo.
 */
async function noteMaintenanceIfPresent(res: Response): Promise<void> {
  if (typeof window === 'undefined') return;
  let isMaintenance = res.headers.get('X-Maintenance') === '1';
  if (!isMaintenance && res.status === 503) {
    try {
      const ct = res.headers.get('content-type') ?? '';
      if (ct.includes('application/json')) {
        const cloned = res.clone();
        const body = (await cloned.json()) as { error?: unknown } | null;
        if (body && typeof body === 'object' && body.error === 'maintenance') {
          isMaintenance = true;
        }
      }
    } catch {
      /* body unreadable / not JSON — fall through, header-only path */
    }
  }
  if (!isMaintenance) return;
  const w = window as unknown as { __BUILDER_MAINTENANCE__?: boolean };
  if (w.__BUILDER_MAINTENANCE__) return;
  w.__BUILDER_MAINTENANCE__ = true;
  try {
    window.dispatchEvent(new CustomEvent('builder:maintenance'));
  } catch {
    /* CustomEvent unsupported (very old environments); ignore. */
  }
}

export async function request<T>(
  path: string,
  init: RequestInit & { auth?: boolean } = {},
): Promise<T> {
  const { auth = true, headers, ...rest } = init;
  const finalHeaders = new Headers(headers);
  if (!finalHeaders.has('content-type') && init.body && typeof init.body === 'string') {
    finalHeaders.set('content-type', 'application/json');
  }
  if (auth) {
    const state = useAuth.getState();
    const token = state.token;
    // P0 fix 2026-05-02 — pre-flight check. If the auth store is empty
    // OR the persisted `expiresAt` is already in the past, there's no
    // point sending a request that's guaranteed to 401. Short-circuit
    // with a clear, friendly error and bounce the user to /login. This
    // prevents the "Token de acesso ausente" trap where a stale tab
    // sat through token expiry, then on the first action the API
    // returned a confusing message with no recovery path.
    const expiresAt = state.expiresAt;
    const sessionExpired =
      !!expiresAt && new Date(expiresAt).getTime() <= Date.now();
    if (!token || sessionExpired) {
      bounceToLogin();
      throw new ApiError(
        401,
        'session_expired',
        'Sua sessão expirou. Faça login novamente.',
      );
    }
    finalHeaders.set('authorization', `Bearer ${token}`);
  }

  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, { ...rest, headers: finalHeaders });
  } catch (cause) {
    // Fetch only throws on network-layer failures: DNS, offline, CORS preflight
    // rejection, SSL, mixed-content, etc. Surface these as ApiError so the UI
    // can render a meaningful message instead of a generic fallback.
    const detail = cause instanceof Error ? cause.message : String(cause);
    throw new ApiError(
      0,
      'network_error',
      `Could not reach the server (${detail || 'network error'}). ` +
        `Check your connection — if this persists, the API origin may be wrong or blocked by CORS.`,
      { cause: detail, url: `${API_BASE}${path}` },
    );
  }

  // W123.1 PR2 · 2026-05-25 — check the maintenance header BEFORE the
  // 401 branch runs, so bounceToLogin can short-circuit when we're in
  // maintenance (see `bounceToLogin` for the rationale).
  // W123.1 hotfix · 2026-05-25 — now async (body fallback if CORS hides
  // the header). Awaited so the flag is set before the 401 branch.
  await noteMaintenanceIfPresent(res);

  if (res.status === 401 && auth) {
    // P0 fix 2026-05-02 — was just `useAuth.getState().clear()`, which
    // left the user staring at a 401 message with no redirect. Now we
    // clear AND bounce to /login with a `?next=` so the user can resume
    // their flow after re-auth.
    bounceToLogin();
  }

  let payload: unknown = null;
  const ct = res.headers.get('content-type') ?? '';
  if (ct.includes('application/json')) {
    try {
      payload = await res.json();
    } catch {
      // fall through
    }
  }

  if (!res.ok) {
    let errCode   = 'http_error';
    let errMsg    = `HTTP ${res.status}`;
    let errDetails: unknown = undefined;
    if (payload && typeof payload === 'object' && 'error' in payload) {
      const raw = (payload as { error: unknown });
      if (typeof raw.error === 'string') {
        // Shape: { error: 'some_code', ...extra_fields }
        // Pass all non-error fields as details so callers can extract them.
        errCode = raw.error;
        errMsg  = raw.error;
        const { error: _ignored, ...rest } = payload as Record<string, unknown>;
        errDetails = Object.keys(rest).length > 0 ? rest : undefined;
      } else if (raw.error && typeof raw.error === 'object') {
        // Shape: { error: { code, message, details? } }
        const e = raw.error as { code?: string; message?: string; details?: unknown };
        errCode   = e.code    ?? 'http_error';
        errMsg    = e.message ?? `HTTP ${res.status}`;
        errDetails = e.details;
      }
    }
    throw new ApiError(res.status, errCode, errMsg, errDetails);
  }

  return (payload as { data: T }).data;
}

// W146 · 2026-05-27 · Attendance record returned by GET /v1/attendance/today.
export interface AttendanceRecord {
  id: string;
  phone: string;
  workerName: string;
  recordedAt: string;
  recordedVia: 'whatsapp' | 'qr' | 'manual';
}

export const api = {
  // --- auth ---
  login: (body: LoginRequest, opts?: { turnstileToken?: string }) =>
    request<AuthSession>('/v1/auth/login', {
      method: 'POST',
      body: JSON.stringify(body),
      auth: false,
      headers: opts?.turnstileToken ? { 'cf-turnstile-response': opts.turnstileToken } : undefined,
    }),
  register: (body: RegisterRequest, opts?: { turnstileToken?: string }) =>
    request<AuthSession>('/v1/auth/register', {
      method: 'POST',
      body: JSON.stringify(body),
      auth: false,
      headers: opts?.turnstileToken ? { 'cf-turnstile-response': opts.turnstileToken } : undefined,
    }),
  loginWithGoogle: (body: GoogleSignInRequest) =>
    request<AuthSession>('/v1/auth/google', {
      method: 'POST',
      body: JSON.stringify(body),
      auth: false,
    }),
  logout: () => request<{ ok: true }>('/v1/auth/logout', { method: 'POST' }),
  me: () => request<AuthSession['user']>('/v1/auth/me'),
  /**
   * Wave 279 — narrow lazy-load for the SettingsPage profile editor.
   * pixKey was previously bundled into the AuthUser response; now it
   * lives behind this dedicated endpoint and is only fetched when the
   * user actually opens their profile page.
   */
  getMyPixKey: () =>
    request<{ pixKey: string | null }>('/v1/auth/me/pix-key'),
  updateProfile: (body: UpdateProfileRequest) =>
    request<AuthSession['user']>('/v1/auth/me', {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  changePassword: (body: ChangePasswordRequest) =>
    request<{ ok: true }>('/v1/auth/change-password', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  // Self-service "sair de outras sessões": encerra todas as sessões do
  // próprio usuário exceto a atual. A versão admin recusa revogar as
  // próprias sessões — este é o caminho do usuário.
  revokeOtherSessions: () =>
    request<{ revoked: number }>('/v1/auth/me/revoke-other-sessions', {
      method: 'POST',
    }),
  forgotPassword: (body: ForgotPasswordRequest, opts?: { turnstileToken?: string }) =>
    request<{ ok: true }>('/v1/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify(body),
      auth: false,
      headers: opts?.turnstileToken ? { 'cf-turnstile-response': opts.turnstileToken } : undefined,
    }),
  resetPassword: (body: ResetPasswordRequest, opts?: { turnstileToken?: string }) =>
    request<{ ok: true }>('/v1/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify(body),
      auth: false,
      headers: opts?.turnstileToken ? { 'cf-turnstile-response': opts.turnstileToken } : undefined,
    }),

  // --- admin: user management (Wave 13.3) ---
  adminListUsers: () => request<AdminUser[]>('/v1/admin/users'),
  adminCreateUser: (body: AdminCreateUserRequest) =>
    request<{ user: AdminUser; setupUrl: string }>('/v1/admin/users', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  adminUpdateUser: (id: string, body: AdminUpdateUserRequest) =>
    request<AdminUser>(`/v1/admin/users/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  adminForceReset: (id: string) =>
    request<{ ok: true; resetUrl: string }>(`/v1/admin/users/${id}/force-reset`, {
      method: 'POST',
    }),
  adminDeleteUser: (id: string) =>
    request<{ ok: true }>(`/v1/admin/users/${id}`, { method: 'DELETE' }),
  // #262 — full user detail panel + extra admin actions.
  adminUserDetail: (id: string) =>
    request<{
      user: AdminUser & { lastSeenAt: string | null };
      activeSessions: number;
      lastSessionCreatedAt: string | null;
      events: Array<{
        eventType: string;
        entityType: string | null;
        entityId: string | null;
        payloadJson: string | null;
        createdAt: string;
        asActor: boolean;
      }>;
      memberships: Array<{
        projectId: string;
        projectName: string;
        role: string;
        invitedAt: string | null;
      }>;
      resetTokens: Array<{
        createdAt: string;
        expiresAt: string;
        usedAt: string | null;
      }>;
    }>(`/v1/admin/users/${id}`),
  adminRevokeUserSessions: (id: string) =>
    request<{ revoked: number }>(`/v1/admin/users/${id}/revoke-sessions`, { method: 'POST' }),
  adminEnableUser: (id: string) =>
    request<{ ok: true }>(`/v1/admin/users/${id}/enable`, { method: 'POST' }),
  // Wave 314d — admin shortcut: add user to every active project at once.
  adminAssignAllProjects: (id: string, role = 'project_manager') =>
    request<{ added: number; alreadyMember: number; totalProjects: number }>(
      `/v1/admin/users/${id}/assign-all-projects`,
      { method: 'POST', body: JSON.stringify({ role }) },
    ),

  // Wave 314L — Feedback channel (curado por JP+Claude antes de virar backlog).
  submitFeedback: (body: {
    kind: 'elogio' | 'problema' | 'sugestao';
    text: string;
    routeContext?: string | null;
  }) =>
    request<FeedbackItem>(`/v1/feedback`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  adminListFeedback: (status?: string) =>
    request<FeedbackItem[]>(
      `/v1/admin/feedback${status ? `?status=${encodeURIComponent(status)}` : ''}`,
    ),
  adminUpdateFeedback: (
    id: string,
    body: { status: 'new' | 'accepted' | 'rejected' | 'done'; curatorNote?: string | null },
  ) =>
    request<FeedbackItem>(`/v1/admin/feedback/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  // #262 — soft-delete a project. Body required: { confirmName: '<exact name>' }.
  deleteProject: (id: string, confirmName: string) =>
    request<{ ok: true; projectId: string; deletedAt: string }>(
      `/v1/projects/${id}`,
      {
        method: 'DELETE',
        body: JSON.stringify({ confirmName }),
      },
    ),

  // --- Wave 33: approval queue (admin OR PM) ---
  listPendingUsers: () => request<AdminUser[]>('/v1/users/pending'),
  approveUser: (id: string, body: ApproveUserRequest) =>
    request<AdminUser>(`/v1/users/${id}/approve`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  rejectUser: (id: string) =>
    request<{ ok: true }>(`/v1/users/${id}/reject`, { method: 'POST' }),

  // --- Wave WelcomeFlow · 2026-05-20: first-time PM welcome flag ---
  markWelcomed: () =>
    request<{ welcomedAt: string }>('/v1/users/me/welcomed', { method: 'POST' }),

  // --- Final Push #5 · 2026-05-20: demo project seed + public health ---
  /**
   * POST /v1/admin/demo/seed — materializa "Casa Demo · Reforma 120m²"
   * em D1 e devolve `{ projectId, counts }`. Idempotente (re-run = no-op).
   * Throttled em 1 chamada por user por 5min.
   */
  adminSeedDemo: () =>
    request<{
      projectId: string;
      counts: {
        project: number;
        suppliers: number;
        budgetItems: number;
        pos: number;
        rdos: number;
        fvs: number;
        rfis: number;
        rncs: number;
        measurements: number;
      };
    }>('/v1/admin/demo/seed', { method: 'POST', body: JSON.stringify({}) }),
  /**
   * Final Push #6 · 2026-05-21 — DELETE /v1/admin/demo/seed/:projectId.
   * Cleanup pro PM que terminou de explorar e quer apagar a obra demo
   * antes de criar a real. Backend gate-checa `is_test_data = 1` antes
   * de apagar, então passar projectId de obra real devolve 403.
   */
  adminDeleteDemoProject: (projectId: string) =>
    request<{ projectId: string; deleted: boolean }>(
      `/v1/admin/demo/seed/${encodeURIComponent(projectId)}`,
      { method: 'DELETE' },
    ),
  /**
   * GET /v1/health — public. Sem bearer auth (passamos `auth: false`).
   * Usado pelo StatusPage pra polling cada 30s.
   */
  publicHealth: () =>
    request<{
      status: 'ok' | 'degraded' | 'down';
      ok: boolean;
      env: 'development' | 'production';
      time: string;
      checks: {
        db: { ok: boolean; latencyMs?: number; error?: string };
        r2: { ok: boolean; latencyMs?: number; error?: string };
        cron: { ok: boolean; lastRunAt?: string | null; error?: string };
      };
    }>('/v1/health', { auth: false }),
  /**
   * GET /v1/health/history — public. Últimos 24h de checks (288 cap).
   */
  publicHealthHistory: () =>
    request<{
      history: Array<{
        time: string;
        status: string;
        db_ms: number | null;
        r2_ms: number | null;
      }>;
    }>('/v1/health/history', { auth: false }),

  // --- Wave 94/95: presence + Housy nudges ---
  heartbeat: (route: string | null) => request<{ ok: true }>('/v1/me/heartbeat', {
    method: 'POST',
    body: JSON.stringify({ route }),
  }),
  listPresence: () => request<Array<{
    user_id: string; full_name: string; email: string; avatar_url: string | null;
    last_seen_at: string; last_route: string | null; client_kind: string;
  }>>('/v1/me/presence'),
  listNudges: () => request<Array<{
    id: string; kind: string; body: string;
    context_json: string; severity: 'info' | 'warm' | 'warn' | 'urgent';
    created_at: string;
  }>>('/v1/me/nudges'),
  markNudgeSeen: (id: string) =>
    request<{ ok: true }>(`/v1/me/nudges/${id}/seen`, { method: 'POST' }),
  dismissNudge: (id: string) =>
    request<{ ok: true }>(`/v1/me/nudges/${id}/dismiss`, { method: 'POST' }),

  // --- Wave 90: Housy conversation list + history ---
  listAgentConversations: () => request<Array<{
    conversationId: string;
    lastAt: string;
    messageCount: number;
    preview: string | null;
  }>>('/v1/agent/conversations'),
  getAgentConversation: (id: string) => request<Array<{
    id: string; role: string; content: string;
    toolCalls: unknown[]; suspicionLevel: string; createdAt: string;
  }>>(`/v1/agent/conversations/${id}`),

  // --- Wave 87: self-service admin/ops API keys ---
  listOpsKeys: () => request<Array<{
    id: string; name: string; prefix: string;
    created_at: string; last_used_at: string | null; revoked_at: string | null;
  }>>('/v1/me/ops-keys'),
  mintOpsKey: (name: string) => request<{
    id: string; plaintext: string; prefix: string;
  }>('/v1/me/ops-keys', {
    method: 'POST',
    body: JSON.stringify({ name }),
  }),
  revokeOpsKey: (id: string) =>
    request<{ ok: true }>(`/v1/me/ops-keys/${id}/revoke`, { method: 'POST' }),

  // --- Wave 303 / item D — email inbox settings ---
  getEmailInbox: () => request<{
    addresses: Array<{
      id: string; address: string; hash: string; isAlias: boolean;
      active: boolean; createdAt: string;
    }>;
    blocklist: Array<{
      id: string; scope: string; mode: 'address' | 'domain'; value: string;
      reason: string | null; createdAt: string;
    }>;
    stats: { delivered7d: number; blocked7d: number; noRoute7d: number };
  }>('/v1/me/email-inbox'),
  blockEmailSender: (sender: string) =>
    request<{ id: string; mode: 'address' | 'domain'; value: string; alreadyExists: boolean }>(
      '/v1/me/email-inbox/blocklist',
      { method: 'POST', body: JSON.stringify({ sender }) },
    ),
  unblockEmailSender: (id: string) =>
    request<{ ok: true }>(`/v1/me/email-inbox/blocklist/${id}`, { method: 'DELETE' }),
  getProjectEmailAddress: (projectId: string) =>
    request<{ address: string; hash: string; created: boolean }>(
      `/v1/projects/${projectId}/email-address`,
    ),
  getPurchaseOrderEmailAddress: (poId: string) =>
    request<{ address: string; hash: string; code: string; created: boolean }>(
      `/v1/purchase-orders/${poId}/email-address`,
    ),
  getIssueEmailAddress: (issueId: string) =>
    request<{ address: string; hash: string; title: string; created: boolean }>(
      `/v1/issues/${issueId}/email-address`,
    ),

  // Wave 308 — per-project calendar.
  getProjectCalendar: (projectId: string, from: string, to: string) =>
    request<{
      projectId: string;
      from: string;
      to: string;
      count: number;
      events: Array<{
        date: string;
        kind:
          | 'rdo' | 'issue' | 'medicao'
          | 'po_delivery' | 'po_issued'
          | 'stage_start' | 'stage_end';
        id: string;
        title: string;
        status: string | null;
        link: string;
      }>;
    }>(`/v1/projects/${projectId}/calendar?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`),

  // Generic helpers used by Wave 79+ pages.
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'POST', body: JSON.stringify(body ?? {}) }),

  // --- Wave 40/43: Vitrine (showcases) admin ---
  listShowcases: () =>
    request<AdminShowcase[]>('/v1/showcases'),
  getShowcase: (id: string) =>
    request<AdminShowcase>(`/v1/showcases/${id}`),
  createShowcase: (body: CreateShowcaseRequest) =>
    request<AdminShowcase>('/v1/showcases', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  updateShowcase: (id: string, body: UpdateShowcaseRequest) =>
    request<AdminShowcase>(`/v1/showcases/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  /**
   * #265 follow-up — manual marketing autofill for existing vitrines.
   * Idempotent: only writes to NULL columns, never overwrites a manual
   * edit. Returns the draft so the UI can show a feedback toast.
   */
  refillShowcaseMarketing: (id: string) =>
    request<{
      seoTitle: string;
      seoDescription: string;
      heroEyebrow: string;
      heroHeadline: string;
    }>(`/v1/showcases/${id}/refill-marketing`, { method: 'POST' }),
  // W130.A · rate-limit observability for the Vitrine admin list header.
  getShowcaseLeadStats: () =>
    request<{
      total: number;
      pending: number;
      approvedLast7d: number;
      blockedLast24h: number;
      blockedLast7d: number;
    }>('/v1/showcases/leads/stats'),
  listShowcaseLeads: (id: string, onlyPending: boolean) =>
    request<ShowcaseLead[]>(
      `/v1/showcases/${id}/leads${onlyPending ? '?pending=1' : ''}`,
    ),
  approveShowcaseLead: (leadId: string, body: ApproveShowcaseLeadRequest) =>
    request<{ lead: ShowcaseLead; rawToken: string }>(
      `/v1/showcases/leads/${leadId}/approve`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  rejectShowcaseLead: (leadId: string) =>
    request<{ ok: true }>(`/v1/showcases/leads/${leadId}/reject`, {
      method: 'POST',
    }),
  // W153 · P1 — revoga o link de acesso de um interessado aprovado.
  revokeShowcaseLeadAccess: (leadId: string) =>
    request<{ ok: true }>(`/v1/showcases/leads/${leadId}/revoke`, {
      method: 'POST',
    }),
  // Wave 154 — transfere a casa vendida ao comprador como proprietário.
  // `origin` é passado para o backend compor o link /accept-invitation
  // com o domínio do app (mesmo padrão do convite de membro).
  transferShowcaseToOwner: (leadId: string) =>
    request<{
      ownerLink: string;
      buyerName: string;
      buyerEmail: string;
      firstTransfer: boolean;
    }>(
      `/v1/showcases/leads/${leadId}/transfer-owner?origin=${encodeURIComponent(
        window.location.origin,
      )}`,
      { method: 'POST' },
    ),
  listShowcaseDocuments: (id: string) =>
    request<ShowcaseDocument[]>(`/v1/showcases/${id}/documents`),
  getShowcaseChecklist: (id: string) =>
    request<ShowcaseDocumentChecklist>(`/v1/showcases/${id}/checklist`),
  uploadShowcaseDocument: (
    id: string,
    body: UploadShowcaseDocumentRequest,
  ) =>
    request<ShowcaseDocument>(`/v1/showcases/${id}/documents`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  // Wave 65 · single-shot file upload (multipart). Uses the new
  // /documents/upload endpoint that writes the file to R2 + registers
  // the metadata row in one request.
  uploadShowcaseDocumentFile: async (
    id: string,
    file: File,
    meta: {
      kind: ShowcaseDocumentKind;
      tier: ShowcaseDocumentTier;
      accessLevel: ShowcaseDocumentAccessLevel;
      title: string;
      description?: string;
    },
  ) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('meta', JSON.stringify(meta));
    const token = useAuth.getState().token;
    const res = await fetch(`${API_BASE}/v1/showcases/${id}/documents/upload`, {
      method: 'POST',
      body: fd,
      headers: token ? { authorization: `Bearer ${token}` } : {},
    });
    const payload = (await res.json().catch(() => null)) as { data?: ShowcaseDocument; error?: { message: string } } | null;
    if (!res.ok || !payload?.data) {
      throw new ApiError(res.status, 'upload_failed', payload?.error?.message ?? `HTTP ${res.status}`);
    }
    return payload.data;
  },
  deleteShowcaseDocument: (docId: string) =>
    request<{ ok: true }>(`/v1/showcases/documents/${docId}`, {
      method: 'DELETE',
    }),
  setShowcasePhotoPublish: (id: string, photoId: string, publish: boolean) =>
    request<{ ok: true }>(`/v1/showcases/${id}/photos/${photoId}/publish`, {
      method: 'POST',
      body: JSON.stringify({ publish }),
    }),
  // ── Wave 44.14 · project_specs CRUD ───────────────────────────
  listShowcaseSpecs: (id: string) =>
    request<Array<{
      id: string;
      namespace: string;
      key: string;
      value: string;
      unit: string | null;
      source: string | null;
      notes: string | null;
    }>>(`/v1/showcases/${id}/specs`),
  upsertShowcaseSpec: (
    id: string,
    body: { namespace: string; key: string; value: string; unit?: string | null; source?: string | null; notes?: string | null },
  ) =>
    request<{ ok: true }>(`/v1/showcases/${id}/specs`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  patchShowcaseSpec: (
    id: string,
    specId: string,
    body: Partial<{ value: string; unit: string | null; source: string | null; notes: string | null }>,
  ) =>
    request<{ ok: true }>(`/v1/showcases/${id}/specs/${specId}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  deleteShowcaseSpec: (id: string, specId: string) =>
    request<{ ok: true }>(`/v1/showcases/${id}/specs/${specId}`, {
      method: 'DELETE',
    }),
  // ── Wave 45 · Messaging ──────────────────────────────────────
  listInbox: (opts: { onlyUnread?: boolean; folder?: 'inbox' | 'sent' | 'deleted' } = {}) => {
    const p = new URLSearchParams();
    if (opts.onlyUnread) p.set('unread', '1');
    if (opts.folder && opts.folder !== 'inbox') p.set('folder', opts.folder);
    const qs = p.toString();
    return request<Array<{
      id: string;
      tenantId: string;
      projectId: string | null;
      parentMessageId: string | null;
      authorUserId: string | null;
      kind: 'user' | 'system' | 'agent_alert';
      source: string | null;
      subject: string | null;
      body: string;
      metadata: Record<string, unknown> | null;
      createdAt: string;
      editedAt: string | null;
      readAt: string | null;
      author: { id: string; fullName: string; email: string } | null;
    }>>(`/v1/messages/inbox${qs ? `?${qs}` : ''}`);
  },
  getHousyDigest: () =>
    request<{
      unreadMessages: number;
      rncsAssigned: number;
      pendingApprovals: number;
      overdueMedicoes: number;
      total: number;
    }>('/v1/agent/digest'),
  getMessageThread: (id: string) =>
    request<{
      rootId: string;
      messages: Array<{
        id: string;
        tenantId: string;
        projectId: string | null;
        parentMessageId: string | null;
        authorUserId: string | null;
        kind: 'user' | 'system' | 'agent_alert';
        source: string | null;
        subject: string | null;
        body: string;
        metadata: Record<string, unknown> | null;
        createdAt: string;
        editedAt: string | null;
        readAt: string | null;
        author: { id: string; fullName: string; email: string } | null;
      }>;
    }>(`/v1/messages/thread/${encodeURIComponent(id)}`),
  deleteMessages: (messageIds: string[]) =>
    request<{ ok: true }>('/v1/messages/delete', {
      method: 'POST',
      body: JSON.stringify({ messageIds }),
    }),

  // Wave 316 — Message reactions (curated 6-code set: tu/hr/td/ey/fr/ck).
  //
  // Wave 316b · BUG FIX: o `request<T>` helper já desembrulha `payload.data`
  // (linha 292). Eu havia declarado `request<{ data: [...] }>`, o que fez
  // TS aceitar `r.data` no callsite, mas em runtime `r.data` era undefined
  // (porque `r` já era o array). Crash: `Cannot read properties of
  // undefined (reading 'filter')`. Agora declaro `request<Array<...>>` e os
  // callsites usam `r` diretamente.
  listReactions: (messageIds: string[]) =>
    messageIds.length === 0
      ? Promise.resolve(
          [] as Array<{
            messageId: string;
            code: 'tu' | 'hr' | 'td' | 'ey' | 'fr' | 'ck';
            count: number;
            reactedByViewer: boolean;
          }>,
        )
      : request<
          Array<{
            messageId: string;
            code: 'tu' | 'hr' | 'td' | 'ey' | 'fr' | 'ck';
            count: number;
            reactedByViewer: boolean;
          }>
        >(`/v1/messages/reactions?ids=${encodeURIComponent(messageIds.join(','))}`),
  addReaction: (messageId: string, code: 'tu' | 'hr' | 'td' | 'ey' | 'fr' | 'ck') =>
    request<
      Array<{
        messageId: string;
        code: 'tu' | 'hr' | 'td' | 'ey' | 'fr' | 'ck';
        count: number;
        reactedByViewer: boolean;
      }>
    >(`/v1/messages/${encodeURIComponent(messageId)}/reactions`, {
      method: 'POST',
      body: JSON.stringify({ code }),
    }),
  removeReaction: (messageId: string, code: 'tu' | 'hr' | 'td' | 'ey' | 'fr' | 'ck') =>
    request<
      Array<{
        messageId: string;
        code: 'tu' | 'hr' | 'td' | 'ey' | 'fr' | 'ck';
        count: number;
        reactedByViewer: boolean;
      }>
    >(`/v1/messages/${encodeURIComponent(messageId)}/reactions/${code}`, {
      method: 'DELETE',
    }),
  restoreMessages: (messageIds: string[]) =>
    request<{ ok: true }>('/v1/messages/restore', {
      method: 'POST',
      body: JSON.stringify({ messageIds }),
    }),
  getUnreadCount: () =>
    request<{ unread: number }>('/v1/messages/unread-count'),
  markMessagesRead: (messageIds: string[]) =>
    request<{ ok: true }>('/v1/messages/mark-read', {
      method: 'POST',
      body: JSON.stringify({ messageIds }),
    }),
  markAllMessagesRead: () =>
    request<{ ok: true }>('/v1/messages/mark-read', {
      method: 'POST',
      body: JSON.stringify({ all: true }),
    }),
  sendMessage: (body: {
    recipientUserIds?: string[];
    body: string;
    subject?: string | null;
    parentMessageId?: string | null;
    projectId?: string | null;
    attachmentIds?: string[];
  }) =>
    request<{ id: string }>('/v1/messages/send', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  uploadMessageAttachment: async (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    const token = useAuth.getState().token;
    const res = await fetch(`${API_BASE}/v1/messages/upload-attachment`, {
      method: 'POST',
      body: fd,
      headers: token ? { authorization: `Bearer ${token}` } : {},
    });
    const payload = (await res.json()) as { data?: { id: string; filename: string; contentType: string; sizeBytes: number }; error?: { message: string } };
    if (!res.ok || !payload.data) {
      throw new ApiError(res.status, 'upload_failed', payload.error?.message ?? `HTTP ${res.status}`);
    }
    return payload.data;
  },
  messageAttachmentDownloadUrl: (id: string) =>
    `${API_BASE}/v1/messages/attachment/${encodeURIComponent(id)}/download`,
  /**
   * Wave 56 fix · attachment download. Clicking a plain <a href> on
   * the auth-gated download endpoint sends NO Authorization header,
   * so the API returns 401. We fetch with the bearer token, turn the
   * response into a blob, and trigger a synthetic download via an
   * anchor click. Same UX, just one indirection.
   */
  downloadMessageAttachment: async (id: string, suggestedFilename: string) => {
    const token = useAuth.getState().token;
    const res = await fetch(
      `${API_BASE}/v1/messages/attachment/${encodeURIComponent(id)}/download`,
      { headers: token ? { authorization: `Bearer ${token}` } : {} },
    );
    if (!res.ok) {
      throw new ApiError(res.status, 'download_failed', `HTTP ${res.status}`);
    }
    const blob = await res.blob();
    const objUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objUrl;
    a.download = suggestedFilename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    // Free the blob after the browser has processed the click.
    setTimeout(() => URL.revokeObjectURL(objUrl), 1000);
  },
  // --- Wave 54 spec extractor ---
  extractSpecPreview: (projectId: string, text: string) =>
    request<{
      projectId: string;
      proposed: Array<{ namespace: string; key: string; value: string; unit: string | null; notes: string | null }>;
      rawResponse: string;
    }>(`/v1/projects/${encodeURIComponent(projectId)}/extract-spec`, {
      method: 'POST',
      body: JSON.stringify({ text }),
    }),
  // Wave 65 · PDF spec extractor (multipart upload)
  extractSpecPreviewPdf: async (projectId: string, file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    const token = useAuth.getState().token;
    const res = await fetch(
      `${API_BASE}/v1/projects/${encodeURIComponent(projectId)}/extract-spec/pdf`,
      {
        method: 'POST',
        body: fd,
        headers: token ? { authorization: `Bearer ${token}` } : {},
      },
    );
    type Resp = {
      data?: {
        projectId: string;
        proposed: Array<{
          namespace: string;
          key: string;
          value: string;
          unit: string | null;
          notes: string | null;
        }>;
        rawResponse: string;
        extractedChars: number;
        extractedPreview: string;
      };
      error?: { message: string };
    };
    const payload = (await res.json().catch(() => null)) as Resp | null;
    if (!res.ok || !payload?.data) {
      throw new ApiError(
        res.status,
        'extract_pdf_failed',
        payload?.error?.message ?? `HTTP ${res.status}`,
      );
    }
    return payload.data;
  },
  extractSpecCommit: (
    projectId: string,
    specs: Array<{ namespace: string; key: string; value: string; unit: string | null; notes: string | null }>,
  ) =>
    request<{ inserted: number; skipped: number }>(
      `/v1/projects/${encodeURIComponent(projectId)}/extract-spec/commit`,
      {
        method: 'POST',
        body: JSON.stringify({ specs }),
      },
    ),
  searchUsers: (q: string) =>
    request<Array<{
      id: string;
      fullName: string;
      email: string;
      role: string;
      avatarUrl: string | null;
      handle: string;
    }>>(`/v1/messages/users/search?q=${encodeURIComponent(q)}`),
  getNotificationPrefs: () =>
    request<{
      userId: string;
      agentAlertIntensity: 'off' | 'critical_only' | 'all_suspicious' | 'everything';
      mutedThreadIds: string[];
    }>('/v1/messages/notification-prefs'),
  setNotificationPrefs: (body: {
    agentAlertIntensity?: 'off' | 'critical_only' | 'all_suspicious' | 'everything';
    mutedThreadIds?: string[];
  }) =>
    request<{
      userId: string;
      agentAlertIntensity: 'off' | 'critical_only' | 'all_suspicious' | 'everything';
      mutedThreadIds: string[];
    }>('/v1/messages/notification-prefs', {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  /** Fire a synthetic agent alert at the user's current intensity.
   *  Lets the Notificações settings card give a verifiable outcome
   *  without waiting for a real suspicious chat to surface. The
   *  endpoint respects the user's prefs — if 'off', no inbox row
   *  is created (and `delivered: false` comes back). */
  sendNotificationTest: () =>
    request<{ delivered: boolean; intensity: string }>(
      '/v1/messages/notification-prefs/test',
      { method: 'POST', body: JSON.stringify({}) },
    ),
  adminBackup: () =>
    request<{ ok: true; key: string; sizeBytes: number; takenAt: string }>(
      '/v1/admin/backup',
      { method: 'POST' },
    ),
  adminExportCsvUrl: (table: string) =>
    `${API_BASE}/v1/admin/export?table=${encodeURIComponent(table)}`,
  /**
   * The CSV endpoint requires an Authorization header, which a plain
   * `<a href>` anchor can't send. We fetch the blob with auth and trigger
   * the download via a temporary object URL. The content-disposition
   * header from the server sets the filename — we pull it out of the
   * response and hand it to the anchor so the download keeps the same
   * `<table>-YYYY-MM-DD.csv` shape the server chose.
   */
  adminDownloadCsv: async (table: string): Promise<void> => {
    const token = useAuth.getState().token;
    const headers = new Headers();
    if (token) headers.set('authorization', `Bearer ${token}`);
    const res = await fetch(
      `${API_BASE}/v1/admin/export?table=${encodeURIComponent(table)}`,
      { headers },
    );
    if (!res.ok) {
      throw new ApiError(
        res.status,
        'export_failed',
        `Não foi possível exportar ${table} (HTTP ${res.status}).`,
      );
    }
    const disposition = res.headers.get('content-disposition') ?? '';
    const match = disposition.match(/filename="([^"]+)"/);
    const filename = match?.[1] ?? `${table}.csv`;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    // Give the browser a tick to start the download before we revoke.
    setTimeout(() => URL.revokeObjectURL(url), 1_000);
  },

  // --- maps (Wave geo · 2026-05-21) ---
  /**
   * Geocoding via the backend wrapper (`/v1/maps/geocode`). Returns `null`
   * when no result, never throws on ZERO_RESULTS — backend already maps it.
   */
  mapsGeocode: (query: string) =>
    request<MapsGeocodeResult | null>('/v1/maps/geocode', {
      method: 'POST',
      body: JSON.stringify({ query }),
    }),
  /**
   * Distance Matrix batch. Max 25 destinations per call (Google quota);
   * caller is expected to slice if they have more. See
   * `hooks/useSupplierDistance.ts` for the standard wrapper.
   */
  mapsDistance: (body: MapsDistanceRequest) =>
    request<MapsDistanceRow[]>('/v1/maps/distance', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // --- weather (Wave 36 · 2026-05-22) ---
  /**
   * Current conditions for a project (requires lat/lng saved on the obra).
   * Returns `null` when backend has no API key or upstream failed —
   * caller must hide the UI element silently in that case.
   * Throws ApiError(422, 'project_missing_geo') quando a obra existe mas
   * ainda não foi geocoded; UI mostra hint "adicione endereço".
   */
  getWeatherCurrent: (projectId: string) =>
    request<WeatherCurrent | null>(`/v1/projects/${projectId}/weather/current`),
  /**
   * 7-day daily forecast. Returns [] on failure / missing key.
   */
  getWeatherForecast: (projectId: string) =>
    request<WeatherDayForecast[]>(`/v1/projects/${projectId}/weather/forecast`),
  /**
   * Observed weather for `date` (YYYY-MM-DD). Only today/yesterday are
   * supported by the upstream history window — older dates return null.
   */
  getWeatherObserved: (projectId: string, date: string) =>
    request<WeatherObservedDay | null>(
      `/v1/projects/${projectId}/weather/observed?date=${encodeURIComponent(date)}`,
    ),
  /**
   * Ad-hoc observed lookup com lat/lng explícitos — usado quando o
   * caller já sabe a localização e não tem projectId pra consultar.
   */
  getWeatherObservedAdHoc: (lat: number, lng: number, date: string) =>
    request<WeatherObservedDay | null>(
      `/v1/weather/observed?lat=${lat}&lng=${lng}&date=${encodeURIComponent(date)}`,
    ),

  // --- projects ---
  listProjects: (opts?: { scope?: 'prod' | 'playground' | 'all' }) =>
    request<Project[]>(`/v1/projects${opts?.scope ? `?scope=${opts.scope}` : ''}`),
  createProject: (body: CreateProjectRequest) =>
    request<Project>('/v1/projects', { method: 'POST', body: JSON.stringify(body) }),
  getProject: (id: string) => request<Project>(`/v1/projects/${id}`),
  // W147 · 2026-05-28 · QR token endpoints for attendance check-in
  getProjectQrToken: (projectId: string) =>
    request<{ qrToken: string | null }>(`/v1/projects/${projectId}/qr-token`),
  rotateProjectQrToken: (projectId: string) =>
    request<{ qrToken: string }>(`/v1/projects/${projectId}/qr-token`, { method: 'POST' }),
  /**
   * P0 anti-fantasma · 2026-05-20 · bate o martelo da obra.
   * Stamps started_at = now and bumps status 'planning' → 'in_progress'.
   * Idempotente: chamar duas vezes não duplica nada.
   */
  startProject: (id: string) =>
    request<Project>(`/v1/projects/${id}/start`, { method: 'POST' }),
  /** Obra Hoje · Project-scoped operating queue. */
  getObraHoje: (projectId: string) =>
    request<ObraHojeQueue>(`/v1/projects/${projectId}/obra-hoje`),
  /**
   * W123.5 · 2026-05-26 · Próxima Ação determinística da obra.
   * Algoritmo em apps/api/src/services/next-action.service.ts.
   * Substitui as "sugestões" genéricas do Builder por verdade real do
   * estado da obra (planning / active / completed). Cravado em JP-1.
   */
  getProjectNextAction: (projectId: string) =>
    request<ProjectNextAction>(`/v1/projects/${projectId}/next-action`),
  /**
   * FEATURE B1 · `/hoje cross-obra` · 2026-05-21.
   * Aggregates obra-hoje across every active project the PM can see and
   * ranks the top 3 actions by composite urgency. Cached 30s server-side.
   */
  getHojeCrossObra: () =>
    request<HojeCrossObra>('/v1/me/hoje-cross-obra'),
  /**
   * FEATURE B2 · Netflix-style owner project timeline (H3.2 · 2026-05-21).
   * Aggregates milestones, approvals, RNCs, photos and monthly reports
   * already produced for the project. Cached 5min server-side.
   */
  getOwnerProjectTimeline: (projectId: string) =>
    request<OwnerProjectTimeline>(`/v1/owner/projects/${projectId}/timeline`),
  /** Wave UUUU · Qualidade Hub — cross-project quality aggregator. */
  getQualidadeHub: () =>
    request<QualidadeHubData>('/v1/qualidade/hub'),
  /** Wave FFFFF · Project-scoped activity feed. */
  listProjectActivity: (
    projectId: string,
    opts: { limit?: number; cursor?: string; sinceDays?: number } = {},
  ) => {
    const qs = new URLSearchParams();
    if (opts.limit) qs.set('limit', String(opts.limit));
    if (opts.cursor) qs.set('cursor', opts.cursor);
    if (opts.sinceDays) qs.set('sinceDays', String(opts.sinceDays));
    const query = qs.toString();
    return request<ProjectActivityPage>(
      `/v1/projects/${projectId}/atividade${query ? `?${query}` : ''}`,
    );
  },
  /**
   * Wave NNNNN · Project-level photo gallery — paginated + filterable.
   *
   * Returns photos uploaded across the project's RDOs and direct uploads,
   * each enriched with RDO context (date + narrative snippet) and the
   * work-stage that the UI surfaces as "Ambiente". Signed URLs are valid
   * for 1 hour — long enough for lazy-loading on infinite scroll.
   */
  listProjectGalleryPhotos: (
    projectId: string,
    opts: { month?: string; q?: string; room?: string; cursor?: string; limit?: number } = {},
  ) => {
    const qs = new URLSearchParams();
    if (opts.month) qs.set('month', opts.month);
    if (opts.q) qs.set('q', opts.q);
    if (opts.room) qs.set('room', opts.room);
    if (opts.cursor) qs.set('cursor', opts.cursor);
    if (opts.limit) qs.set('limit', String(opts.limit));
    const query = qs.toString();
    return request<GalleryPhotoPage>(
      `/v1/projects/${projectId}/photo-gallery${query ? `?${query}` : ''}`,
    );
  },
  updateProject: (id: string, body: UpdateProjectRequest) =>
    request<Project>(`/v1/projects/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  listMembers: (id: string) => request<ProjectMember[]>(`/v1/projects/${id}/members`),
  addMember: (id: string, body: AddProjectMemberRequest) =>
    request<ProjectMember>(`/v1/projects/${id}/members`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  updateMember: (id: string, userId: string, body: UpdateProjectMemberRequest) =>
    request<ProjectMember>(`/v1/projects/${id}/members/${userId}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  removeMember: (id: string, userId: string) =>
    request<{ ok: true }>(`/v1/projects/${id}/members/${userId}`, { method: 'DELETE' }),
  // Wave 314c — set/clear per-project scope label (decorative, drives banner).
  setMemberScope: (id: string, userId: string, scopeLabel: string | null) =>
    request<ProjectMember>(`/v1/projects/${id}/members/${userId}/scope`, {
      method: 'PATCH',
      body: JSON.stringify({ scopeLabel }),
    }),
  // ── Wave 285 / P1b · project_specs universal read ─────────────
  // Returns every spec row for a project. Optionally filter by `ns`
  // (e.g. 'areas', 'materials', 'aberturas'). Used by Visão Geral
  // ficha-técnica, the PDF cover blocks, and Housy bulk-prefetch.
  listProjectSpecs: (id: string, ns?: string) => {
    const qs = ns ? `?ns=${encodeURIComponent(ns.toLowerCase())}` : '';
    return request<Array<{
      id: string;
      namespace: string;
      key: string;
      value: string;
      unit: string | null;
      source: string | null;
      notes: string | null;
    }>>(`/v1/projects/${id}/specs${qs}`);
  },

  // --- invitations ---
  listInvitations: (projectId: string) =>
    request<OwnerInvitation[]>(`/v1/projects/${projectId}/invitations`),
  createInvitation: (projectId: string, body: CreateOwnerInvitationRequest) => {
    const origin = encodeURIComponent(window.location.origin);
    return request<CreateOwnerInvitationResponse>(
      `/v1/projects/${projectId}/invitations?origin=${origin}`,
      { method: 'POST', body: JSON.stringify(body) },
    );
  },
  revokeInvitation: (projectId: string, invitationId: string) =>
    request<{ ok: true }>(`/v1/projects/${projectId}/invitations/${invitationId}`, {
      method: 'DELETE',
    }),
  // W147 · 2026-05-28 — preview é endpoint PÚBLICO. Sem `auth: false`,
  // o `request<>` short-circuita com 401 sintético "Sua sessão expirou"
  // pra qualquer visitante deslogado (mestre/pedreiro/cliente). Esse
  // foi o bug que travou todo o fluxo de convite no audit 2026-05-28.
  previewInvitation: (token: string) =>
    request<AcceptInvitationPreview>(
      `/v1/invitations/preview?token=${encodeURIComponent(token)}`,
      { auth: false },
    ),
  acceptInvitation: (body: AcceptInvitationRequest, opts?: { turnstileToken?: string }) =>
    request<AuthSession>('/v1/invitations/accept', {
      method: 'POST',
      body: JSON.stringify(body),
      auth: false,
      headers: opts?.turnstileToken ? { 'cf-turnstile-response': opts.turnstileToken } : undefined,
    }),

  // --- project templates ---
  applyProjectTemplate: (
    projectId: string,
    body: { kind: 'residencial' | 'comercial' | 'reforma' | 'popular' | 'sobrado'; surface: 'orcamento' | 'cronograma' },
  ) =>
    request<{ inserted: number; skipped: number }>(
      `/v1/projects/${projectId}/templates/apply`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  // --- Wave LLLLL · estimate templates (Trena) ---
  // Wave 13 (Onda B) · B10 · `obraType` opcional pra diferenciar templates
  // por tipo de obra. Valores domain-known: residencial_popular |
  // residencial_medio | comercial_loja | industrial_galpao.
  saveEstimateAsTemplate: (body: {
    estimateId: string;
    name: string;
    description?: string | null;
    scope: 'residencial' | 'comercial' | 'reforma' | 'outro';
    obraType?:
      | 'residencial_popular'
      | 'residencial_medio'
      | 'comercial_loja'
      | 'industrial_galpao'
      | null;
  }) =>
    request<{
      templateId: string;
      measurementCount: number;
      assumptionCount: number;
      compositionPickCount: number;
    }>('/v1/estimate-templates', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  listEstimateTemplates: (
    arg?:
      | 'residencial'
      | 'comercial'
      | 'reforma'
      | 'outro'
      | {
          scope?: 'residencial' | 'comercial' | 'reforma' | 'outro';
          obraType?:
            | 'residencial_popular'
            | 'residencial_medio'
            | 'comercial_loja'
            | 'industrial_galpao';
        },
  ) => {
    // Compat: callers antigos passam scope direto como string.
    const scope = typeof arg === 'string' ? arg : arg?.scope;
    const obraType = typeof arg === 'object' && arg ? arg.obraType : undefined;
    const params = new URLSearchParams();
    if (scope) params.set('scope', scope);
    if (obraType) params.set('obra_type', obraType);
    const qs = params.toString();
    return request<{
      items: Array<{
        id: string;
        tenantId: string;
        name: string;
        description: string | null;
        scope: 'residencial' | 'comercial' | 'reforma' | 'outro';
        obraType: string | null;
        sourceEstimateId: string | null;
        isPublic: boolean;
        createdBy: string;
        createdAt: string;
        measurementCount: number;
        assumptionCount: number;
        compositionPickCount: number;
      }>;
    }>(`/v1/estimate-templates${qs ? `?${qs}` : ''}`);
  },
  applyEstimateTemplate: (templateId: string, body: { estimateId: string }) =>
    request<{
      applicationId: string;
      measurementsInserted: number;
      assumptionsInserted: number;
    }>(`/v1/estimate-templates/${templateId}/apply`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  deleteEstimateTemplate: (templateId: string) =>
    request<{ deleted: boolean }>(`/v1/estimate-templates/${templateId}`, {
      method: 'DELETE',
    }),

  // --- Wave OOOOO · clone (duplicate) estimate ---
  cloneEstimate: (sourceEstimateId: string, body?: { name?: string }) =>
    request<{
      id: string;
      name: string;
      sourceEstimateId: string;
      measurementsCopied: number;
      assumptionsCopied: number;
      compositionPicksCopied: number;
    }>(`/v1/estimates/${sourceEstimateId}/clone`, {
      method: 'POST',
      body: JSON.stringify(body ?? {}),
    }),

  // --- stages ---
  listStages: (projectId: string) => request<WorkStage[]>(`/v1/projects/${projectId}/stages`),
  createStage: (projectId: string, body: CreateStageRequest) =>
    request<WorkStage>(`/v1/projects/${projectId}/stages`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  updateStage: (projectId: string, stageId: string, body: UpdateStageRequest) =>
    request<WorkStage>(`/v1/projects/${projectId}/stages/${stageId}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  // Wave 379AY · delete stage. Backend é hard-delete; UI confirma antes.
  deleteStage: (projectId: string, stageId: string) =>
    request<{ ok: true }>(`/v1/projects/${projectId}/stages/${stageId}`, {
      method: 'DELETE',
    }),
  // Wave 379AB · gerar etapas a partir do orçamento (preview + execute).
  previewStagesFromBudget: (projectId: string) =>
    request<{
      previews: Array<{
        key: string;
        name: string;
        description: string;
        chapters: string[];
        subStages: Array<{ code: string; name: string; plannedValueCents: number; itemCount: number }>;
        plannedValueCents: number;
        itemCount: number;
        plannedStartDate: string | null;
        plannedEndDate: string | null;
        alreadyExists: boolean;
      }>;
      unmappedChapters: string[];
    }>(`/v1/projects/${projectId}/stages/generate-preview`),
  generateStagesFromBudget: (projectId: string) =>
    request<{ stages: unknown[]; itemsLinked: number; unmappedChapters: string[] }>(
      `/v1/projects/${projectId}/stages/generate-from-budget`,
      { method: 'POST', body: '{}' },
    ),

  // --- daily logs / RDO ---
  listDailyLogs: (projectId: string) =>
    request<DailyLog[]>(`/v1/projects/${projectId}/daily-logs`),
  getDailyLogsKpi: (projectId: string) =>
    request<{
      totalLogs: number;
      signedLogs: number;
      lastLogDate: string | null;
      lastSignedAt: string | null;
      signedThisMonth: number;
    }>(`/v1/projects/${projectId}/daily-logs/kpi`),
  createDailyLog: (projectId: string, body: CreateDailyLogRequest) =>
    request<DailyLog>(`/v1/projects/${projectId}/daily-logs`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  signDailyLog: (
    projectId: string,
    logId: string,
    body: { expectedVersion?: number } = {},
  ) =>
    request<DailyLog>(
      `/v1/projects/${projectId}/daily-logs/${logId}/sign`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  correctDailyLog: (
    projectId: string,
    body: CreateDailyLogRequest & { correctsId: string; correctionNote: string },
  ) =>
    request<DailyLog>(`/v1/projects/${projectId}/daily-logs/correct`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  // Wave 379BE · #103 · soft-delete RDO em status='draft'.
  // Backend rejeita signed/corrected (`rdo_already_signed`) e exige autor
  // OU admin/PM (`only_author_or_admin`). Cascadeia fotos (R2 hard-delete)
  // e o mirror em timeline_events.
  deleteDailyLog: (projectId: string, logId: string) =>
    request<{ ok: true }>(
      `/v1/projects/${projectId}/daily-logs/${logId}`,
      { method: 'DELETE' },
    ),

  // --- PPP audio notes (voice notes attached to RDOs) ---

  /**
   * Upload a voice note blob to the audio-notes endpoint.
   * The server stores it in R2 (audio/rdos/<rdoId>/<hash>.<ext>) and
   * inserts a daily_log_audio_notes row.
   *
   * Returns { audioNoteId, r2Key, signedUrl, durationMs, mimeType,
   *           sizeBytes, uploadedAt }.
   */
  uploadAudioNote: (
    projectId: string,
    dailyLogId: string,
    audioBlob: Blob,
    durationMs: number,
    mimeType: string,
  ) => {
    const form = new FormData();
    form.append('audio', audioBlob, `voice-note.${mimeType.split('/')[1] ?? 'webm'}`);
    form.append('durationMs', String(durationMs));
    form.append('mimeType', mimeType);
    return request<{
      audioNoteId: string;
      r2Key: string;
      signedUrl: string | null;
      durationMs: number;
      mimeType: string;
      sizeBytes: number;
      uploadedAt: string;
    }>(`/v1/projects/${projectId}/daily-logs/${dailyLogId}/audio-notes`, {
      method: 'POST',
      body: form,
      // Do NOT set content-type — browser sets it with the boundary.
      headers: {},
    });
  },

  /** List all voice notes for a daily log, each with a signed playback URL. */
  listAudioNotes: (projectId: string, dailyLogId: string) =>
    request<
      Array<{
        id: string;
        dailyLogId: string;
        r2Key: string;
        durationMs: number;
        mimeType: string;
        sizeBytes: number;
        uploadedBy: string;
        uploadedAt: string;
        signedUrl?: string;
      }>
    >(`/v1/projects/${projectId}/daily-logs/${dailyLogId}/audio-notes`),

  /** Delete a voice note by id. Only the uploader (or admin/PM) may delete. */
  deleteAudioNote: (projectId: string, dailyLogId: string, audioNoteId: string) =>
    request<{ ok: true }>(
      `/v1/projects/${projectId}/daily-logs/${dailyLogId}/audio-notes/${audioNoteId}`,
      { method: 'DELETE' },
    ),

  // --- me / notifications ---
  // NOTE: whoami is already exposed above as `me`, which hits /v1/auth/me.
  // `/v1/me` is the newer me-routes endpoint (added in Wave 1 for the
  // notifications inbox) — we expose it as `whoami` to avoid clobbering
  // the auth-scoped method while keeping naming explicit.
  whoami: () => request<import('@builder/shared').AuthUser>('/v1/me'),
  listNotifications: (status: 'unread' | 'all' = 'unread') =>
    request<{
      notifications: Array<{
        id: string;
        channel: string;
        template_code: string;
        subject: string | null;
        body: string;
        status: string;
        sent_at: string | null;
        read_at: string | null;
        created_at: string;
        related_event_id: string | null;
      }>;
      unreadCount: number;
    }>(`/v1/me/notifications?status=${status}`),
  markNotificationRead: (id: string) =>
    request<{ ok: true }>(`/v1/me/notifications/${id}/read`, { method: 'POST' }),

  // --- PM notification bell (Wave RRRR) ---
  listUserNotifications: (params?: { unreadOnly?: boolean; limit?: number; cursor?: string }) => {
    const sp = new URLSearchParams();
    if (params?.unreadOnly) sp.set('unreadOnly', 'true');
    if (params?.limit) sp.set('limit', String(params.limit));
    if (params?.cursor) sp.set('cursor', params.cursor);
    const qs = sp.toString() ? `?${sp.toString()}` : '';
    return request<{
      notifications: Array<{
        id: string;
        userId: string;
        projectId: string | null;
        kind: string;
        title: string;
        body: string | null;
        href: string | null;
        payload: Record<string, unknown> | null;
        readAt: string | null;
        createdAt: string;
      }>;
      hasMore: boolean;
      cursor: string | null;
    }>(`/v1/notifications${qs}`);
  },
  markUserNotificationRead: (id: string) =>
    request<{ ok: true; count?: number }>(`/v1/notifications/${id}/mark-read`, { method: 'POST' }),
  markAllUserNotificationsRead: () =>
    request<{ ok: true; count: number }>('/v1/notifications/mark-all-read', { method: 'POST' }),

  // --- timeline ---
  listTimeline: (projectId: string) =>
    request<TimelineEvent[]>(`/v1/projects/${projectId}/timeline`),
  /**
   * Wave 379BN+ · #87 slice (c) — endpoint unificado do TimeCanvas.
   * Consolida timeline + calendar + gantt + feriados numa janela. O
   * client passa `from`/`to` como ISO date (YYYY-MM-DD); endpoint
   * default para mês corrente quando ausentes. Range cap server-side
   * de 6 meses; client deve paginar por mês quando precisar de janela
   * maior.
   */
  getTimeview: (
    projectId: string,
    range?: { from?: string; to?: string },
  ) => {
    const qs = new URLSearchParams();
    if (range?.from) qs.set('from', range.from);
    if (range?.to) qs.set('to', range.to);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return request<TimeviewResponse>(`/v1/projects/${projectId}/timeview${suffix}`);
  },
  createTimelineEvent: (projectId: string, body: CreateTimelineEventRequest) =>
    request<TimelineEvent>(`/v1/projects/${projectId}/timeline`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  listTimelinePhotos: (projectId: string, eventId: string) =>
    request<Photo[]>(`/v1/projects/${projectId}/timeline/${eventId}/photos`),
  // Wave 379BD · #99 · soft-delete uma nota da timeline. Backend rejeita
  // event_type !== 'note' (`only_notes_deletable`) e exige autor OU admin
  // (`only_author_or_admin`).
  deleteTimelineEvent: (projectId: string, eventId: string) =>
    request<{ ok: true }>(`/v1/projects/${projectId}/timeline/${eventId}`, {
      method: 'DELETE',
    }),

  // --- photos ---
  listProjectPhotos: (projectId: string) =>
    request<Photo[]>(`/v1/projects/${projectId}/photos`),
  requestPhotoUpload: (projectId: string, body: RequestUploadRequest) =>
    request<RequestUploadResponse>(`/v1/projects/${projectId}/photos/request-upload`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  completePhotoUpload: (body: { photoId: string; width?: number; height?: number }) =>
    request<Photo>('/v1/photos/complete', { method: 'POST', body: JSON.stringify(body) }),
  // Wave 379BC · delete foto. Hard-delete em R2, soft-delete em DB.
  // Permission policy aplicada server-side (PM/admin sempre; uploader
  // só se RDO ainda for rascunho).
  deletePhoto: (projectId: string, photoId: string) =>
    request<{ ok: true }>(`/v1/projects/${projectId}/photos/${photoId}`, {
      method: 'DELETE',
    }),
  // Wave 78 F1 SUB 4 · flip publish_to_showcase direto da PhotosPage
  // (sem precisar abrir o admin de Vitrine). JP D2: auto-publica se PM
  // marca explicitamente. Default = publish=true ao clicar; false
  // despublica.
  setProjectPhotoShowcasePublish: (
    projectId: string,
    photoId: string,
    publish: boolean,
  ) =>
    request<{ ok: true; publish: boolean }>(
      `/v1/projects/${projectId}/photos/${photoId}/publish`,
      {
        method: 'POST',
        body: JSON.stringify({ publish }),
      },
    ),

  // --- owner portal ---
  ownerProjects: () =>
    request<
      Array<
        Pick<
          Project,
          | 'id'
          | 'name'
          | 'address'
          | 'clientName'
          | 'status'
          | 'startDate'
          | 'estimatedEndDate'
          | 'actualEndDate'
          | 'description'
          | 'coverPhotoUrl'
          | 'progressPercent'
          | 'updatedAt'
        > & {
          /**
           * Wave 379BP · #93 owner portal v2 — number of attention items the
           * server detected (overdue stages, RNCs, anomalies, FVS pending,
           * upcoming milestones, RDO staleness). 0 means "render nothing".
           * Brokers always see 0 (scoped out server-side).
           */
          alertCount: number;
        }
      >
    >('/v1/owner/projects'),
  ownerProject: (id: string) =>
    request<{
      project: Project;
      stages: WorkStage[];
      timeline: TimelineEvent[];
      photos: Photo[];
      /**
       * The project's lead project manager, surfaced to the client so they can
       * see who to contact and how to pay via PIX. Null when the project has
       * no PM assigned (uncommon but possible for archived projects).
       */

      projectManager: { fullName: string; pixKey: string | null } | null;
      /**
       * Wave 379BP · #93 owner portal v2 — read-only alert array. Empty
       * when the project is healthy → caller hides the banner section.
       */
      alerts: Array<{
        kind:
          | 'stage_overdue'
          | 'milestone_due_soon'
          | 'rdo_stale'
          | 'rncs_open'
          | 'anomalies_pending'
          | 'fvs_pending';
        severity: 'low' | 'medium' | 'high';
        label: string;
        detail?: string;
        link?: 'stages' | 'timeline' | 'trust';
      }>;
    }>(`/v1/owner/projects/${id}`),

  ownerTrustSnapshot: (projectId: string) =>
    request<{
      project: {
        id: string;
        name: string;
        status: 'planning' | 'in_progress' | 'on_hold' | 'completed' | 'archived';
        actual_percent: number;
        expected_percent: number;
        last_rdo_at: string | null;
      };
      recent: {
        last_rdo: { id: string; date: string; summary: string } | null;
        last_payment: { date: string; amount_cents: number } | null;
        last_photo_count: number;
      };
      pending: {
        anomalies_pending: number;
        rncs_open: number;
        fvs_pending: number;
      };
      transparency: {
        autonomy_actions_last_30d: number;
        proposals_decided_last_30d: number;
        owner_messages_unread: number;
      };
    }>(`/v1/owner/trust-snapshot?projectId=${projectId}`),
  // --- Wave 3: stage dependencies ---
  listStageDependencies: (projectId: string) =>
    request<StageDependency[]>(`/v1/projects/${projectId}/stage-dependencies`),
  createStageDependency: (projectId: string, body: CreateStageDependencyRequest) =>
    request<StageDependency>(`/v1/projects/${projectId}/stage-dependencies`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  deleteStageDependency: (projectId: string, depId: string) =>
    request<{ ok: true }>(`/v1/projects/${projectId}/stage-dependencies/${depId}`, {
      method: 'DELETE',
    }),

  // --- Wave 3: progress snapshots (curva-S) ---
  listSnapshots: (projectId: string) =>
    request<ProgressSnapshot[]>(`/v1/projects/${projectId}/snapshots`),
  recomputeSnapshot: (projectId: string) =>
    request<ProgressSnapshot>(`/v1/projects/${projectId}/snapshots/recompute`, {
      method: 'POST',
    }),

  // --- Wave 3: attachments ---
  listAttachments: (projectId: string) =>
    request<ProjectAttachment[]>(`/v1/projects/${projectId}/attachments`),
  requestAttachmentUpload: (projectId: string, body: RequestAttachmentUploadRequest) =>
    request<RequestUploadResponse & { attachmentId: string }>(
      `/v1/projects/${projectId}/attachments/request-upload`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  deleteAttachment: (projectId: string, attachmentId: string) =>
    request<{ ok: true }>(`/v1/projects/${projectId}/attachments/${attachmentId}`, {
      method: 'DELETE',
    }),
  /**
   * Fetches the attachment via authenticated GET and returns a blob-URL the
   * caller can hand to `window.open` or an `<a href>`. We do this client-side
   * because a plain `<a href>` can't attach the `Authorization: Bearer`
   * header that our API requires. Caller is responsible for revoking the
   * blob URL (or just letting the page unload drop it).
   */
  async downloadAttachment(
    projectId: string,
    attachmentId: string,
  ): Promise<{ url: string; filename: string; revoke: () => void }> {
    const token = useAuth.getState().token;
    const headers = new Headers();
    if (token) headers.set('authorization', `Bearer ${token}`);
    const res = await fetch(
      `${API_BASE}/v1/projects/${projectId}/attachments/${attachmentId}/download`,
      { headers },
    );
    if (!res.ok) {
      throw new ApiError(res.status, 'download_failed', 'Download failed');
    }
    const disposition = res.headers.get('content-disposition') ?? '';
    const m = disposition.match(/filename="([^"]+)"/);
    const filename = m && m[1] ? decodeURIComponent(m[1]) : 'download';
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    return { url, filename, revoke: () => URL.revokeObjectURL(url) };
  },

  // --- Wave 3: medições ---
  listMeasurements: (projectId: string) =>
    request<Measurement[]>(`/v1/projects/${projectId}/measurements`),
  getMeasurement: (projectId: string, id: string) =>
    request<Measurement>(`/v1/projects/${projectId}/measurements/${id}`),
  createMeasurement: (projectId: string, body: CreateMeasurementRequest) =>
    request<Measurement>(`/v1/projects/${projectId}/measurements`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  approveMeasurement: (projectId: string, id: string, body: ApproveMeasurementRequest = {}) =>
    request<Measurement>(`/v1/projects/${projectId}/measurements/${id}/approve`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  markMeasurementPaid: (projectId: string, id: string) =>
    request<Measurement>(`/v1/projects/${projectId}/measurements/${id}/mark-paid`, {
      method: 'POST',
    }),
  // Wave 379BF · #104 — soft-delete medição rascunho. Backend rejeita
  // approved/sent/paid (`medicao_already_decided`) e exige autor OU admin/PM
  // (`only_author_or_admin`). measurement_items ficam ocultos via filtro
  // deleted_at IS NULL no parent (FK CASCADE não dispara em soft-delete).
  deleteMeasurement: (projectId: string, id: string) =>
    request<{ ok: true }>(
      `/v1/projects/${projectId}/measurements/${id}`,
      { method: 'DELETE' },
    ),

  // --- Wave 14.4: Boletim de Medição mensal ---
  getBoletimMedicao: (projectId: string, month: string) =>
    request<BoletimMedicao>(
      `/v1/projects/${projectId}/boletim-medicao?month=${encodeURIComponent(month)}`,
    ),

  // --- Wave 15.1: DRE por obra ---
  getDreReport: (projectId: string, params?: { from?: string; to?: string }) => {
    const qs = new URLSearchParams();
    if (params?.from) qs.set('from', params.from);
    if (params?.to) qs.set('to', params.to);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return request<DreReport>(`/v1/projects/${projectId}/dre${suffix}`);
  },

  // --- Wave 15.2: Cronograma físico-financeiro (S-curve) ---
  getCronograma: (projectId: string, granularity?: CronogramaGranularity) => {
    const suffix = granularity ? `?granularity=${granularity}` : '';
    return request<CronogramaReport>(
      `/v1/projects/${projectId}/cronograma${suffix}`,
    );
  },

  // --- Wave 17: Carteira (Portfolio) ---
  getPortfolioSummary: () =>
    request<PortfolioSummary>('/v1/portfolio/summary'),
  /**
   * Wave 20 — Download the same portfolio aggregate as CSV. Uses the
   * same blob-via-fetch dance as admin CSV export (auth header +
   * temporary object URL + anchor click) because the endpoint requires
   * Authorization which a plain <a href> can't send.
   */
  downloadPortfolioCsv: async (): Promise<void> => {
    const token = useAuth.getState().token;
    const headers = new Headers();
    if (token) headers.set('authorization', `Bearer ${token}`);
    const res = await fetch(`${API_BASE}/v1/portfolio/summary.csv`, {
      headers,
    });
    if (!res.ok) {
      throw new ApiError(
        res.status,
        'portfolio_csv_failed',
        `Não foi possível exportar a carteira (HTTP ${res.status}).`,
      );
    }
    const disposition = res.headers.get('content-disposition') ?? '';
    const match = disposition.match(/filename="([^"]+)"/);
    const filename = match?.[1] ?? 'carteira.csv';
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1_000);
  },

  // --- Wave 18: Fluxo de Caixa Projetado ---
  getFluxoCaixa: (
    projectId: string,
    params?: { months?: number; past?: number },
  ) => {
    const qs = new URLSearchParams();
    if (params?.months) qs.set('months', String(params.months));
    if (params?.past !== undefined) qs.set('past', String(params.past));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return request<FluxoCaixaSummary>(
      `/v1/projects/${projectId}/fluxo-caixa${suffix}`,
    );
  },

  // --- Wave 19: Central alerts hub ---
  getAlertsSummary: () => request<AlertsSummary>('/v1/alertas'),

  // --- Wave 22: Central approvals inbox ---
  getApprovalsInbox: () => request<ApprovalsInbox>('/v1/approvals/inbox'),
  // C13 (#124) — batch approve / reject from the inbox.
  batchDecideApprovals: (body: ApprovalsBatchRequest) =>
    request<ApprovalsBatchResponse>('/v1/approvals/batch', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // --- Wave 23: Cross-project activity feed ---
  getActivityFeed: (opts?: { days?: number; limit?: number }) => {
    const qs = new URLSearchParams();
    if (opts?.days !== undefined) qs.set('days', String(opts.days));
    if (opts?.limit !== undefined) qs.set('limit', String(opts.limit));
    const q = qs.toString();
    return request<ActivityFeed>(`/v1/atividade/feed${q ? `?${q}` : ''}`);
  },

  // --- Wave 24: NFe XML ingestion ---
  parseNfeXml: (xml: string) =>
    request<{ metadata: NfeMetadata }>('/v1/nfe/parse', {
      method: 'POST',
      body: JSON.stringify({ xml }),
    }),
  ingestNfeForPurchaseOrder: (
    projectId: string,
    purchaseOrderId: string,
    body: NfeIngestRequest,
  ) =>
    request<NfeIngestResult>(
      `/v1/projects/${projectId}/purchase-orders/${purchaseOrderId}/nfe`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  // --- Wave 3: purchase requests ---
  listPurchaseRequests: (projectId: string) =>
    request<PurchaseRequest[]>(`/v1/projects/${projectId}/purchase-requests`),
  createPurchaseRequest: (projectId: string, body: CreatePurchaseRequestRequest) =>
    request<PurchaseRequest>(`/v1/projects/${projectId}/purchase-requests`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  approvePurchaseRequest: (
    projectId: string,
    id: string,
    body: DecisionPurchaseRequestRequest = {},
  ) =>
    request<PurchaseRequest>(`/v1/projects/${projectId}/purchase-requests/${id}/approve`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  rejectPurchaseRequest: (
    projectId: string,
    id: string,
    body: DecisionPurchaseRequestRequest = {},
  ) =>
    request<PurchaseRequest>(`/v1/projects/${projectId}/purchase-requests/${id}/reject`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  fulfillPurchaseRequest: (projectId: string, id: string) =>
    request<PurchaseRequest>(`/v1/projects/${projectId}/purchase-requests/${id}/fulfill`, {
      method: 'POST',
    }),

  // --- Wave 4: suppliers (tenant-scoped) ---
  listSuppliers: (includeArchived = false) =>
    request<Supplier[]>(`/v1/suppliers${includeArchived ? '?includeArchived=1' : ''}`),
  createSupplier: (body: CreateSupplierRequest) =>
    request<Supplier>('/v1/suppliers', { method: 'POST', body: JSON.stringify(body) }),
  updateSupplier: (id: string, body: UpdateSupplierRequest) =>
    request<Supplier>(`/v1/suppliers/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  // Wave 66 · H4.3 · Supplier Trust Score (admin)
  // W97.3 · endpoint agora retorna 200 com `score: null` + `reason` quando
  // não há histórico (em vez de 404). Tipo opcional pra compat reversa.
  getSupplierTrustScore: (supplierId: string) =>
    request<{
      supplierId: string;
      scoreTotal: number | null;
      confidenceLevel?: 'low' | 'medium' | 'high';
      components?: {
        on_time_delivery_pct: number | null;
        quote_acceptance_pct: number | null;
        complaint_rate: number | null;
        price_variance_pct: number | null;
      };
      sampleSize?: number;
      lastComputedAt?: string | null;
      reason?: 'no_purchase_history';
      message?: string;
    }>(`/v1/admin/suppliers/${supplierId}/trust-score`),
  recomputeSupplierTrustScore: (supplierId: string) =>
    request<{
      supplierId: string;
      scoreTotal: number | null;
      confidenceLevel: 'low' | 'medium' | 'high';
      components: {
        on_time_delivery_pct: number | null;
        quote_acceptance_pct: number | null;
        complaint_rate: number | null;
        price_variance_pct: number | null;
      };
      sampleSize: number;
      lastComputedAt: string | null;
    }>(`/v1/admin/suppliers/${supplierId}/trust-score/recompute`, { method: 'POST' }),
  getSupplierTrustScoreSummary: () =>
    request<{
      top10: Array<{ supplierId: string; supplierName: string; scoreTotal: number; confidenceLevel: string; sampleSize: number }>;
      bottom10: Array<{ supplierId: string; supplierName: string; scoreTotal: number; confidenceLevel: string; sampleSize: number }>;
      noData: number;
      totalSuppliers: number;
    }>('/v1/admin/suppliers/trust-score-summary'),
  // 2026-05-10 · soft-delete supplier with pendency check.
  // Server returns 409 + { pendings: [{kind, count, sample}] } when blocked,
  // 200 + { id, deletedAt } on success. Caller should catch 409 to show
  // the user which active commitments are blocking.
  deleteSupplier: (id: string) =>
    request<{ id: string; legalName: string; deletedAt: string; method: string }>(
      `/v1/suppliers/${id}`,
      { method: 'DELETE' },
    ),

  // --- Wave 1.5 · 2026-05-10 · canonical SKU search & inline create ---
  // Mantém a forma simples (q, category, source, limit) e a resposta
  // já vem normalizada pra o picker (sem precisar parsear JSON cols).
  searchSkus: (params: { q?: string; category?: string; source?: string; limit?: number } = {}) => {
    const qs = new URLSearchParams();
    if (params.q) qs.set('q', params.q);
    if (params.category) qs.set('category', params.category);
    if (params.source) qs.set('source', params.source);
    if (params.limit) qs.set('limit', String(params.limit));
    const query = qs.toString();
    return request<{
      data: Array<{
        id: string;
        code: string;
        source: string;
        refer: string | null;
        name: string;
        unit: string;
        categoryPath: string[];
        specification: Record<string, unknown>;
        aliases: string[];
        referenceUnitPriceCents: number | null;
        referenceQuantity: number | null;
      }>;
      meta: { query: string | null; count: number; limit: number };
    }>(`/v1/skus${query ? '?' + query : ''}`);
  },
  // WAVE 28.3 cleanup · request() em client.ts:492 já faz unwrap do `data:` envelope.
  // Caller agora recebe shape direto (sem precisar de .data).
  listSkuCategories: () =>
    request<Array<{ category: string; skuCount: number }>>(
      '/v1/skus/categories',
    ),
  createCustomSku: (body: {
    name: string;
    unit: string;
    categoryPath?: string[];
    aliases?: string[];
  }) =>
    request<{
      id: string;
      code: string;
      source: string;
      name: string;
      unit: string;
      categoryPath: string[];
      aliases: string[];
    }>('/v1/skus', { method: 'POST', body: JSON.stringify(body) }),

  // --- Wave 21: supplier scorecard (Fornecedor 360) ---
  getSupplierScorecard: (id: string) =>
    request<SupplierScorecard>(`/v1/suppliers/${id}/scorecard`),

  // --- Wave 379BM (#94): supplier portal token ---
  generateSupplierPortalToken: (id: string) =>
    request<GenerateSupplierPortalTokenResponse>(
      `/v1/suppliers/${id}/portal-token`,
      { method: 'POST' },
    ),

  // --- Wave 4: purchase orders ---
  listPurchaseOrders: (projectId: string) =>
    request<PurchaseOrder[]>(`/v1/projects/${projectId}/purchase-orders`),
  createPurchaseOrder: (projectId: string, body: CreatePurchaseOrderRequest) =>
    request<PurchaseOrder>(`/v1/projects/${projectId}/purchase-orders`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  issuePurchaseOrder: (projectId: string, id: string) =>
    request<PurchaseOrder>(`/v1/projects/${projectId}/purchase-orders/${id}/issue`, {
      method: 'POST',
    }),
  cancelPurchaseOrder: (
    projectId: string,
    id: string,
    body: CancelPurchaseOrderRequest,
  ) =>
    request<PurchaseOrder>(`/v1/projects/${projectId}/purchase-orders/${id}/cancel`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  // Gate humano de envio · 2026-06-08 — manda o pedido ao fornecedor (ato
  // externo). Idempotente no backend: reenvio retorna alreadySent=true.
  sendPoToSupplier: (projectId: string, id: string) =>
    request<{
      purchaseOrder: PurchaseOrder;
      alreadySent: boolean;
      skippedSimulation: boolean;
      losersNotified: number;
    }>(`/v1/projects/${projectId}/purchase-orders/${id}/send-to-supplier`, {
      method: 'POST',
    }),

  // --- Wave 14.1: PO retention (caução) ---
  getPoRetention: (projectId: string, id: string) =>
    request<PurchaseOrderRetention>(
      `/v1/projects/${projectId}/purchase-orders/${id}/retention`,
    ),
  releasePoRetention: (
    projectId: string,
    id: string,
    body: ReleasePurchaseOrderRetentionRequest,
  ) =>
    request<Payment>(
      `/v1/projects/${projectId}/purchase-orders/${id}/retention/release`,
      {
        method: 'POST',
        body: JSON.stringify(body),
      },
    ),

  // --- Wave 4: receipts ---
  listReceipts: (projectId: string, purchaseOrderId?: string) => {
    const q = purchaseOrderId
      ? `?purchaseOrderId=${encodeURIComponent(purchaseOrderId)}`
      : '';
    return request<Receipt[]>(`/v1/projects/${projectId}/receipts${q}`);
  },
  createReceipt: (projectId: string, body: CreateReceiptRequest) =>
    request<Receipt>(`/v1/projects/${projectId}/receipts`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // --- W123.9 · Mesa do dinheiro ---
  getDinheiro: (projectId: string) =>
    request<{
      rows: Array<{
        budgetItemId: string | null;
        capituloLabel: string | null;
        itemLabel: string;
        plannedCents: number;
        committedCents: number;
        paidCents: number;
        payableCents: number;
      }>;
      totals: {
        plannedCents: number;
        committedCents: number;
        paidCents: number;
        payableCents: number;
      };
    }>(`/v1/projects/${projectId}/dinheiro`),
  listManualExpenses: (projectId: string) =>
    request<
      Array<{
        id: string;
        projectId: string;
        budgetItemId: string | null;
        amountCents: number;
        paidAt: string | null;
        vendorName: string | null;
        description: string;
        createdByUserId: string;
        createdAt: string;
        updatedAt: string;
      }>
    >(`/v1/projects/${projectId}/manual-expenses`),
  createManualExpense: (
    projectId: string,
    body: {
      amountCents: number;
      paidAt?: string | null;
      vendorName?: string | null;
      description: string;
      budgetItemId?: string | null;
    },
  ) =>
    request<{
      id: string;
      projectId: string;
      budgetItemId: string | null;
      amountCents: number;
      paidAt: string | null;
      vendorName: string | null;
      description: string;
      createdByUserId: string;
      createdAt: string;
      updatedAt: string;
    }>(`/v1/projects/${projectId}/manual-expenses`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  deleteManualExpense: (projectId: string, id: string) =>
    request<void>(`/v1/projects/${projectId}/manual-expenses/${id}`, {
      method: 'DELETE',
    }),

  // --- Wave 4: payments ledger ---
  listPayments: (projectId: string) =>
    request<Payment[]>(`/v1/projects/${projectId}/payments`),
  createPayment: (projectId: string, body: CreatePaymentRequest) =>
    request<Payment>(`/v1/projects/${projectId}/payments`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  settlePayment: (projectId: string, id: string, body: SettlePaymentRequest = {}) =>
    request<Payment>(`/v1/projects/${projectId}/payments/${id}/settle`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // --- Wave 4: change orders ---
  listChangeOrders: (projectId: string) =>
    request<ChangeOrder[]>(`/v1/projects/${projectId}/change-orders`),
  createChangeOrder: (projectId: string, body: CreateChangeOrderRequest) =>
    request<ChangeOrder>(`/v1/projects/${projectId}/change-orders`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  submitChangeOrder: (projectId: string, id: string) =>
    request<ChangeOrder>(`/v1/projects/${projectId}/change-orders/${id}/submit`, {
      method: 'POST',
    }),
  approveChangeOrder: (
    projectId: string,
    id: string,
    body: DecideChangeOrderRequest = {},
  ) =>
    request<ChangeOrder>(`/v1/projects/${projectId}/change-orders/${id}/approve`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  rejectChangeOrder: (
    projectId: string,
    id: string,
    body: DecideChangeOrderRequest,
  ) =>
    request<ChangeOrder>(`/v1/projects/${projectId}/change-orders/${id}/reject`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // --- Wave 4: approval tokens ---
  issueApprovalToken: (body: IssueApprovalTokenRequest) =>
    request<IssueApprovalTokenResponse>('/v1/approval-tokens', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  resolveApprovalToken: (body: UseApprovalTokenRequest) =>
    request<{
      id: string;
      entityType: ApprovalEntityType;
      entityId: string;
      action: ApprovalAction;
      expiresAt: string;
    }>('/v1/approval-tokens/resolve', {
      method: 'POST',
      body: JSON.stringify(body),
      auth: false,
    }),
  useApprovalToken: (body: UseApprovalTokenRequest, opts?: { turnstileToken?: string }) =>
    request<{ ok: true }>('/v1/approval-tokens/use', {
      method: 'POST',
      body: JSON.stringify(body),
      auth: false,
      headers: opts?.turnstileToken ? { 'cf-turnstile-response': opts.turnstileToken } : undefined,
    }),

  // --- Wave 6: documents & compliance ---
  listDocuments: (projectId: string) =>
    request<{
      documents: ProjectDocument[];
      requirements: ProjectDocumentRequirement[];
    }>(`/v1/projects/${projectId}/documents`),
  createDocument: (projectId: string, body: CreateDocumentInput) =>
    request<ProjectDocument>(`/v1/projects/${projectId}/documents`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  updateDocument: (documentId: string, body: UpdateDocumentInput) =>
    request<ProjectDocument>(`/v1/documents/${documentId}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  deleteDocument: (documentId: string) =>
    request<{ ok: true }>(`/v1/documents/${documentId}`, { method: 'DELETE' }),
  addDocumentRequirement: (
    projectId: string,
    body: { docType: DocType; requiredBy?: string | null; notes?: string | null },
  ) =>
    request<{ ok: true }>(
      `/v1/projects/${projectId}/document-requirements`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  removeDocumentRequirement: (requirementId: string) =>
    request<{ ok: true }>(`/v1/document-requirements/${requirementId}`, {
      method: 'DELETE',
    }),

  // --- Wave 10: issues (problemas / punch-list) ---
  listIssues: (
    projectId: string,
    filters: {
      status?: IssueStatus;
      severity?: IssueSeverity;
      assigneeUserId?: string;
      stageId?: string;
      includeDeleted?: boolean;
    } = {},
  ) => {
    const q = new URLSearchParams();
    if (filters.status) q.set('status', filters.status);
    if (filters.severity) q.set('severity', filters.severity);
    if (filters.assigneeUserId) q.set('assigneeUserId', filters.assigneeUserId);
    if (filters.stageId) q.set('stageId', filters.stageId);
    if (filters.includeDeleted) q.set('includeDeleted', '1');
    const qs = q.toString();
    return request<Issue[]>(
      `/v1/projects/${projectId}/issues${qs ? `?${qs}` : ''}`,
    );
  },
  getIssue: (projectId: string, id: string) =>
    request<Issue>(`/v1/projects/${projectId}/issues/${id}`),
  createIssue: (projectId: string, body: CreateIssueRequest) =>
    request<Issue>(`/v1/projects/${projectId}/issues`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  updateIssue: (projectId: string, id: string, body: UpdateIssueRequest) =>
    request<Issue>(`/v1/projects/${projectId}/issues/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  deleteIssue: (projectId: string, id: string) =>
    request<{ ok: true }>(`/v1/projects/${projectId}/issues/${id}`, {
      method: 'DELETE',
    }),

  // --- Wave 10: comments (polymorphic) ---
  listComments: (
    projectId: string,
    entityType: CommentEntityType,
    entityId: string,
  ) =>
    request<Comment[]>(
      `/v1/projects/${projectId}/comments?entityType=${encodeURIComponent(
        entityType,
      )}&entityId=${encodeURIComponent(entityId)}`,
    ),
  createComment: (projectId: string, body: CreateCommentRequest) =>
    request<Comment>(`/v1/projects/${projectId}/comments`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  deleteComment: (projectId: string, id: string) =>
    request<{ ok: true }>(`/v1/projects/${projectId}/comments/${id}`, {
      method: 'DELETE',
    }),

  // --- Wave 10: external links (owner + buyer portals) ---
  listExternalLinks: (projectId: string, includeRevoked = false) =>
    request<ExternalLink[]>(
      `/v1/projects/${projectId}/external-links${
        includeRevoked ? '?includeRevoked=1' : ''
      }`,
    ),
  createExternalLink: (projectId: string, body: CreateExternalLinkRequest) =>
    request<CreateExternalLinkResponse>(
      `/v1/projects/${projectId}/external-links`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  revokeExternalLink: (projectId: string, id: string) =>
    request<ExternalLink>(
      `/v1/projects/${projectId}/external-links/${id}/revoke`,
      { method: 'POST' },
    ),
  /** Public — no auth. Token in path IS the auth. */
  getBuyerPortal: (token: string) =>
    request<ExternalLinkProjectView>(`/v1/c/${encodeURIComponent(token)}`, {
      auth: false,
    }),
  /** Public — no auth. Token in path IS the auth. */
  getOwnerPortalByToken: (token: string) =>
    request<ExternalLinkProjectView>(`/v1/o/${encodeURIComponent(token)}`, {
      auth: false,
    }),

  // W152 · P0-B · 2026-05-29 — Acesso do interessado aprovado na vitrine.
  // Rota pública /v1/ver/:token/documents (sem auth — o token É a credencial).
  // Padrão 38: método explícito com { auth: false } para rota pública.
  getBuyerAccess: (token: string) =>
    request<{
      slug: string;
      tier: 'prospect' | 'buyer' | 'delivery';
      expiresAt: string | null;
      // Confiança · 2026-05-31 — quem liberou o acesso. Pode vir null em
      // links/obras antigos; a UI compõe um fallback gracioso.
      requestedBy?: { name: string | null };
      documents: GatedDocument[];
    }>(`/v1/ver/${encodeURIComponent(token)}/documents`, { auth: false }),

  // W152 · P0-B — dados públicos da vitrine (nome/progresso) por slug.
  // Mesma rota que a página de marketing usa; sem auth, edge-cached.
  getPublicShowcase: (slug: string) =>
    request<PublicShowcase>(
      `/v1/public/showcase/${encodeURIComponent(slug)}`,
      { auth: false },
    ),

  // --- Wave 3: audit drawer (events + chain verify) ---
  listProjectEvents: (
    projectId: string,
    opts: { limit?: number; entityType?: string; entityId?: string } = {},
  ) => {
    const qs = new URLSearchParams();
    qs.set('limit', String(opts.limit ?? 200));
    if (opts.entityType) qs.set('entityType', opts.entityType);
    if (opts.entityId) qs.set('entityId', opts.entityId);
    return request<
      Array<{
        id: string;
        entityType: string;
        entityId: string;
        eventType: string;
        payload: unknown;
        actorUserId: string | null;
        actorName: string | null;
        occurredAt: string;
        createdAt: string;
      }>
    >(`/v1/projects/${projectId}/events?${qs.toString()}`);
  },
  verifyEventChain: (projectId: string, entityType: string, entityId: string) =>
    request<{ ok: boolean; brokenAt?: string }>(
      `/v1/projects/${projectId}/events/verify-chain?entityType=${encodeURIComponent(entityType)}&entityId=${encodeURIComponent(entityId)}`,
    ),

  // --- Wave 11: budget (plan vs. actual) ---
  listBudgetItems: (projectId: string, includeArchived = false) =>
    request<BudgetItem[]>(
      `/v1/projects/${projectId}/budget/items${
        includeArchived ? '?includeArchived=1' : ''
      }`,
    ),
  getBudgetItem: (projectId: string, itemId: string) =>
    request<BudgetItem>(`/v1/projects/${projectId}/budget/items/${itemId}`),
  createBudgetItem: (projectId: string, body: CreateBudgetItemRequest) =>
    request<BudgetItem>(`/v1/projects/${projectId}/budget/items`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  updateBudgetItem: (
    projectId: string,
    itemId: string,
    body: UpdateBudgetItemRequest,
  ) =>
    request<BudgetItem>(`/v1/projects/${projectId}/budget/items/${itemId}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  deleteBudgetItem: (projectId: string, itemId: string) =>
    request<{ ok: true }>(`/v1/projects/${projectId}/budget/items/${itemId}`, {
      method: 'DELETE',
    }),

  // Wave 92 F2 · 2026-05-24 · Restore (undo 30s server-side window).
  // PM clica "Excluir" → toast com Undo. Undo chama este endpoint.
  restoreBudgetItem: (projectId: string, itemId: string) =>
    request<{ ok: true; restored?: boolean; alreadyActive?: boolean }>(
      `/v1/projects/${projectId}/budget/items/${itemId}/restore`,
      { method: 'POST' },
    ),

  // Wave 92 F2 · 2026-05-24 · Marca linha como "fora de escopo da obra".
  // Zera planned/total e exige justificativa (>= 5 chars).
  markBudgetItemOutOfScope: (
    projectId: string,
    itemId: string,
    reason: string,
  ) =>
    request<{ ok: true; categoryPriority: number }>(
      `/v1/projects/${projectId}/budget/items/${itemId}/mark-out-of-scope`,
      { method: 'POST', body: JSON.stringify({ reason }) },
    ),

  // Wave 92 F1.5 · 2026-05-24 · Aceitar estimativa IA sem editar.
  // Wizard de revisar — quando PM clica "Aceitar" num card IA, marca
  // como revisado sem mudar valor. Mantém source='ia_estimate'
  // (rastreabilidade: foi a IA que sugeriu, PM concordou).
  acceptBudgetItem: (projectId: string, itemId: string) =>
    request<{ ok: true }>(
      `/v1/projects/${projectId}/budget/items/${itemId}/accept`,
      { method: 'POST' },
    ),

  // FEATURE A4 · 2026-05-21 · Undo last action 30s.
  //
  // Calls POST /v1/undo/:eventId — backend reverses the soft-delete that
  // corresponds to `eventId` (looked up in `events`). Returns
  // `{ ok: true, alreadyRestored: boolean }`. On non-undoable actions
  // (any event type not in the recipe map), backend returns 400 with
  // `error.code = 'undo_not_supported'` which surfaces as ApiError with
  // a friendly message; the UndoToast catches and shows it.
  undoEvent: (eventId: string) =>
    request<{ ok: true; alreadyRestored: boolean }>(`/v1/undo/${eventId}`, {
      method: 'POST',
    }),
  /**
   * Procura o evento `.deleted` mais recente (últimos 5min) do par
   * (entityType, entityId) gravado pelo usuário atual. Frontend chama
   * imediatamente após um delete pra capturar o id e mostrar o toast.
   */
  undoLookup: (entityType: string, entityId: string) =>
    request<{ eventId: string | null; eventType: string | null }>(
      `/v1/undo/lookup?entityType=${encodeURIComponent(entityType)}&entityId=${encodeURIComponent(entityId)}`,
    ),
  getBudgetSummary: (projectId: string, includeArchived = false) =>
    request<BudgetSummary>(
      `/v1/projects/${projectId}/budget/summary${
        includeArchived ? '?includeArchived=1' : ''
      }`,
    ),
  // Wave 379S · backlog Compras — items marcados `to_purchase` que
  // ainda não estão em cotação aberta nem PO ativo.
  listComprasBacklog: (projectId: string) =>
    request<Array<BudgetItem & {
      state: 'pending' | 'in_quote' | 'ordered';
      openQuotationRoundId: string | null;
      activePurchaseOrderId: string | null;
    }>>(
      `/v1/projects/${projectId}/compras/backlog`,
    ),
  // Wave 379T · adjudicação per-line — cria N POs (1 por fornecedor
  // vencedor) a partir de awards [{itemIndex, responseId}].
  // Wave Bid 100% · dry-run de adjudicação. Retorna warnings sem efeito.
  previewAdjudicate: (
    roundId: string,
    awards: Array<{ itemIndex: number; responseId: string }>,
  ) =>
    request<{
      totalAwardedCents: number;
      budgetWarnings: Array<{
        kind: 'budget_overrun_total' | 'budget_overrun_item';
        budgetItemId: string | null;
        label: string;
        plannedCents: number;
        awardedCents: number;
        overrunCents: number;
        overrunPct: number;
      }>;
    }>(`/v1/quotations/${roundId}/adjudicate-preview`, {
      method: 'POST',
      body: JSON.stringify({ awards }),
    }),
  // Wave Bid 100% · rotaciona token de resposta (regenerar link público).
  rotateQuotationResponseToken: (responseId: string) =>
    request<{
      responseId: string;
      responseToken: string;
      tokenExpiresAt: string;
      publicLink: string;
    }>(`/v1/quotations/responses/${responseId}/rotate-token`, { method: 'POST' }),
  // Wave Bid 100% · histórico de cotações ganhas pelo supplier.
  listSupplierAwards: (supplierId: string) =>
    request<{
      items: Array<{
        roundId: string;
        roundTitle: string | null;
        projectName: string | null;
        description: string;
        unit: string;
        quantity: number;
        unitPriceCents: number;
        totalCents: number;
        decidedAt: string;
        poId: string | null;
      }>;
      count: number;
      totalAwardedCents: number;
    }>(`/v1/suppliers/${supplierId}/awards`),
  // Wave Bid 100% · histórico de preços cotados pelo supplier (últimos 90d).
  listSupplierPriceHistory: (supplierId: string) =>
    request<{
      items: Array<{
        description: string;
        unit: string;
        quantity: number;
        unitPriceCents: number;
        recordedAt: string;
        sourceRoundId: string | null;
      }>;
      count: number;
    }>(`/v1/suppliers/${supplierId}/price-history`),
  adjudicateQuotationPerLine: (
    roundId: string,
    awards: Array<{ itemIndex: number; responseId: string }>,
  ) =>
    request<{
      purchaseOrderIds: string[];
      itemsLeftInBacklog: number;
      budgetWarnings?: Array<{
        kind: 'budget_overrun_total' | 'budget_overrun_item';
        budgetItemId: string | null;
        label: string;
        plannedCents: number;
        awardedCents: number;
        overrunCents: number;
        overrunPct: number;
      }>;
    }>(
      `/v1/quotations/${roundId}/adjudicate-per-line`,
      { method: 'POST', body: JSON.stringify({ awards }) },
    ),
  // Wave 379BD · delete quotation round. Soft-delete; só PM/admin;
  // só se nenhuma resposta foi submetida ainda (server bloqueia
  // com 400 has_submitted_responses).
  deleteQuotation: (roundId: string) =>
    request<{ ok: true }>(`/v1/quotations/${roundId}`, { method: 'DELETE' }),

  // --- Wave 12: global search (legacy — CommandPalette) ---
  search: (q: string, limit = 30) =>
    request<SearchHit[]>(
      `/v1/search?q=${encodeURIComponent(q)}&limit=${limit}`,
    ),

  // --- Wave eeeee: cross-module command bar ---
  globalSearch: (q: string, limit = 20) =>
    request<import('@/lib/global-search').GlobalSearchResponse>(
      `/v1/search?q=${encodeURIComponent(q)}&limit=${limit}`,
    ),

  // --- Wave 25: expiring share links (PM side) ---
  createShareLink: (projectId: string, body: CreateShareLinkRequest) =>
    request<CreateShareLinkResponse>(
      `/v1/projects/${projectId}/share-links`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  listShareLinks: (projectId: string) =>
    request<ListShareLinksResponse>(`/v1/projects/${projectId}/share-links`),
  revokeShareLink: (projectId: string, id: string) =>
    request<RevokeShareLinkResponse>(
      `/v1/projects/${projectId}/share-links/${id}/revoke`,
      { method: 'POST' },
    ),

  // --- Wave 25: public share link — NO auth header sent. ---
  resolveShareLink: (token: string) =>
    request<ResolveShareLinkResponse>('/v1/share/resolve', {
      method: 'POST',
      body: JSON.stringify({ token }),
      auth: false,
    }),
  getSharedMedicao: (token: string) =>
    request<SharedMedicaoPayload>(
      `/v1/share/${encodeURIComponent(token)}/medicao`,
      { auth: false },
    ),
  getSharedDre: (token: string) =>
    request<SharedDrePayload>(
      `/v1/share/${encodeURIComponent(token)}/dre`,
      { auth: false },
    ),

  // --- Wave 27: supplier catalog + Curva ABC ---
  listSupplierCatalog: (supplierId: string, includeArchived = false) =>
    request<ListSupplierCatalogResponse>(
      `/v1/suppliers/${supplierId}/catalogo${includeArchived ? '?includeArchived=1' : ''}`,
    ),
  createSupplierCatalogItem: (
    supplierId: string,
    body: CreateSupplierCatalogItemRequest,
  ) =>
    request<SupplierCatalogItem>(`/v1/suppliers/${supplierId}/catalogo`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  updateSupplierCatalogItem: (
    supplierId: string,
    itemId: string,
    body: UpdateSupplierCatalogItemRequest,
  ) =>
    request<SupplierCatalogItem>(
      `/v1/suppliers/${supplierId}/catalogo/${itemId}`,
      { method: 'PATCH', body: JSON.stringify(body) },
    ),
  getCurvaAbc: (projectId: string) =>
    request<CurvaAbcReport>(`/v1/projects/${projectId}/curva-abc`),

  // --- Wave 28: punch list de entrega ---
  listPunchList: (projectId: string) =>
    request<ListPunchListResponse>(`/v1/projects/${projectId}/punch-list`),
  createPunchListItem: (projectId: string, body: CreatePunchListItemRequest) =>
    request<PunchListItem>(`/v1/projects/${projectId}/punch-list`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  updatePunchListItem: (
    projectId: string,
    itemId: string,
    body: UpdatePunchListItemRequest,
  ) =>
    request<PunchListItem>(
      `/v1/projects/${projectId}/punch-list/${itemId}`,
      { method: 'PATCH', body: JSON.stringify(body) },
    ),

  // --- Wave 29: mão-de-obra diária ---
  // Optional from/to date range is passed as query params. The API
  // clamps the response to entries whose log_date falls in [from, to].
  listLabor: (
    projectId: string,
    range?: { from?: string; to?: string },
  ) => {
    const qs = new URLSearchParams();
    if (range?.from) qs.set('from', range.from);
    if (range?.to) qs.set('to', range.to);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return request<ListLaborResponse>(
      `/v1/projects/${projectId}/labor${suffix}`,
    );
  },
  createLaborEntry: (projectId: string, body: CreateLaborEntryRequest) =>
    request<LaborEntry>(`/v1/projects/${projectId}/labor`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  updateLaborEntry: (
    projectId: string,
    entryId: string,
    body: UpdateLaborEntryRequest,
  ) =>
    request<LaborEntry>(
      `/v1/projects/${projectId}/labor/${entryId}`,
      { method: 'PATCH', body: JSON.stringify(body) },
    ),
  deleteLaborEntry: (projectId: string, entryId: string) =>
    request<{ ok: true }>(
      `/v1/projects/${projectId}/labor/${entryId}`,
      { method: 'DELETE' },
    ),

  // --- Wave 30: RNC (Registro de Não Conformidade) ---
  listRnc: (projectId: string) =>
    request<ListRncResponse>(`/v1/projects/${projectId}/rnc`),
  createRncItem: (projectId: string, body: CreateRncItemRequest) =>
    request<RncItem>(`/v1/projects/${projectId}/rnc`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  updateRncItem: (
    projectId: string,
    itemId: string,
    body: UpdateRncItemRequest,
  ) =>
    request<RncItem>(`/v1/projects/${projectId}/rnc/${itemId}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  deleteRncItem: (projectId: string, itemId: string) =>
    request<{ ok: true }>(`/v1/projects/${projectId}/rnc/${itemId}`, {
      method: 'DELETE',
    }),

  // --- Wave 31: FVS (Ficha de Verificação de Serviço) ---
  listFvsTemplates: () =>
    request<ListFvsTemplatesResponse>(`/v1/fvs/templates`),
  createFvsTemplate: (body: CreateFvsTemplateRequest) =>
    request<FvsTemplate>(`/v1/fvs/templates`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  updateFvsTemplate: (templateId: string, body: UpdateFvsTemplateRequest) =>
    request<FvsTemplate>(`/v1/fvs/templates/${templateId}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  deleteFvsTemplate: (templateId: string) =>
    request<{ ok: true }>(`/v1/fvs/templates/${templateId}`, {
      method: 'DELETE',
    }),

  listFvsInspections: (projectId: string) =>
    request<ListFvsInspectionsResponse>(`/v1/projects/${projectId}/fvs`),
  createFvsInspection: (projectId: string, body: CreateFvsInspectionRequest) =>
    request<FvsInspection>(`/v1/projects/${projectId}/fvs`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  updateFvsInspection: (
    projectId: string,
    inspectionId: string,
    body: UpdateFvsInspectionRequest,
  ) =>
    request<FvsInspection>(
      `/v1/projects/${projectId}/fvs/${inspectionId}`,
      { method: 'PATCH', body: JSON.stringify(body) },
    ),
  deleteFvsInspection: (projectId: string, inspectionId: string) =>
    request<{ ok: true }>(
      `/v1/projects/${projectId}/fvs/${inspectionId}`,
      { method: 'DELETE' },
    ),

  // Wave 32 — relatório executivo
  getExecutivoReport: (period?: Partial<ExecutivoPeriod>) => {
    const qs = new URLSearchParams();
    if (period?.from) qs.set('from', period.from);
    if (period?.to) qs.set('to', period.to);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return request<ExecutivoReport>(`/v1/relatorios/executivo${suffix}`);
  },

  // Wave 351-B/C — Unified Inbox aggregator.
  // Returns the canonical `InboxResponseDTO`. The DTO covers all
  // three buckets (`today` / `pending` / `decided`); when `tab` is
  // set the server zeros out the other buckets so the response shape
  // never changes per call.
  getInbox: (opts?: { tab?: InboxTab; projectId?: string; limit?: number }) => {
    const qs = new URLSearchParams();
    if (opts?.tab) qs.set('tab', opts.tab);
    if (opts?.projectId) qs.set('projectId', opts.projectId);
    if (opts?.limit) qs.set('limit', String(opts.limit));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return request<InboxResponseDTO>(`/v1/inbox${suffix}`);
  },

  // Wave POS — Inbox decision actions.
  // POST /v1/inbox/:id/decide — persists approved / snoozed / dismissed.
  inboxDecide: (
    itemId: string,
    decision: 'approved' | 'snoozed' | 'dismissed',
    opts?: {
      undoKey?: string;
      itemKind?: string;
      sourceTable?: string;
      sourceId?: string;
      prumoCardId?: string | null;
    },
  ) =>
    request<{ id: string; decision: string; undoKey: string | null; decidedAt: string }>(
      `/v1/inbox/${encodeURIComponent(itemId)}/decide`,
      {
        method: 'POST',
        body: JSON.stringify({
          decision,
          undoKey: opts?.undoKey ?? null,
          itemKind: opts?.itemKind ?? null,
          sourceTable: opts?.sourceTable ?? null,
          sourceId: opts?.sourceId ?? null,
          prumoCardId: opts?.prumoCardId ?? null,
        }),
      },
    ),

  // DELETE /v1/inbox/decisions/:undoKey — soft-deletes decision so item re-appears.
  inboxUndoDecision: (undoKey: string) =>
    request<void>(`/v1/inbox/decisions/${encodeURIComponent(undoKey)}`, {
      method: 'DELETE',
    }),

  // Wave 312 — Prumo proposal queue (the operational copilot's surface).
  // Wave 314+ — `status` is a comma-separated list (e.g. "executed,declined,expired")
  // for the Histórico tab. Defaults to "proposed" server-side.
  listPrumoProposals: (opts?: { projectId?: string; kind?: string; limit?: number; status?: string }) => {
    const qs = new URLSearchParams();
    if (opts?.projectId) qs.set('projectId', opts.projectId);
    if (opts?.kind) qs.set('kind', opts.kind);
    if (opts?.limit) qs.set('limit', String(opts.limit));
    if (opts?.status) qs.set('status', opts.status);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return request<PrumoProposal[]>(`/v1/prumo/proposals${suffix}`);
  },
  getPrumoProposal: (id: string) =>
    request<PrumoProposal>(`/v1/prumo/proposals/${encodeURIComponent(id)}`),
  approvePrumoProposal: (id: string) =>
    request<{ status: string; outcome?: Record<string, unknown>; error?: string }>(
      `/v1/prumo/proposals/${encodeURIComponent(id)}/approve`,
      { method: 'POST', body: JSON.stringify({}) },
    ),
  declinePrumoProposal: (id: string, note?: string) =>
    request<{ status: string }>(
      `/v1/prumo/proposals/${encodeURIComponent(id)}/decline`,
      { method: 'POST', body: JSON.stringify({ note: note ?? null }) },
    ),
  // Wave 313+ — admin-only manual trigger so a demo can land cards
  // without waiting for the next cron tick.
  runPrumoProposers: () =>
    request<{
      po: { scanned?: number; proposed?: number; skipped?: number; errors?: number; error?: string };
      rdo: { scanned?: number; proposed?: number; skipped?: number; errors?: number; error?: string };
      medicao: { scanned?: number; proposed?: number; skipped?: number; errors?: number; error?: string };
    }>('/v1/admin/autonomy/run-proposers', { method: 'POST', body: JSON.stringify({}) }),

  // Wave 314+ — autonomy rules CRUD for the Settings panel.
  listAutonomyRules: () => request<AutonomyRule[]>('/v1/autonomy/rules'),
  toggleAutonomyRule: (id: string, enabled: boolean) =>
    request<{ ok: true }>(`/v1/autonomy/rules/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify({ enabled }),
    }),
  deleteAutonomyRule: (id: string) =>
    request<{ ok: true }>(`/v1/autonomy/rules/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }),

  // Wave 380 — Product Analytics roadmap dashboard.
  getRoadmapMetrics: (params: { since: string; until: string }) =>
    request<RoadmapMetrics>(
      `/v1/admin/product-analytics/roadmap?since=${encodeURIComponent(params.since)}&until=${encodeURIComponent(params.until)}`,
    ),

  // Wave 393A · 2026-05-15 · Admin override audit history.
  // GET /v1/admin/trena/overrides — lists all admin_override_commit entries
  // written to estimates.sanity_warnings by P.1's atomic audit transaction.
  adminTrenaOverrides: (params?: {
    from?: string;
    to?: string;
    projectId?: string;
    actorUserId?: string;
    limit?: number;
    cursor?: string;
  }) => {
    const qs = new URLSearchParams();
    if (params?.from)         qs.set('from', params.from);
    if (params?.to)           qs.set('to', params.to);
    if (params?.projectId)    qs.set('projectId', params.projectId);
    if (params?.actorUserId)  qs.set('actorUserId', params.actorUserId);
    if (params?.limit != null) qs.set('limit', String(params.limit));
    if (params?.cursor)       qs.set('cursor', params.cursor);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return request<{
      overrides: Array<{
        estimateId: string;
        estimateName: string | null;
        projectId: string;
        projectName: string | null;
        actor: { userId: string; userName: string | null; role: string };
        reason: string;
        redBlockerCount: number;
        at: string;
      }>;
      hasMore: boolean;
      cursor: string | null;
    }>(`/v1/admin/trena/overrides${suffix}`);
  },

  // Wave QQQ · 2026-05-16 · Admin follow-ups telemetry.
  // GET /v1/admin/follow-ups/recent — rows from follow_ups_audit joined with projects.
  // request() strips the outer `data:` wrapper so the resolved value is
  // { rows, hasMore, cursor } directly.
  adminFollowUpsRecent: (params?: {
    from?: string;
    to?: string;
    kind?: string;
    projectId?: string;
    limit?: number;
    cursor?: string;
  }) => {
    const qs = new URLSearchParams();
    if (params?.from)          qs.set('from', params.from);
    if (params?.to)            qs.set('to', params.to);
    if (params?.kind)          qs.set('kind', params.kind);
    if (params?.projectId)     qs.set('projectId', params.projectId);
    if (params?.limit != null) qs.set('limit', String(params.limit));
    if (params?.cursor)        qs.set('cursor', params.cursor);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return request<{
      rows: Array<{
        id: string;
        projectId: string;
        projectName: string | null;
        kind: string;
        targetId: string;
        firedAt: string;
        details: Record<string, unknown> | null;
      }>;
      hasMore: boolean;
      cursor: string | null;
    }>(`/v1/admin/follow-ups/recent${suffix}`);
  },

  // Wave 392 · 2026-05-15 · Admin Trena R2 storage stats.
  // GET /v1/admin/trena/storage-stats — totals, orphan count, truncation flag.
  // The API returns { data: { ... } }; request() strips the outer data: wrapper,
  // so the resolved value is the inner object directly.
  adminTrenaStorageStats: () =>
    request<{
      totalObjects: number;
      totalBytes: number;
      oldestObjectAt: string | null;
      newestObjectAt: string | null;
      orphanCount: number;
      truncated: boolean;
      cursor?: string;
    }>('/v1/admin/trena/storage-stats'),

  // WWWWW · 2026-05-16 · orphan R2 file cleanup (dry-run + confirmed delete).
  // GET /v1/admin/trena/orphans returns the candidate list; the admin UI
  // shows it in a confirmation drawer before any deletion happens.
  adminTrenaListOrphans: () =>
    request<{
      orphans: Array<{ key: string; size: number; uploadedAt: string | null }>;
      totalCount: number;
      totalBytes: number;
    }>('/v1/admin/trena/orphans'),

  // POST /v1/admin/trena/cleanup-orphans — actual deletion. Server-side
  // re-verifies orphan status on every key before deleting so a row that
  // started referencing a key between dry-run and confirm is never touched.
  adminTrenaCleanupOrphans: (keys: string[]) =>
    request<{
      requested: number;
      deleted: number;
      skipped: number;
      deletedKeys: Array<{ key: string; size: number }>;
      skippedKeys: Array<{ key: string; reason: string }>;
    }>('/v1/admin/trena/cleanup-orphans', {
      method: 'POST',
      body: JSON.stringify({ keys }),
    }),

  // Wave 393B · 2026-05-15 · Admin Trena compositions compat audit.
  // Wave uuuuu · 2026-05-16 · Unwrap server `{ rows }` envelope so callers
  //   receive a plain array (the page treated the response as iterable and
  //   crashed in production with `n.map is not a function`).
  // GET /v1/trena/compositions — returns all compositions including
  // requiredMeasurementKeys / blockedMeasurementKeys compat arrays.
  // request() strips the outer `data:` envelope; the inner shape is
  // `{ rows: Composition[] }`, so we pull `.rows` out here.
  adminTrenaCompositions: async (category?: string) => {
    const suffix = category ? `?category=${encodeURIComponent(category)}` : '';
    type Composition = {
      id: string;
      code: string;
      version: number;
      name: string | null;
      description: string | null;
      baseUnit: string | null;
      category: string | null;
      source: string | null;
      confidence: string | null;
      editable: boolean;
      isSystem: boolean;
      notes: string | null;
      requiredMeasurementKeys: string[] | null;
      blockedMeasurementKeys: string[] | null;
    };
    const r = await request<{ rows: Composition[] }>(
      `/v1/trena/compositions${suffix}`,
    );
    return r.rows ?? [];
  },

  // Wave 0198 · PATCH /v1/trena/compositions/:compositionId/compat
  // Admin-only: update required/blocked measurement keys with audit trail.
  updateCompositionCompat: (
    compositionId: string,
    body: {
      requiredMeasurementKeys?: string[] | null;
      blockedMeasurementKeys?: string[] | null;
      reason?: string;
    },
  ) =>
    request<{
      updatedComposition: {
        id: string;
        code: string;
        version: number;
        name: string | null;
        description: string | null;
        baseUnit: string | null;
        category: string | null;
        source: string | null;
        confidence: string | null;
        editable: boolean;
        isSystem: boolean;
        notes: string | null;
        requiredMeasurementKeys: string[] | null;
        blockedMeasurementKeys: string[] | null;
      };
    }>(`/v1/trena/compositions/${encodeURIComponent(compositionId)}/compat`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),

  // Wave 0198 · GET /v1/trena/compositions/:compositionId/compat/audit
  // Admin-only: compat audit history for a composition (newest-first, limit 50).
  getCompositionCompatAudit: (compositionId: string) =>
    request<{
      rows: Array<{
        id: string;
        composition_id: string;
        before_required: string | null;
        after_required: string | null;
        before_blocked: string | null;
        after_blocked: string | null;
        edited_by: string;
        edited_at: string;
        reason: string | null;
        editor_name: string | null;
      }>;
    }>(`/v1/trena/compositions/${encodeURIComponent(compositionId)}/compat/audit`),

  // Wave 0189 · Edit-audit history per measurement (newest-first, limit 50).
  // GET /v1/projects/:pid/trena/estimates/:eid/measurements/:mid/audit
  // Auth: PM or admin (matches PATCH measurement pattern).
  getTrenaMeasurementAudit: (
    projectId: string,
    estimateId: string,
    measurementId: string,
  ) =>
    request<Array<{
      id: string;
      tenant_id: string;
      estimate_id: string;
      measurement_id: string;
      before_value: number | null;
      after_value: number | null;
      before_unit: string | null;
      after_unit: string | null;
      before_confidence: string | null;
      after_confidence: string | null;
      edited_by: string;
      edited_at: string;
      reason: string | null;
      source: string;
      impact_cents_delta: number | null;
      editor_name: string | null;
    }>>(`/v1/projects/${projectId}/trena/estimates/${estimateId}/measurements/${measurementId}/audit`),

  // Wave 0189 · Edit-audit history per assumption (newest-first, limit 50).
  // GET /v1/projects/:pid/trena/estimates/:eid/assumptions/:aid/audit
  // Auth: PM or admin (matches PATCH assumption pattern).
  getTrenaAssumptionAudit: (
    projectId: string,
    estimateId: string,
    assumptionId: string,
  ) =>
    request<Array<{
      id: string;
      tenant_id: string;
      estimate_id: string;
      assumption_id: string;
      before_value: string | null;
      after_value: string | null;
      before_unit: string | null;
      after_unit: string | null;
      before_confidence: string | null;
      after_confidence: string | null;
      edited_by: string;
      edited_at: string;
      reason: string | null;
      source: string;
      impact_cents_delta: number | null;
      editor_name: string | null;
    }>>(`/v1/projects/${projectId}/trena/estimates/${estimateId}/assumptions/${assumptionId}/audit`),

  // Trena Reliability Brief · Items 1+2 · 2026-05-15
  // Reject, restore, and promote operations for measurements and assumptions.
  // Auth: PM or admin. All write audit entries.

  // POST .../measurements/:mid/reject — body: { reason: string ≥5 }
  rejectTrenaMeasurement: (
    projectId: string,
    estimateId: string,
    measurementId: string,
    reason: string,
  ) =>
    request<{ rowId: string; reviewState: 'rejected'; rejectedAt: string }>(
      `/v1/projects/${projectId}/trena/estimates/${estimateId}/measurements/${measurementId}/reject`,
      { method: 'POST', body: JSON.stringify({ reason }) },
    ),

  // POST .../assumptions/:aid/reject — body: { reason: string ≥5 }
  rejectTrenaAssumption: (
    projectId: string,
    estimateId: string,
    assumptionId: string,
    reason: string,
  ) =>
    request<{ rowId: string; reviewState: 'rejected'; rejectedAt: string }>(
      `/v1/projects/${projectId}/trena/estimates/${estimateId}/assumptions/${assumptionId}/reject`,
      { method: 'POST', body: JSON.stringify({ reason }) },
    ),

  // POST .../measurements/:mid/restore — body: {}
  restoreTrenaMeasurement: (
    projectId: string,
    estimateId: string,
    measurementId: string,
  ) =>
    request<{ rowId: string; reviewState: null }>(
      `/v1/projects/${projectId}/trena/estimates/${estimateId}/measurements/${measurementId}/restore`,
      { method: 'POST', body: '{}' },
    ),

  // POST .../assumptions/:aid/restore — body: {}
  restoreTrenaAssumption: (
    projectId: string,
    estimateId: string,
    assumptionId: string,
  ) =>
    request<{ rowId: string; reviewState: null }>(
      `/v1/projects/${projectId}/trena/estimates/${estimateId}/assumptions/${assumptionId}/restore`,
      { method: 'POST', body: '{}' },
    ),

  // POST .../measurements/:mid/promote-to-assumption — body: { reason? }
  promoteTrenaMeasurementToAssumption: (
    projectId: string,
    estimateId: string,
    measurementId: string,
    opts?: { reason?: string },
  ) =>
    request<{ oldId: string; newId: string; key: string }>(
      `/v1/projects/${projectId}/trena/estimates/${estimateId}/measurements/${measurementId}/promote-to-assumption`,
      { method: 'POST', body: JSON.stringify(opts ?? {}) },
    ),

  // POST .../assumptions/:aid/promote-to-measurement — body: { value, unit, scope, reason? }
  promoteTrenaAssumptionToMeasurement: (
    projectId: string,
    estimateId: string,
    assumptionId: string,
    params: { value: number; unit: string; scope: 'projeto' | 'ambiente' | 'externa'; reason?: string },
  ) =>
    request<{ oldId: string; newId: string; key: string }>(
      `/v1/projects/${projectId}/trena/estimates/${estimateId}/assumptions/${assumptionId}/promote-to-measurement`,
      { method: 'POST', body: JSON.stringify(params) },
    ),

  // ── Trena PM questions (Wave 29.4) ───────────────────────────────────
  // Surfaces reflection-pass output to the wizard so the PM can answer
  // open questions and dismiss "didn't understand" sections.
  //
  // GET    pm-questions                       → PmQuestionsResponse
  // PATCH  pm-questions/:qid  body { answer } → { ok: true } upsert
  // DELETE pm-questions/did-not-understand/:section  (encodeURIComponent)
  getPmQuestions: (projectId: string, estimateId: string) =>
    request<PmQuestionsResponse>(
      `/v1/projects/${projectId}/trena/estimates/${estimateId}/pm-questions`,
    ),

  answerPmQuestion: (
    projectId: string,
    estimateId: string,
    questionId: string,
    answer: string,
  ) =>
    request<{ ok: true }>(
      `/v1/projects/${projectId}/trena/estimates/${estimateId}/pm-questions/${encodeURIComponent(questionId)}`,
      { method: 'PATCH', body: JSON.stringify({ answer }) },
    ),

  dismissDidNotUnderstand: (projectId: string, estimateId: string, section: string) =>
    request<{ ok: true }>(
      `/v1/projects/${projectId}/trena/estimates/${estimateId}/pm-questions/did-not-understand/${encodeURIComponent(section)}`,
      { method: 'DELETE' },
    ),

  // WAVE 29.4 bug fix · 2026-05-22 · PM digita os dados que o item de
  // didNotUnderstand pediu (ex.: "12 degraus, 1.20m, granito"). O item
  // permanece no array como histórico — só ganha pmResponse + respondedAt
  // e a resposta é espelhada em pm_question_responses_json pra hint
  // injection no próximo /re-extract.
  respondToDidNotUnderstand: (
    projectId: string,
    estimateId: string,
    section: string,
    pmResponse: string,
  ) =>
    request<{ ok: true }>(
      `/v1/projects/${projectId}/trena/estimates/${estimateId}/pm-questions/did-not-understand/${encodeURIComponent(section)}`,
      { method: 'PATCH', body: JSON.stringify({ pmResponse }) },
    ),

  // WAVE 29.5 · re-roda o pipeline de extração reutilizando o último PDF
  // commitado no R2 e injetando as respostas do PM como hints no system
  // prompt.  Sem hints → 422 (`re_extract_no_hints`).  Sem PDF disponível
  // → 422 (`re_extract_no_source_doc`) — UI mostra mensagem pedindo reupload.
  reExtractWithStoredHints: (
    projectId: string,
    estimateId: string,
    hintsFromPm?: Record<string, string>,
  ) =>
    request<{
      estimateId: string;
      hintsUsed: number;
      summary: string;
      proposed: {
        measurements: TrenaExtractMeasurementEntry[];
        assumptions: TrenaExtractAssumptionEntry[];
      };
    }>(
      `/v1/projects/${projectId}/trena/estimates/${estimateId}/re-extract`,
      {
        method: 'POST',
        body: JSON.stringify(hintsFromPm ? { hintsFromPm } : {}),
      },
    ),

  // Wave (project-trena-overview) · 2026-05-15
  // GET /v1/projects/:pid/trena/estimates — paginated list with trust indicators.
  // Each row includes reviewStateDist, pendingOverrideCount, totalCents, lastEdited*.
  listProjectTrenaEstimates: (
    projectId: string,
    params?: { status?: string; cursor?: string; limit?: number },
  ) => {
    const qs = new URLSearchParams();
    if (params?.status)          qs.set('status', params.status);
    if (params?.cursor)          qs.set('cursor', params.cursor);
    if (params?.limit != null)   qs.set('limit', String(params.limit));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return request<{
      rows: Array<{
        id: string;
        name: string;
        status: string;
        createdAt: string;
        updatedAt: string;
        approvedAt: string | null;
        totalCents: number | null;
        reviewStateDist: {
          safe_to_use: number;
          needs_confirmation: number;
          blocked: number;
          manually_reviewed: number;
          legacy: number;
        };
        pendingOverrideCount: number;
        lastEditedBy: string | null;
        lastEditedAt: string | null;
      }>;
      hasMore: boolean;
      cursor: string | null;
    }>(`/v1/projects/${projectId}/trena/estimates${suffix}`);
  },

  previewTrenaChecklist: (projectId: string, estimateId: string) =>
    request<{ checklist: ServerChecklistPreview }>(
      `/v1/projects/${projectId}/trena/estimates/${estimateId}/checklist-preview`,
    ),

  // Wave MMMMM · 2026-05-16 · Side-by-side compare of two estimates.
  // GET /v1/estimates/:id/compare?withEstimateId=<other>[&allowCrossProject=1]
  //
  // Tenant-scoped on the server. Same project by default; cross-project
  // requires admin override (allowCrossProject=1) AND same client.
  //
  // Wave QQQQQ · 2026-05-16 — accepts optional `leftSnapshotId` /
  // `rightSnapshotId` so the revision timeline can pipe snapshot ids in
  // place of (or in addition to) live estimate ids. See compareTrenaSnapshots
  // for the higher-level helper.
  compareTrenaEstimates: (
    leftId: string,
    rightId: string,
    opts?: {
      allowCrossProject?: boolean;
      leftSnapshotId?: string;
      rightSnapshotId?: string;
    },
  ) => {
    const qs = new URLSearchParams({ withEstimateId: rightId });
    if (opts?.allowCrossProject) qs.set('allowCrossProject', '1');
    if (opts?.leftSnapshotId) qs.set('leftSnapshotId', opts.leftSnapshotId);
    if (opts?.rightSnapshotId) qs.set('withSnapshotId', opts.rightSnapshotId);
    return request<TrenaEstimateCompareResult>(
      `/v1/estimates/${encodeURIComponent(leftId)}/compare?${qs.toString()}`,
    );
  },

  // WAVE 34 · H3.4 · 2026-05-22 · Confidence multi-estimate compare.
  // GET /v1/projects/:id/trena/estimates/compare?ids=A,B[,C]
  //
  // Loads 2..3 estimates by id and returns each ConfidenceEstimate plus
  // their active assertions map. Used by TrenaCompareEstimatesPage to
  // render central + band + buckets diff for "telha cerâmica vs metálica",
  // "BDI 18 vs 22%" scenarios.
  compareTrenaConfidenceEstimates: (projectId: string, ids: string[]) => {
    const qs = new URLSearchParams({ ids: ids.join(',') });
    return request<{
      estimates: Array<{
        id: string;
        name: string;
        status: string;
        confidenceMode: boolean;
        previewMode: boolean;
        totalCents: number | null;
        createdAt: string;
        updatedAt: string;
        confidence: ConfidenceEstimate | null;
        assertionsMap: Record<string, string>;
      }>;
    }>(
      `/v1/projects/${encodeURIComponent(projectId)}/trena/estimates/compare?${qs.toString()}`,
    );
  },

  // Wave QQQQQ · 2026-05-16 · revision timeline.
  // GET /v1/projects/:projectId/trena/estimates/:estimateId/snapshots
  listTrenaSnapshots: (projectId: string, estimateId: string) =>
    request<{ items: TrenaSnapshotEntry[] }>(
      `/v1/projects/${encodeURIComponent(projectId)}/trena/estimates/${encodeURIComponent(estimateId)}/snapshots`,
    ),

  // GET /v1/projects/:projectId/trena/estimates/:estimateId/snapshots/:snapshotId
  getTrenaSnapshot: (projectId: string, estimateId: string, snapshotId: string) =>
    request<TrenaSnapshotFull>(
      `/v1/projects/${encodeURIComponent(projectId)}/trena/estimates/${encodeURIComponent(estimateId)}/snapshots/${encodeURIComponent(snapshotId)}`,
    ),

  // POST /v1/projects/:projectId/trena/estimates/:estimateId/snapshots/manual
  // Always force-captures (server-side); body.note becomes the timeline label.
  createTrenaManualSnapshot: (
    projectId: string,
    estimateId: string,
    body: { note?: string | null },
  ) =>
    request<{
      snapshotId: string | null;
      totalCents: number;
      skippedByThrottle: boolean;
    }>(
      `/v1/projects/${encodeURIComponent(projectId)}/trena/estimates/${encodeURIComponent(estimateId)}/snapshots/manual`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  // Trena — export estimate as CSV. Uses a manual fetch (same pattern as
  // downloadPortfolioCsv) because the endpoint returns text/csv, not JSON,
  // and a plain <a href> can't send the Authorization header.
  downloadTrenaCsv: async (projectId: string, estimateId: string): Promise<void> => {
    const token = useAuth.getState().token;
    const headers = new Headers();
    if (token) headers.set('authorization', `Bearer ${token}`);
    const res = await fetch(
      `${API_BASE}/v1/projects/${projectId}/trena/estimates/${estimateId}/export.csv`,
      { headers },
    );
    if (!res.ok) {
      throw new ApiError(
        res.status,
        'trena_csv_failed',
        `Não foi possível exportar a estimativa (HTTP ${res.status}).`,
      );
    }
    const blob = new Blob([await res.text()], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `trena-${estimateId}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1_000);
  },

  // Trena — export estimate as PDF. Mirrors downloadTrenaCsv: manual fetch
  // with Authorization header (a plain <a href> cannot send Bearer tokens),
  // then saves via URL.createObjectURL + synthetic <a> click.
  // The server uses Browser Rendering (Puppeteer) to generate the PDF bytes.
  downloadTrenaPdf: async (projectId: string, estimateId: string): Promise<void> => {
    const token = useAuth.getState().token;
    const headers = new Headers();
    if (token) headers.set('authorization', `Bearer ${token}`);
    const res = await fetch(
      `${API_BASE}/v1/projects/${projectId}/trena/estimates/${estimateId}/export.pdf`,
      { headers },
    );
    if (!res.ok) {
      throw new ApiError(
        res.status,
        'trena_pdf_failed',
        `Não foi possível exportar o PDF da estimativa (HTTP ${res.status}).`,
      );
    }
    const blob = new Blob([await res.arrayBuffer()], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `trena-${estimateId}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1_000);
  },

  // --- Wave ddddd · Per-project monthly owner report PDF ---

  /**
   * Download the monthly owner-report PDF for a project.
   * Uses a manual fetch (same pattern as downloadTrenaPdf) so it can
   * send the Bearer token — a plain <a href> cannot do that.
   *
   * @param projectId  the project UUID
   * @param month      YYYY-MM (defaults to current month if omitted)
   */
  downloadOwnerReportPdf: async (projectId: string, month?: string): Promise<void> => {
    const resolvedMonth =
      month ??
      (() => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
      })();
    const token = useAuth.getState().token;
    const headers = new Headers();
    if (token) headers.set('authorization', `Bearer ${token}`);
    const res = await fetch(
      `${API_BASE}/v1/projects/${projectId}/owner-report.pdf?month=${encodeURIComponent(resolvedMonth)}`,
      { headers },
    );
    if (!res.ok) {
      throw new ApiError(
        res.status,
        'owner_report_pdf_failed',
        `Não foi possível gerar o relatório mensal (HTTP ${res.status}).`,
      );
    }
    const blob = new Blob([await res.arrayBuffer()], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `relatorio-mensal-${resolvedMonth}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1_000);
  },

  // --- Wave 54 · Async owner-report PDF via job queue (202 + polling) ---

  /**
   * POST /v1/projects/:pid/owner-report/jobs
   * Returns immediately with { jobId, pollUrl }.
   * Caller polls getOwnerReportJob every 2s until status='done'|'error'.
   */
  createOwnerReportJob: async (
    projectId: string,
    month?: string,
  ): Promise<{ jobId: string; pollUrl: string; status: string; message: string }> => {
    const resolvedMonth =
      month ??
      (() => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
      })();
    return request<{ jobId: string; pollUrl: string; status: string; message: string }>(
      `/v1/projects/${projectId}/owner-report/jobs?month=${encodeURIComponent(resolvedMonth)}`,
      { method: 'POST' },
    );
  },

  /**
   * GET /v1/projects/:pid/owner-report/jobs/:jobId
   * Returns { status, r2Url, errorMsg, ... }.
   */
  getOwnerReportJob: async (
    projectId: string,
    jobId: string,
  ): Promise<{
    jobId: string;
    projectId: string;
    month: string;
    status: 'pending' | 'processing' | 'done' | 'error';
    r2Url: string | null;
    errorMsg: string | null;
    createdAt: string;
    completedAt: string | null;
  }> => {
    return request(
      `/v1/projects/${projectId}/owner-report/jobs/${jobId}`,
    );
  },

  // --- Wave KKKKK · Per-project backup / export (.zip) ---

  /**
   * Preview-only — returns counts the UI uses to decide whether to show a
   * confirmation modal ("Este projeto tem 87 estimativas. O backup pode
   * levar ~30s.") before kicking off the heavier zip build.
   */
  previewProjectExportZip: async (
    projectId: string,
  ): Promise<{
    estimateCount: number;
    rdoCount: number;
    drawingCount: number;
    documentCount: number;
    isLarge: boolean;
  }> => {
    const res = await request<{
      data: {
        estimateCount: number;
        rdoCount: number;
        drawingCount: number;
        documentCount: number;
        isLarge: boolean;
      };
    }>(`/v1/projects/${encodeURIComponent(projectId)}/export-zip/preview`);
    return res.data;
  },

  /**
   * Downloads the full project backup as a .zip. Manual fetch is required
   * because <a href> can't send the Bearer token. The server picks the
   * filename from the project name + ISO date; we honor it via the
   * content-disposition header when present, falling back to a sensible
   * default.
   */
  downloadProjectExportZip: async (
    projectId: string,
    opts: { skipExcel?: boolean } = {},
  ): Promise<{ bytes: number }> => {
    const token = useAuth.getState().token;
    const headers = new Headers();
    if (token) headers.set('authorization', `Bearer ${token}`);
    const params = new URLSearchParams();
    if (opts.skipExcel) params.set('skipExcel', 'true');
    const qs = params.toString() ? `?${params}` : '';
    const res = await fetch(
      `${API_BASE}/v1/projects/${encodeURIComponent(projectId)}/export-zip${qs}`,
      { headers },
    );
    if (!res.ok) {
      throw new ApiError(
        res.status,
        'project_export_failed',
        `Não foi possível gerar o backup do projeto (HTTP ${res.status}).`,
      );
    }
    const buf = await res.arrayBuffer();
    const blob = new Blob([buf], { type: 'application/zip' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    // Derive filename from content-disposition or fall back to date slug.
    const cd = res.headers.get('content-disposition') ?? '';
    const match = cd.match(/filename="?([^"]+)"?/);
    a.download =
      match?.[1] ??
      `projeto-${projectId.slice(-8)}-backup-${new Date().toISOString().slice(0, 10)}.zip`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1_000);
    return { bytes: buf.byteLength };
  },

  // --- Wave 394 · Budget Excel import/export ---

  /** Preview an xlsx file for budget import: returns mapping + confidence heuristics. */
  previewExcel: async (projectId: string, file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    const token = useAuth.getState().token;
    const res = await fetch(
      `${API_BASE}/v1/projects/${encodeURIComponent(projectId)}/budget/excel-import/preview`,
      {
        method: 'POST',
        body: fd,
        headers: token ? { authorization: `Bearer ${token}` } : {},
      },
    );
    type PreviewResp = {
      data?: import('./budget-excel-types').ExcelImportPreview;
      error?: { message: string };
    };
    const payload = (await res.json().catch(() => null)) as PreviewResp | null;
    if (!res.ok || !payload?.data) {
      throw new ApiError(
        res.status,
        'preview_failed',
        payload?.error?.message ?? `HTTP ${res.status}`,
      );
    }
    return payload.data;
  },

  /** Apply (or dry-run) an xlsx budget import with the given column mapping. */
  applyExcel: async (
    projectId: string,
    file: File,
    mapping: Record<string, string | null>,
    dryRun: boolean,
  ) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('mapping', JSON.stringify(mapping));
    fd.append('dryRun', dryRun ? 'true' : 'false');
    const token = useAuth.getState().token;
    const res = await fetch(
      `${API_BASE}/v1/projects/${encodeURIComponent(projectId)}/budget/excel-import/apply`,
      {
        method: 'POST',
        body: fd,
        headers: token ? { authorization: `Bearer ${token}` } : {},
      },
    );
    type ApplyResp = {
      data?: import('./budget-excel-types').ExcelApplyResult;
      error?: { message: string };
    };
    const payload = (await res.json().catch(() => null)) as ApplyResp | null;
    if (!res.ok || !payload?.data) {
      throw new ApiError(
        res.status,
        'apply_failed',
        payload?.error?.message ?? `HTTP ${res.status}`,
      );
    }
    return payload.data;
  },

  /** Download the project budget as an .xlsx file. */
  downloadExcel: async (projectId: string) => {
    const token = useAuth.getState().token;
    const res = await fetch(
      `${API_BASE}/v1/projects/${encodeURIComponent(projectId)}/budget/excel-export`,
      {
        headers: token ? { authorization: `Bearer ${token}` } : {},
      },
    );
    if (!res.ok) {
      throw new ApiError(res.status, 'export_failed', `HTTP ${res.status}`);
    }
    const disposition = res.headers.get('content-disposition') ?? '';
    const match = disposition.match(/filename="([^"]+)"/);
    const filename = match?.[1] ?? `orcamento.xlsx`;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1_000);
  },

  // --- Wave MMMM · Compras Excel import/export ---

  /** Preview an xlsx file for Compras import: returns mapping + confidence heuristics. */
  previewComprasExcel: async (projectId: string, file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    const token = useAuth.getState().token;
    const res = await fetch(
      `${API_BASE}/v1/projects/${encodeURIComponent(projectId)}/compras/excel-import/preview`,
      {
        method: 'POST',
        body: fd,
        headers: token ? { authorization: `Bearer ${token}` } : {},
      },
    );
    type PreviewResp = {
      data?: import('./compras-excel-types').ComprasExcelImportPreview;
      error?: { message: string };
    };
    const payload = (await res.json().catch(() => null)) as PreviewResp | null;
    if (!res.ok || !payload?.data) {
      throw new ApiError(
        res.status,
        'compras_preview_failed',
        payload?.error?.message ?? `HTTP ${res.status}`,
      );
    }
    return payload.data;
  },

  /** Apply (or dry-run) an xlsx Compras import with the given column mapping. */
  applyComprasExcel: async (
    projectId: string,
    file: File,
    mapping: Record<string, string | null>,
    dryRun: boolean,
  ) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('mapping', JSON.stringify(mapping));
    fd.append('dryRun', dryRun ? 'true' : 'false');
    const token = useAuth.getState().token;
    const res = await fetch(
      `${API_BASE}/v1/projects/${encodeURIComponent(projectId)}/compras/excel-import/apply`,
      {
        method: 'POST',
        body: fd,
        headers: token ? { authorization: `Bearer ${token}` } : {},
      },
    );
    type ApplyResp = {
      data?: import('./compras-excel-types').ComprasExcelApplyResult;
      error?: { message: string };
    };
    const payload = (await res.json().catch(() => null)) as ApplyResp | null;
    if (!res.ok || !payload?.data) {
      throw new ApiError(
        res.status,
        'compras_apply_failed',
        payload?.error?.message ?? `HTTP ${res.status}`,
      );
    }
    return payload.data;
  },

  /** Download the project Compras (purchase orders) as an .xlsx file. */
  downloadComprasExcel: async (projectId: string) => {
    const token = useAuth.getState().token;
    const res = await fetch(
      `${API_BASE}/v1/projects/${encodeURIComponent(projectId)}/compras/excel-export`,
      {
        headers: token ? { authorization: `Bearer ${token}` } : {},
      },
    );
    if (!res.ok) {
      throw new ApiError(res.status, 'compras_export_failed', `HTTP ${res.status}`);
    }
    const disposition = res.headers.get('content-disposition') ?? '';
    const match = disposition.match(/filename="([^"]+)"/);
    const filename = match?.[1] ?? `compras.xlsx`;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1_000);
  },

  // --- Wave NNNN · Cronograma Excel import/export (project-scoped) ---

  /** Preview an xlsx file for Cronograma import: returns mapping + confidence heuristics. */
  previewCronogramaExcel: async (projectId: string, file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    const token = useAuth.getState().token;
    const res = await fetch(
      `${API_BASE}/v1/projects/${encodeURIComponent(projectId)}/cronograma/excel-import/preview`,
      {
        method: 'POST',
        body: fd,
        headers: token ? { authorization: `Bearer ${token}` } : {},
      },
    );
    type PreviewResp = {
      data?: import('./cronograma-excel-types').CronogramaExcelImportPreview;
      error?: { message: string };
    };
    const payload = (await res.json().catch(() => null)) as PreviewResp | null;
    if (!res.ok || !payload?.data) {
      throw new ApiError(
        res.status,
        'cronograma_preview_failed',
        payload?.error?.message ?? `HTTP ${res.status}`,
      );
    }
    return payload.data;
  },

  /** Apply (or dry-run) an xlsx Cronograma import with the given column mapping. */
  applyCronogramaExcel: async (
    projectId: string,
    file: File,
    mapping: Record<string, string | null>,
    dryRun: boolean,
  ) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('mapping', JSON.stringify(mapping));
    fd.append('dryRun', dryRun ? 'true' : 'false');
    const token = useAuth.getState().token;
    const res = await fetch(
      `${API_BASE}/v1/projects/${encodeURIComponent(projectId)}/cronograma/excel-import/apply`,
      {
        method: 'POST',
        body: fd,
        headers: token ? { authorization: `Bearer ${token}` } : {},
      },
    );
    type ApplyResp = {
      data?: import('./cronograma-excel-types').CronogramaExcelApplyResult;
      error?: { message: string };
    };
    const payload = (await res.json().catch(() => null)) as ApplyResp | null;
    if (!res.ok || !payload?.data) {
      throw new ApiError(
        res.status,
        'cronograma_apply_failed',
        payload?.error?.message ?? `HTTP ${res.status}`,
      );
    }
    return payload.data;
  },

  /** Download the project Cronograma (work stages) as an .xlsx file. */
  downloadCronogramaExcel: async (projectId: string) => {
    const token = useAuth.getState().token;
    const res = await fetch(
      `${API_BASE}/v1/projects/${encodeURIComponent(projectId)}/cronograma/excel-export`,
      {
        headers: token ? { authorization: `Bearer ${token}` } : {},
      },
    );
    if (!res.ok) {
      throw new ApiError(res.status, 'cronograma_export_failed', `HTTP ${res.status}`);
    }
    const disposition = res.headers.get('content-disposition') ?? '';
    const match = disposition.match(/filename="([^"]+)"/);
    const filename = match?.[1] ?? `cronograma.xlsx`;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1_000);
  },

  // --- Wave OOOO · Medições Excel import/export (project-scoped) ---

  /** Preview an xlsx file for Medições import: returns mapping + confidence heuristics. */
  previewMedicoesExcel: async (projectId: string, file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    const token = useAuth.getState().token;
    const res = await fetch(
      `${API_BASE}/v1/projects/${encodeURIComponent(projectId)}/medicoes/excel-import/preview`,
      {
        method: 'POST',
        body: fd,
        headers: token ? { authorization: `Bearer ${token}` } : {},
      },
    );
    type PreviewResp = {
      data?: import('./medicoes-excel-types').MedicoesExcelImportPreview;
      error?: { message: string };
    };
    const payload = (await res.json().catch(() => null)) as PreviewResp | null;
    if (!res.ok || !payload?.data) {
      throw new ApiError(
        res.status,
        'medicoes_preview_failed',
        payload?.error?.message ?? `HTTP ${res.status}`,
      );
    }
    return payload.data;
  },

  /** Apply (or dry-run) an xlsx Medições import with the given column mapping. */
  applyMedicoesExcel: async (
    projectId: string,
    file: File,
    mapping: Record<string, string | null>,
    dryRun: boolean,
  ) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('mapping', JSON.stringify(mapping));
    fd.append('dryRun', dryRun ? 'true' : 'false');
    const token = useAuth.getState().token;
    const res = await fetch(
      `${API_BASE}/v1/projects/${encodeURIComponent(projectId)}/medicoes/excel-import/apply`,
      {
        method: 'POST',
        body: fd,
        headers: token ? { authorization: `Bearer ${token}` } : {},
      },
    );
    type ApplyResp = {
      data?: import('./medicoes-excel-types').MedicoesExcelApplyResult;
      error?: { message: string };
    };
    const payload = (await res.json().catch(() => null)) as ApplyResp | null;
    if (!res.ok || !payload?.data) {
      throw new ApiError(
        res.status,
        'medicoes_apply_failed',
        payload?.error?.message ?? `HTTP ${res.status}`,
      );
    }
    return payload.data;
  },

  /** Download the project Medições (measurements) as an .xlsx file. */
  downloadMedicoesExcel: async (projectId: string) => {
    const token = useAuth.getState().token;
    const res = await fetch(
      `${API_BASE}/v1/projects/${encodeURIComponent(projectId)}/medicoes/excel-export`,
      {
        headers: token ? { authorization: `Bearer ${token}` } : {},
      },
    );
    if (!res.ok) {
      throw new ApiError(res.status, 'medicoes_export_failed', `HTTP ${res.status}`);
    }
    const disposition = res.headers.get('content-disposition') ?? '';
    const match = disposition.match(/filename="([^"]+)"/);
    const filename = match?.[1] ?? `medicoes.xlsx`;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1_000);
  },

  // --- Wave NNNN · Fornecedores Excel import/export (tenant-scoped) ---

  /** Preview an xlsx file for Fornecedores import: returns mapping + confidence heuristics. */
  previewFornecedoresExcel: async (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    const token = useAuth.getState().token;
    const res = await fetch(
      `${API_BASE}/v1/fornecedores/excel-import/preview`,
      {
        method: 'POST',
        body: fd,
        headers: token ? { authorization: `Bearer ${token}` } : {},
      },
    );
    type PreviewResp = {
      data?: import('./fornecedores-excel-types').FornecedoresExcelImportPreview;
      error?: { message: string };
    };
    const payload = (await res.json().catch(() => null)) as PreviewResp | null;
    if (!res.ok || !payload?.data) {
      throw new ApiError(
        res.status,
        'fornecedores_preview_failed',
        payload?.error?.message ?? `HTTP ${res.status}`,
      );
    }
    return payload.data;
  },

  /** Apply (or dry-run) an xlsx Fornecedores import with the given column mapping. */
  applyFornecedoresExcel: async (
    file: File,
    mapping: Record<string, string | null>,
    dryRun: boolean,
  ) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('mapping', JSON.stringify(mapping));
    fd.append('dryRun', dryRun ? 'true' : 'false');
    const token = useAuth.getState().token;
    const res = await fetch(
      `${API_BASE}/v1/fornecedores/excel-import/apply`,
      {
        method: 'POST',
        body: fd,
        headers: token ? { authorization: `Bearer ${token}` } : {},
      },
    );
    type ApplyResp = {
      data?: import('./fornecedores-excel-types').FornecedoresExcelApplyResult;
      error?: { message: string };
    };
    const payload = (await res.json().catch(() => null)) as ApplyResp | null;
    if (!res.ok || !payload?.data) {
      throw new ApiError(
        res.status,
        'fornecedores_apply_failed',
        payload?.error?.message ?? `HTTP ${res.status}`,
      );
    }
    return payload.data;
  },

  /** Download the tenant Fornecedores (suppliers) as an .xlsx file. */
  downloadFornecedoresExcel: async () => {
    const token = useAuth.getState().token;
    const res = await fetch(
      `${API_BASE}/v1/fornecedores/excel-export`,
      {
        headers: token ? { authorization: `Bearer ${token}` } : {},
      },
    );
    if (!res.ok) {
      throw new ApiError(res.status, 'fornecedores_export_failed', `HTTP ${res.status}`);
    }
    const disposition = res.headers.get('content-disposition') ?? '';
    const match = disposition.match(/filename="([^"]+)"/);
    const filename = match?.[1] ?? `fornecedores.xlsx`;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1_000);
  },

  // DELETE /v1/projects/:pid/trena/estimates/:eid · soft-delete (sets deleted_at)
  // Idempotent. Returns { ok: true, estimateId, deletedAt }.
  deleteTrenaEstimate: (
    projectId: string,
    estimateId: string,
    reason?: string,
  ) =>
    request<{ ok: true; estimateId: string; deletedAt: string }>(
      `/v1/projects/${projectId}/trena/estimates/${estimateId}`,
      {
        method: 'DELETE',
        body: reason ? JSON.stringify({ reason }) : undefined,
      },
    ),

  // Wave 99 · 2026-05-25 · single-estimate archive.
  // POST /v1/projects/:pid/trena/estimates/:eid/archive
  // Backend marca status='archived' + archived_at. Não toca budget_items
  // (audit preservado). UI usa antes de criar nova estimate pra desativar
  // a exported existente e evitar acúmulo de items duplicados no orçamento.
  archiveTrenaEstimate: (projectId: string, estimateId: string) =>
    request<{ estimateId: string; status: 'archived' }>(
      `/v1/projects/${projectId}/trena/estimates/${estimateId}/archive`,
      { method: 'POST' },
    ),

  // Arc JJJJJ · 2026-05-16 · bulk operations on Trena estimates.
  //
  // POST /v1/projects/:pid/trena/estimates/bulk-archive
  // POST /v1/projects/:pid/trena/estimates/bulk-delete  (drafts-only · server-enforced)
  //
  // Both return { ok: true, applied: number, skipped: Array<{id, reason}> } so
  // the UI can render "3 arquivadas, 1 ignorada (já arquivada)".
  bulkArchiveTrenaEstimates: (projectId: string, ids: string[]) =>
    request<{
      ok: true;
      applied: number;
      skipped: Array<{ id: string; reason: string }>;
    }>(`/v1/projects/${projectId}/trena/estimates/bulk-archive`, {
      method: 'POST',
      body: JSON.stringify({ ids }),
    }),

  bulkDeleteTrenaEstimates: (projectId: string, ids: string[], reason?: string) =>
    request<{
      ok: true;
      applied: number;
      skipped: Array<{ id: string; reason: string }>;
    }>(`/v1/projects/${projectId}/trena/estimates/bulk-delete`, {
      method: 'POST',
      body: JSON.stringify(reason ? { ids, reason } : { ids }),
    }),

  // ── Pranchas v1 (Wave TTTT) ─────────────────────────────────────────────

  listPranchas: (projectId: string) =>
    request<Array<{
      id: string; project_id: string; code: string; title: string;
      discipline: string; current_revision_id: string | null;
      revision_label: string | null; uploaded_at: string | null; created_at: string;
    }>>(`/v1/projects/${encodeURIComponent(projectId)}/drawings`),

  createPrancha: (projectId: string, body: { code: string; name: string; discipline: string }) =>
    request<{ id: string }>(
      `/v1/projects/${encodeURIComponent(projectId)}/drawings`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  listPranchaRevisions: (projectId: string, drawingId: string) =>
    request<Array<{
      id: string; version_label: string; file_name: string;
      file_size: number | null; notes: string | null;
      uploaded_by: string; uploaded_at: string;
    }>>(`/v1/projects/${encodeURIComponent(projectId)}/drawings/${encodeURIComponent(drawingId)}/revisions`),

  uploadPranchaRevision: async (
    projectId: string,
    drawingId: string,
    file: File,
    versionLabel: string,
    notes?: string,
    token?: string | null,
  ) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('versionLabel', versionLabel);
    if (notes) fd.append('notes', notes);
    const res = await fetch(
      `${API_BASE}/v1/projects/${encodeURIComponent(projectId)}/drawings/${encodeURIComponent(drawingId)}/revisions`,
      {
        method: 'POST',
        headers: token ? { authorization: `Bearer ${token}` } : {},
        body: fd,
      },
    );
    if (!res.ok) throw new ApiError(res.status, 'upload_failed', `HTTP ${res.status}`);
    const json = await res.json() as { data: { id: string; version_label: string; r2_key: string } };
    return json.data;
  },

  getPranchaRevisionSignedUrl: (projectId: string, drawingId: string, revisionId: string) =>
    request<{ signedUrl: string }>(
      `/v1/projects/${encodeURIComponent(projectId)}/drawings/${encodeURIComponent(drawingId)}/revisions/${encodeURIComponent(revisionId)}/signed-url`,
    ),

  listPranchaMarkups: (projectId: string, drawingId: string, revisionId: string, page?: number) => {
    const qs = page !== undefined ? `?page=${page}` : '';
    return request<Array<{
      id: string; revision_id: string; page_number: number;
      shape: 'box' | 'circle' | 'arrow' | 'text';
      coords: string; label: string | null; color: string;
      author_id: string; created_at: string;
    }>>(
      `/v1/projects/${encodeURIComponent(projectId)}/drawings/${encodeURIComponent(drawingId)}/revisions/${encodeURIComponent(revisionId)}/markups${qs}`,
    );
  },

  createPranchaMarkup: (
    projectId: string,
    drawingId: string,
    revisionId: string,
    body: {
      page_number: number;
      shape: 'box' | 'circle' | 'arrow' | 'text';
      coords: { x: number; y: number; w: number; h: number };
      label?: string | null;
      color?: string;
    },
  ) =>
    request<{ id: string }>(
      `/v1/projects/${encodeURIComponent(projectId)}/drawings/${encodeURIComponent(drawingId)}/revisions/${encodeURIComponent(revisionId)}/markups`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  // Wave periféricos · listagem/criação de RFIs (dúvidas) da obra. Usado
  // pelo picker de âncora em DrawingsRfiPage — substitui o input de ID cru.
  listRfis: (projectId: string, status?: string) =>
    request<Array<{
      id: string; code: string; title: string; question: string;
      status: string; asked_at: string; primary_answer: string | null;
    }>>(
      `/v1/rfis?projectId=${encodeURIComponent(projectId)}${
        status ? `&status=${encodeURIComponent(status)}` : ''
      }`,
    ),

  createRfi: (body: { projectId: string; title: string; question: string }) =>
    request<{ id: string; code: string }>(`/v1/rfis`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  listPranchaRfiAnchors: (projectId: string, drawingId: string, revisionId: string) =>
    request<Array<{
      id: string; rfi_id: string; revision_id: string; page_number: number;
      x: number; y: number; created_at: string;
      rfi_title: string | null; rfi_status: string | null; rfi_code: string | null;
    }>>(
      `/v1/projects/${encodeURIComponent(projectId)}/drawings/${encodeURIComponent(drawingId)}/revisions/${encodeURIComponent(revisionId)}/rfi-anchors`,
    ),

  createPranchaRfiAnchor: (
    projectId: string,
    drawingId: string,
    revisionId: string,
    body: { rfi_id: string; page_number: number; x: number; y: number },
  ) =>
    request<{ id: string }>(
      `/v1/projects/${encodeURIComponent(projectId)}/drawings/${encodeURIComponent(drawingId)}/revisions/${encodeURIComponent(revisionId)}/rfi-anchors`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  // --- Wave DP2 · New Excel importers (verba, estoque, rdos, pranchas, trena) ---

  previewVerbaExcel: async (projectId: string, file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    const token = useAuth.getState().token;
    const res = await fetch(`${API_BASE}/v1/projects/${encodeURIComponent(projectId)}/verba/excel/preview`, {
      method: 'POST', body: fd, headers: token ? { authorization: `Bearer ${token}` } : {},
    });
    const payload = (await res.json().catch(() => null)) as { data?: unknown; error?: { message: string } } | null;
    if (!res.ok || !payload?.data) throw new ApiError(res.status, 'verba_preview_failed', payload?.error?.message ?? `HTTP ${res.status}`);
    return payload.data;
  },

  applyVerbaExcel: async (projectId: string, file: File, mapping: Record<string, string | null>, dryRun: boolean) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('mapping', JSON.stringify(mapping));
    fd.append('dryRun', dryRun ? 'true' : 'false');
    const token = useAuth.getState().token;
    const res = await fetch(`${API_BASE}/v1/projects/${encodeURIComponent(projectId)}/verba/excel/apply`, {
      method: 'POST', body: fd, headers: token ? { authorization: `Bearer ${token}` } : {},
    });
    const payload = (await res.json().catch(() => null)) as { data?: unknown; error?: { message: string } } | null;
    if (!res.ok || !payload?.data) throw new ApiError(res.status, 'verba_apply_failed', payload?.error?.message ?? `HTTP ${res.status}`);
    return payload.data as { applied: { adds: number; updates: number; skipped: number }; auditId: string; undoPayload: unknown };
  },

  undoVerbaExcel: (projectId: string, undoPayload: unknown) =>
    request<{ ok: true }>(`/v1/projects/${projectId}/verba/excel/undo`, { method: 'POST', body: JSON.stringify({ undoPayload }) }),

  downloadVerbaExcel: async (projectId: string) => {
    const token = useAuth.getState().token;
    const res = await fetch(`${API_BASE}/v1/projects/${encodeURIComponent(projectId)}/verba/excel/export`, {
      headers: token ? { authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new ApiError(res.status, 'verba_export_failed', `HTTP ${res.status}`);
    return res.blob();
  },

  previewEstoqueExcel: async (projectId: string, file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    const token = useAuth.getState().token;
    const res = await fetch(`${API_BASE}/v1/projects/${encodeURIComponent(projectId)}/estoque/excel/preview`, {
      method: 'POST', body: fd, headers: token ? { authorization: `Bearer ${token}` } : {},
    });
    const payload = (await res.json().catch(() => null)) as { data?: unknown; error?: { message: string } } | null;
    if (!res.ok || !payload?.data) throw new ApiError(res.status, 'estoque_preview_failed', payload?.error?.message ?? `HTTP ${res.status}`);
    return payload.data;
  },

  applyEstoqueExcel: async (projectId: string, file: File, mapping: Record<string, string | null>, dryRun: boolean) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('mapping', JSON.stringify(mapping));
    fd.append('dryRun', dryRun ? 'true' : 'false');
    const token = useAuth.getState().token;
    const res = await fetch(`${API_BASE}/v1/projects/${encodeURIComponent(projectId)}/estoque/excel/apply`, {
      method: 'POST', body: fd, headers: token ? { authorization: `Bearer ${token}` } : {},
    });
    const payload = (await res.json().catch(() => null)) as { data?: unknown; error?: { message: string } } | null;
    if (!res.ok || !payload?.data) throw new ApiError(res.status, 'estoque_apply_failed', payload?.error?.message ?? `HTTP ${res.status}`);
    return payload.data as { applied: { adds: number; updates: number; skipped: number }; auditId: string; undoPayload: unknown };
  },

  downloadEstoqueExcel: async (projectId: string) => {
    const token = useAuth.getState().token;
    const res = await fetch(`${API_BASE}/v1/projects/${encodeURIComponent(projectId)}/estoque/excel/export`, {
      headers: token ? { authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new ApiError(res.status, 'estoque_export_failed', `HTTP ${res.status}`);
    return res.blob();
  },

  previewDailyLogsExcel: async (projectId: string, file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    const token = useAuth.getState().token;
    const res = await fetch(`${API_BASE}/v1/projects/${encodeURIComponent(projectId)}/rdos/excel/preview`, {
      method: 'POST', body: fd, headers: token ? { authorization: `Bearer ${token}` } : {},
    });
    const payload = (await res.json().catch(() => null)) as { data?: unknown; error?: { message: string } } | null;
    if (!res.ok || !payload?.data) throw new ApiError(res.status, 'rdos_preview_failed', payload?.error?.message ?? `HTTP ${res.status}`);
    return payload.data;
  },

  applyDailyLogsExcel: async (projectId: string, file: File, mapping: Record<string, string | null>, dryRun: boolean) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('mapping', JSON.stringify(mapping));
    fd.append('dryRun', dryRun ? 'true' : 'false');
    const token = useAuth.getState().token;
    const res = await fetch(`${API_BASE}/v1/projects/${encodeURIComponent(projectId)}/rdos/excel/apply`, {
      method: 'POST', body: fd, headers: token ? { authorization: `Bearer ${token}` } : {},
    });
    const payload = (await res.json().catch(() => null)) as { data?: unknown; error?: { message: string } } | null;
    if (!res.ok || !payload?.data) throw new ApiError(res.status, 'rdos_apply_failed', payload?.error?.message ?? `HTTP ${res.status}`);
    return payload.data as { applied: { adds: number; updates: number; skipped: number }; auditId: string; undoPayload: unknown };
  },

  undoDailyLogsExcel: (projectId: string, undoPayload: unknown) =>
    request<{ ok: true }>(`/v1/projects/${projectId}/rdos/excel/undo`, { method: 'POST', body: JSON.stringify({ undoPayload }) }),

  downloadDailyLogsExcel: async (projectId: string) => {
    const token = useAuth.getState().token;
    const res = await fetch(`${API_BASE}/v1/projects/${encodeURIComponent(projectId)}/rdos/excel/export`, {
      headers: token ? { authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new ApiError(res.status, 'rdos_export_failed', `HTTP ${res.status}`);
    return res.blob();
  },

  previewPranchasExcel: async (projectId: string, file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    const token = useAuth.getState().token;
    const res = await fetch(`${API_BASE}/v1/projects/${encodeURIComponent(projectId)}/pranchas/excel/preview`, {
      method: 'POST', body: fd, headers: token ? { authorization: `Bearer ${token}` } : {},
    });
    const payload = (await res.json().catch(() => null)) as { data?: unknown; error?: { message: string } } | null;
    if (!res.ok || !payload?.data) throw new ApiError(res.status, 'pranchas_preview_failed', payload?.error?.message ?? `HTTP ${res.status}`);
    return payload.data;
  },

  applyPranchasExcel: async (projectId: string, file: File, mapping: Record<string, string | null>, dryRun: boolean) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('mapping', JSON.stringify(mapping));
    fd.append('dryRun', dryRun ? 'true' : 'false');
    const token = useAuth.getState().token;
    const res = await fetch(`${API_BASE}/v1/projects/${encodeURIComponent(projectId)}/pranchas/excel/apply`, {
      method: 'POST', body: fd, headers: token ? { authorization: `Bearer ${token}` } : {},
    });
    const payload = (await res.json().catch(() => null)) as { data?: unknown; error?: { message: string } } | null;
    if (!res.ok || !payload?.data) throw new ApiError(res.status, 'pranchas_apply_failed', payload?.error?.message ?? `HTTP ${res.status}`);
    return payload.data as { applied: { adds: number; updates: number; skipped: number }; auditId: string; undoPayload: unknown };
  },

  downloadPranchasExcel: async (projectId: string) => {
    const token = useAuth.getState().token;
    const res = await fetch(`${API_BASE}/v1/projects/${encodeURIComponent(projectId)}/pranchas/excel/export`, {
      headers: token ? { authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new ApiError(res.status, 'pranchas_export_failed', `HTTP ${res.status}`);
    return res.blob();
  },

  previewTrenaExcel: async (projectId: string, file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    const token = useAuth.getState().token;
    const res = await fetch(`${API_BASE}/v1/projects/${encodeURIComponent(projectId)}/trena/excel/preview`, {
      method: 'POST', body: fd, headers: token ? { authorization: `Bearer ${token}` } : {},
    });
    const payload = (await res.json().catch(() => null)) as { data?: unknown; error?: { message: string } } | null;
    if (!res.ok || !payload?.data) throw new ApiError(res.status, 'trena_excel_preview_failed', payload?.error?.message ?? `HTTP ${res.status}`);
    return payload.data;
  },

  applyTrenaExcel: async (projectId: string, file: File, mapping: Record<string, string | null>, dryRun: boolean) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('mapping', JSON.stringify(mapping));
    fd.append('dryRun', dryRun ? 'true' : 'false');
    const token = useAuth.getState().token;
    const res = await fetch(`${API_BASE}/v1/projects/${encodeURIComponent(projectId)}/trena/excel/apply`, {
      method: 'POST', body: fd, headers: token ? { authorization: `Bearer ${token}` } : {},
    });
    const payload = (await res.json().catch(() => null)) as { data?: unknown; error?: { message: string } } | null;
    if (!res.ok || !payload?.data) throw new ApiError(res.status, 'trena_excel_apply_failed', payload?.error?.message ?? `HTTP ${res.status}`);
    return payload.data as { applied: { adds: number; updates: number; skipped: number }; auditId: string; undoPayload: unknown };
  },

  undoTrenaExcel: (projectId: string, undoPayload: unknown) =>
    request<{ ok: true }>(`/v1/projects/${projectId}/trena/excel/undo`, { method: 'POST', body: JSON.stringify({ undoPayload }) }),

  downloadTrenaExcel: async (projectId: string) => {
    const token = useAuth.getState().token;
    const res = await fetch(`${API_BASE}/v1/projects/${encodeURIComponent(projectId)}/trena/excel/export`, {
      headers: token ? { authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new ApiError(res.status, 'trena_excel_export_failed', `HTTP ${res.status}`);
    return res.blob();
  },

  // wave-module-progress · 2026-05-17 · Per-module activation checklist.
  // GET /v1/projects/:id/module-progress?module=<name>
  // Returns typed snapshot; React Query staleTime 60s on the consumer.
  getModuleProgress: (projectId: string, module: string) =>
    request<{
      module: string;
      data: Record<string, unknown>;
    }>(`/v1/projects/${encodeURIComponent(projectId)}/module-progress?module=${encodeURIComponent(module)}`),

  // Wave ggggg · 2026-05-16 · Admin home rollup tile counts.
  // GET /v1/admin/rollup — aggregate counts for the /admin home page.
  // request() strips the outer { data: ... } envelope.
  getAdminRollup: () =>
    request<{
      overridesThisWeek: number;
      r2TrenaDocsBytes: number;
      r2TrenaDocsObjects: number;
      compositionsTotal: number;
      compositionsWithCompat: number;
      followUpsLastDay: number;
      followUpsByKind: Record<string, number>;
      notificationsUnreadGlobal: number;
      pendingApprovals: number;
      activeUsers: number;
      tenantCount: number;
    }>('/v1/admin/rollup'),

  // --- Wave relatorios-canonicos · 2026-05-21 · /relatorios módulo dedicado ---
  /**
   * Lista os 3 relatórios canônicos disponíveis no projeto + metadata do
   * último gerado por slug (se existir). Usado pela RelatoriosPage pra
   * pintar os 3 cards e mostrar "último gerado há X".
   */
  listProjectRelatorios: (projectId: string) =>
    request<{
      reports: Array<{
        slug: 'boletim-mensal' | 'fluxo-caixa' | 'status-cliente' | 'boletim-semanal'; // W148 · 2026-05-28
        title: string;
        description: string;
        cadence: 'monthly' | 'weekly';
        periodLabel: string;
        latest: null | {
          id: string;
          tenant_id: string;
          project_id: string;
          slug: string;
          period: string;
          r2_key: string;
          size_bytes: number;
          generated_at: string;
          generated_by: string;
        };
      }>;
    }>(`/v1/projects/${encodeURIComponent(projectId)}/relatorios`),

  /**
   * Dispara a geração de um dos 3 relatórios canônicos. O backend
   * renderiza o PDF, persiste em R2 (PHOTOS bucket, prefixo reports/),
   * grava row em generated_reports e retorna metadata + downloadUrl.
   * `period` é YYYY-MM pra boletim/fluxo, YYYY-WW pra status-cliente.
   */
  generateRelatorio: (
    projectId: string,
    slug: 'boletim-mensal' | 'fluxo-caixa' | 'status-cliente' | 'boletim-semanal', // W148 · 2026-05-28
    period: string,
  ) => {
    const isWeekly = slug === 'status-cliente' || slug === 'boletim-semanal'; // W148 · 2026-05-28
    const paramKey = isWeekly ? 'week' : 'period';
    return request<{
      id: string;
      slug: string;
      period: string;
      r2Key: string;
      sizeBytes: number;
      generatedAt: string;
      downloadUrl: string;
      csvUrl?: string;
    }>(
      `/v1/projects/${encodeURIComponent(projectId)}/relatorios/${slug}/generate?${paramKey}=${encodeURIComponent(period)}`,
      { method: 'POST' },
    );
  },

  /**
   * Wave relatorio-share-link · 2026-05-30 — mint/reuse a PUBLIC download
   * link for the latest generated PDF of a slug. The returned `url` works
   * without a session (token is the secret), so the PM can paste it in
   * WhatsApp and the client can actually open it — unlike the old
   * downloadUrl, which sat behind requireAuth and 401'd anyone without a
   * session. Crew-only (the mint endpoint runs allowReadOnly=false).
   */
  shareRelatorio: (
    projectId: string,
    slug: 'boletim-mensal' | 'fluxo-caixa' | 'status-cliente' | 'boletim-semanal',
  ) =>
    request<{ slug: string; period: string; url: string }>(
      `/v1/projects/${encodeURIComponent(projectId)}/relatorios/${slug}/share`,
      { method: 'POST' },
    ),

  /**
   * Build the authenticated download URL for the last generated PDF of a
   * given slug. The route streams the R2 object behind the same auth gate
   * as the listing endpoint. We open it in a new tab via a manual fetch
   * + blob URL so the Bearer token flows.
   */
  openRelatorioPdf: async (
    projectId: string,
    slug: 'boletim-mensal' | 'fluxo-caixa' | 'status-cliente' | 'boletim-semanal', // W148 · 2026-05-28
  ): Promise<{ downloaded: boolean }> => {
    const token = useAuth.getState().token;
    const headers = new Headers();
    if (token) headers.set('authorization', `Bearer ${token}`);
    const res = await fetch(
      `${API_BASE}/v1/projects/${encodeURIComponent(projectId)}/relatorios/${slug}/latest/download.pdf`,
      { headers },
    );
    if (!res.ok) {
      throw new ApiError(
        res.status,
        'relatorio_download_failed',
        `Não foi possível abrir o relatório (HTTP ${res.status}).`,
      );
    }
    const blob = new Blob([await res.arrayBuffer()], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    // Open in a new tab; some browsers block window.open without click,
    // but this method is always called from a user-initiated handler.
    const win = window.open(url, '_blank');
    if (!win) {
      // Fallback: trigger download (e.g. iOS Safari blocks window.open).
      const a = document.createElement('a');
      a.href = url;
      a.download = `${slug}-${projectId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      return { downloaded: true } as const;
    }
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
    return { downloaded: false } as const;
  },

  /**
   * Download the Fluxo de Caixa CSV inline. Not persisted — computed on
   * the fly from current D1 state for the requested month.
   */
  downloadFluxoCaixaCsv: async (projectId: string, period: string): Promise<void> => {
    const token = useAuth.getState().token;
    const headers = new Headers();
    if (token) headers.set('authorization', `Bearer ${token}`);
    const res = await fetch(
      `${API_BASE}/v1/projects/${encodeURIComponent(projectId)}/relatorios/fluxo-caixa/${encodeURIComponent(period)}/download.csv`,
      { headers },
    );
    if (!res.ok) {
      throw new ApiError(
        res.status,
        'fluxo_caixa_csv_failed',
        `Não foi possível baixar o CSV (HTTP ${res.status}).`,
      );
    }
    const blob = new Blob([await res.arrayBuffer()], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `fluxo-caixa-${period}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1_000);
  },

  // ───────── Wave 30.1 · Trena Confidence System (top-down V0) ────────
  //
  // ADR-001 endpoints. Backend lives in apps/api/src/routes/trena.ts and
  // apps/api/src/services/confidence-engine.service.ts. Frontend gates via
  // VITE_TRENA_CONFIDENCE_ENABLED.

  // Wave 53 · 2026-05-23 · createTrenaEstimate com suporte a idempotência.
  // Se idempotencyKey for passado no header o backend retorna a estimate
  // existente em vez de criar duplicata em double-click ou retry.
  createTrenaEstimate: (
    projectId: string,
    args: { name: string; idempotencyKey?: string },
  ) => {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (args.idempotencyKey) headers['Idempotency-Key'] = args.idempotencyKey;
    return request<{
      id: string;
      status: string;
      createdAt: string;
      bootstrap: null;
      idempotent?: boolean;
      /**
       * Wave 98 Bug A — N de estimates já 'exported' (ativas) no projeto. > 0
       * significa que o orçamento já recebeu items de outra estimate; a
       * nova vai ACUMULAR. UI usa esse contador pra mostrar warning.
       */
      existingExportedCount?: number;
    }>(
      `/v1/projects/${projectId}/trena/estimates`,
      { method: 'POST', body: JSON.stringify({ name: args.name }), headers },
    );
  },

  startConfidenceEstimate: (
    projectId: string,
    estimateId: string,
    input: Stage0Input,
  ) =>
    request<ConfidenceEstimate>(
      `/v1/projects/${projectId}/trena/estimates/${estimateId}/confidence/start`,
      { method: 'POST', body: JSON.stringify(input) },
    ),

  // Wave 30.5.3 · "pre-flight" do onboarding doc-first: PM sobe PDF(s), backend
  // roda extractor e devolve sugestões pros 5 campos do Stage 0. NÃO persiste
  // assertions — PM revisa primeiro e confirma via startConfidenceEstimate.
  // Wave 40 · aceita files[] (multi-doc) além dos campos legados.
  suggestStage0FromDoc: (
    projectId: string,
    body: {
      text?: string;
      // Legacy single-file
      fileBase64?: string;
      fileName?: string;
      // Wave 40 · multi-doc
      files?: Array<{ fileBase64: string; fileName: string }>;
    },
  ) =>
    request<Stage0SuggestionsResponse>(
      `/v1/projects/${projectId}/trena/confidence/stage0-suggest`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  /**
   * Wave 73 · reuse existing project PDFs as Trena Stage 0 source documents.
   * Accepts attachment ids already registered for this project.
   */
  suggestStage0FromExisting: (
    projectId: string,
    body: { attachmentIds: string[] },
  ) =>
    request<Stage0SuggestionsResponse>(
      `/v1/projects/${projectId}/trena/confidence/stage0-suggest-from-existing`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  /**
   * Wave 73 · list PDF attachments for a project (for "reuse" panel in Trena Stage 0).
   */
  listTrenaAttachments: (projectId: string) =>
    request<{ attachments: TrenaAttachmentPreview[] }>(
      `/v1/projects/${projectId}/trena/attachments`,
    ),

  assertConfidenceInput: (
    projectId: string,
    estimateId: string,
    body: {
      assertionKey: string;
      assertionValue: string;
      unit?: string | null;
      source: string;
    },
  ) =>
    request<ConfidenceEstimate>(
      `/v1/projects/${projectId}/trena/estimates/${estimateId}/confidence/assertion`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  /**
   * Wave 81 · adiciona um item manual (ex: omissão estimada pelo PM) ao
   * estimate. Não roda motor — só INSERT em estimate_items. Sobrevive a
   * re-finalização do confidence (category='manual_omission' é preservado).
   */
  addManualEstimateItem: (
    projectId: string,
    estimateId: string,
    body: {
      description: string;
      unit?: string;
      quantity: number;
      unit_price_cents: number;
      category?: string;
    },
  ) =>
    request<{ itemId: string; totalCents: number }>(
      `/v1/projects/${projectId}/trena/estimates/${estimateId}/items`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  /**
   * Wave 81 · materializa preview do motor confidence em estimate_items
   * + flipa status pra 'calculated'. Precondição pro /approve + /commit.
   */
  finalizeConfidenceEstimate: (projectId: string, estimateId: string) =>
    request<{
      estimateId: string;
      status: 'calculated';
      totalCents: number;
      totalItems: number;
      alreadyFinalized: boolean;
    }>(
      `/v1/projects/${projectId}/trena/estimates/${estimateId}/confidence/finalize`,
      { method: 'POST' },
    ),

  /**
   * Wave 92 F1.5 · 2026-05-24 · Roda imputer pra criar budget_items
   * source='ia_estimate' pra cada omissão mandatória ausente. Chamado pelo
   * wizard quando PM entra step "Revisar". Idempotente — retorna
   * alreadyImputed=true se já há rows pré-existentes sem novas omissões.
   *
   * Antes (F1): imputer rodava dentro de finalize. PM revisava post-facto
   * no /orcamento. Errado.
   * Agora (F1.5): imputer roda no wizard, items aparecem como cards
   * revisáveis ANTES de finalizar.
   */
  imputeNow: (projectId: string, estimateId: string) =>
    request<{
      itemsImputed: number;
      totalImputedCents: number;
      sources: {
        analogical_median: number;
        sanity_range_p50: number;
        bucket_subitem: number;
        llama_fallback: number;
      };
      items: Array<{
        categoryKey: string;
        displayName: string;
        bucketAffected: string;
        budgetItemId: string;
        plannedCents: number;
        imputedFrom: string;
        imputedConfidence: string;
        rangeMinCents: number;
        rangeMaxCents: number;
      }>;
      alreadyImputed: boolean;
      preexistingCount?: number;
      reason?: string;
    }>(
      `/v1/projects/${projectId}/trena/estimates/${estimateId}/confidence/impute-now`,
      { method: 'POST' },
    ),

  /**
   * Wave 92 F1.5 · 2026-05-24 · Lista budget_items IA pendentes de revisão
   * (source='ia_estimate' AND pm_reviewed_at IS NULL) pra popular cards no
   * TrenaPmQuestionsPanel. Após PM clicar [Aceitar]/[Editar]/[Descartar]
   * o item sai dessa lista.
   */
  getIaPendingReview: (projectId: string, estimateId: string) =>
    request<{
      items: Array<{
        id: string;
        category: string;
        description: string;
        plannedCents: number;
        imputedFrom: string | null;
        imputedConfidence: string | null;
        imputedRangeMinCents: number | null;
        imputedRangeMaxCents: number | null;
        ref: string | null;
      }>;
    }>(
      `/v1/projects/${projectId}/trena/estimates/${estimateId}/ia-pending-review`,
    ),

  getConfidenceEstimate: (projectId: string, estimateId: string) =>
    request<ConfidenceEstimate>(
      `/v1/projects/${projectId}/trena/estimates/${estimateId}/confidence`,
    ),

  // ── Wave 30.5.1 · Bridge extractor → confidence-engine ─────────────────
  // Após upload de PDF/IFC/imagem via extract-pdf, o frontend pega o resultado
  // (proposed.measurements + proposed.assumptions) e manda pra cá. Backend
  // mapeia cada item pra assertion do confidence-engine e roda recompute.
  feedConfidenceFromExtraction: (
    projectId: string,
    estimateId: string,
    body: {
      measurements?: Array<{
        key: string;
        value: number;
        unit?: string;
        confidence?: 'baixa' | 'media' | 'alta';
        evidence?: string | { quote: string };
      }>;
      assumptions?: Array<{
        key: string;
        value: string;
        confidence?: 'baixa' | 'media' | 'alta';
        evidence?: string | { quote: string };
      }>;
      // WAVE 31 · openings (porta/janela) extraídos via text-based ou vision pass.
      // Front passa o array bruto de `proposed.openings` que o handler agrega em
      // assertions numero_portas/numero_janelas/area_esquadrias_total_m2.
      openings?: Array<{
        kind?: string;
        code?: string;
        roomName?: string;
        widthCm?: number;
        heightCm?: number;
        count?: number;
      }>;
      sourceFilename?: string;
      visionUsed?: boolean;
    },
  ) =>
    request<{
      created: Array<{ extractorKey: string; assertionKey: string; value: string; confidence: string }>;
      skipped: Array<{ key: string; reason: string }>;
      estimate: ConfidenceEstimate | null;
    }>(
      `/v1/projects/${projectId}/trena/estimates/${estimateId}/confidence/feed-from-extraction`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  // ── Wave 30.3 · Omission detector report ─────────────────────────────
  // POST (não GET) por consistência com a família /confidence/* e pra
  // deixar espaço pra body opcional (filtros, hipóteses) no futuro.
  getOmissionReport: (projectId: string, estimateId: string) =>
    request<OmissionReport>(
      `/v1/projects/${projectId}/trena/estimates/${estimateId}/confidence/omissions`,
      { method: 'POST', body: JSON.stringify({}) },
    ),

  // ── Wave 30.4 · Telemetria + aprendizado ─────────────────────────────
  getConfidenceTelemetry: (projectId: string, estimateId: string) =>
    request<ConfidenceTelemetrySummary>(
      `/v1/projects/${projectId}/trena/estimates/${estimateId}/confidence/telemetry`,
    ),

  getConfidenceAnalogical: (projectId: string, estimateId: string) =>
    request<AnalogicalLookupResult>(
      `/v1/projects/${projectId}/trena/estimates/${estimateId}/confidence/analogical`,
    ),

  postConfidenceOverride: (
    projectId: string,
    estimateId: string,
    body: ConfidenceOverrideRequest,
  ) =>
    request<{ id: string; ok: true }>(
      `/v1/projects/${projectId}/trena/estimates/${estimateId}/confidence/override`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  // ── Wave 32 · Loop fechado Planejado vs Realizado ────────────────────
  // Buckets actuals pra UMA estimate em execução. ?recompute=1 força
  // recomputo fresh; sem ele lê snapshot persistido pelo cron diário.
  getEstimateActuals: (
    projectId: string,
    estimateId: string,
    opts?: { recompute?: boolean },
  ) =>
    request<EstimateActualsResponse>(
      `/v1/projects/${projectId}/trena/estimates/${estimateId}/actuals${
        opts?.recompute ? '?recompute=1' : ''
      }`,
    ),

  // Sumário global do PM: quantas obras fechou, variância média,
  // bucket pior/melhor, histórico recente. Usado pelo TrenaActualsCard.
  getVarianceSummary: (opts?: { projectIds?: string[] }) =>
    request<VarianceSummaryResponse>(
      `/v1/me/trena/variance-summary${
        opts?.projectIds && opts.projectIds.length > 0
          ? `?projectIds=${encodeURIComponent(opts.projectIds.join(','))}`
          : ''
      }`,
    ),

  // Wave 44 · B12 · Distribuição de gaps de preço PO vs estimativa por projeto.
  getProjectFeedbackSummary: (projectId: string) =>
    request<{ summary: PoFeedbackSummary }>(`/v1/projects/${projectId}/trena/feedback-summary`),

  // ── W123.12 · 2026-05-26 · Closeout conduzido ────────────────────────
  getCloseoutStatus: (projectId: string) =>
    request<{ status: 'planning' | 'in_progress' | 'on_hold' | 'handoff' | 'completed' | 'archived'; actualEndDate: string | null }>(
      `/v1/projects/${projectId}/closeout/status`,
    ),
  getCloseoutChecklist: (projectId: string) =>
    request<CloseoutChecklist>(`/v1/projects/${projectId}/closeout/checklist`),
  startCloseout: (projectId: string) =>
    request<{ status: 'handoff' }>(`/v1/projects/${projectId}/closeout/start`, {
      method: 'POST',
    }),
  signAcceptanceTerm: (
    projectId: string,
    body: { signedByOwner: string; signedAt?: string; notes?: string | null },
  ) =>
    request<{ signed: boolean; signedAt: string }>(
      `/v1/projects/${projectId}/closeout/accept`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  completeCloseout: (projectId: string) =>
    request<{ status: 'completed'; ownerManualUrl: string; ownerManualReady: boolean }>(
      `/v1/projects/${projectId}/closeout/complete`,
      { method: 'POST' },
    ),
  reopenCloseout: (projectId: string, reason?: string) =>
    request<{ status: 'in_progress' }>(
      `/v1/projects/${projectId}/closeout/reopen`,
      { method: 'POST', body: JSON.stringify({ reason }) },
    ),
  // W148 · 2026-05-28 · Preview do Manual antes de entregar (sem efeito colateral).
  getOwnerManualPreview: (projectId: string) =>
    request<{ html: string }>(`/v1/projects/${projectId}/owner-manual/preview`),
  // W148 · 2026-05-28 · Resumo compacto da obra para HojeObraPage.
  getProjectSummaryCard: (projectId: string) =>
    request<{
      statusLabel: string;
      stagesCompleted: number;
      stagesTotal: number;
      monthlySpentCents: number;
      monthlyBudgetCents: number | null;
      nextMilestone: { name: string; date: string } | null;
      criticalCount: number;
    }>(`/v1/projects/${projectId}/summary-card`),

  // W148 · 2026-05-28 · Termo de entrega via link público (cliente confirma, não PM).
  sendDeliveryTermToClient: (projectId: string) =>
    request<{ token: string; signatureUrl: string; sentAt: string }>(
      `/v1/projects/${projectId}/closeout/delivery-signature/send`,
      { method: 'POST' },
    ),
  previewDeliverySignature: (token: string) =>
    request<{
      project: { id: string; name: string; address: string | null; clientEmail: string | null };
      // Confiança · 2026-05-31 — quem pede a confirmação de entrega. Pode vir
      // null em obras antigas; a UI compõe um fallback gracioso.
      requestedBy?: { name: string | null };
      sentAt: string | null;
      alreadySigned: boolean;
      signedAt: string | null;
    }>(
      `/v1/delivery-signature/${encodeURIComponent(token)}`,
      { auth: false }, // padrão 38 — endpoint público, token é o segredo
    ),
  // W151 · P0-7 — aceite agora carrega identificação do signatário
  // (nome + CPF) pra dar valor probatório ao comprovante. Persistência
  // dos campos no backend é pendência sinalizada (migration + UPDATE).
  acceptDeliverySignature: (
    token: string,
    body?: { fullName: string; cpf: string },
  ) =>
    request<{ signedAt: string }>(
      `/v1/delivery-signature/${encodeURIComponent(token)}/accept`,
      {
        method: 'POST',
        auth: false, // padrão 38 — endpoint público
        body: body ? JSON.stringify(body) : undefined,
      },
    ),

  // --- W123.13 · Playground sandbox ---
  playgroundEnter: () =>
    request<{ activeMode: 'playground' }>('/v1/playground/enter', { method: 'POST' }),
  playgroundExit: () =>
    request<{ activeMode: 'real' }>('/v1/playground/exit', { method: 'POST' }),
  playgroundStatus: () =>
    request<{ activeMode: 'real' | 'playground'; hasPlaygroundData: boolean }>(
      '/v1/playground/status',
    ),
  playgroundReset: () =>
    request<{ ok: true; cleared: number }>('/v1/playground/reset', { method: 'POST' }),

  // W146 · 2026-05-27 · Attendance list for QR-code presence page.
  listAttendanceToday: (projectId: string) =>
    request<AttendanceRecord[]>(`/v1/attendance/today?projectId=${encodeURIComponent(projectId)}`),

  // W149 · 2026-05-28 · cross-obra views para buyer e controller.
  getCrossObraCompras: () =>
    request<{
      backlog: Array<{ id: string; project_id: string; project_name: string; description: string; unit: string | null; ref: string | null }>;
      quotations: Array<{ id: string; project_id: string; project_name: string; title: string; status: string; deadline_at: string | null }>;
      purchaseOrders: Array<{ id: string; project_id: string; project_name: string; code: string; status: string; total_cents: number; expected_delivery_at: string | null }>;
      byProject: Array<{ project_id: string; project_name: string; backlog_count: number; quotations_count: number; pos_count: number }>;
    }>('/v1/cross-obra/compras'),

  getCrossObraDinheiro: () =>
    request<{
      byProject: Array<{ project_id: string; project_name: string; committed_cents: number; paid_cents: number }>;
      pendingPayments: Array<{ id: string; project_id: string; project_name: string; code: string; total_cents: number; paid_cents: number }>;
      totals: { committedCents: number; paidCents: number; pendingCents: number };
    }>('/v1/cross-obra/dinheiro'),

  // W149 · 2026-05-28 · LGPD Art. 18 — portabilidade + eliminação.
  exportMyData: () =>
    request<{
      exportedAt: string;
      legalBasis: string;
      profile: unknown;
      projectMemberships: unknown[];
      sessions: unknown[];
      note: string;
    }>('/v1/me/data-export'),

  requestAccountDelete: (reason?: string) =>
    request<{ requested: boolean; processedWithin: string; contactEmail: string }>(
      '/v1/me/account-delete-request',
      { method: 'POST', body: JSON.stringify({ reason: reason ?? null }) },
    ),
};

// ── W123.12 · Closeout types ────────────────────────────────────────────
export type CloseoutChecklist = {
  rnc: { open: number; blocking: boolean };
  fvs: { failedRecent: number; total: number; approved: number; blocking: boolean }; // W148 · 2026-05-28
  payments: { unpaidCents: number; blocking: boolean };
  stock: { remainingItems: number; blocking: false };
  documents: { finalDocsMissing: number; missingKinds: string[]; blocking: boolean };
  photos: { finalPhotosCount: number; blocking: false };
  acceptanceTerm: {
    signed: boolean;
    signedAt: string | null;
    blocking: boolean;
    // W148: novo fluxo via link público
    deliveryToken: string | null;
    deliverySentAt: string | null;
    deliverySignedAt: string | null;
    deliverySignedIp: string | null;
  };
  allBlockersClear: boolean;
};

// ───────── Wave 32 · Planejado vs Realizado types ─────────────────────
// Kept in sync with apps/api/src/services/estimate-actuals.service.ts.
export interface BucketActual {
  estimateId: string;
  bucketCode: string;
  plannedCents: number;
  actualCents: number;
  variancePct: number;
  varianceCents: number;
  sourcePosCount: number;
  sourceMeasurementsCount: number;
  lastRecomputedAt: string;
}

export interface EstimateActualsResponse {
  buckets: BucketActual[];
  totals: {
    plannedCents: number;
    actualCents: number;
    variancePct: number;
    varianceCents: number;
  };
}

export interface VarianceSummaryResponse {
  totalEstimatesClosed: number;
  avgVariancePct: number;
  worstBucket: { code: string; avgVariancePct: number } | null;
  bestBucket: { code: string; avgVariancePct: number } | null;
  history: Array<{
    estimateId: string;
    projectName: string;
    variancePct: number;
    closedAt: string;
  }>;
}

// ───────── Wave 44 · B12 · PO feedback types ────────────────────────────
export interface HistogramBucket {
  from: number;
  to: number;
  label: string;
  count: number;
}

export interface CategoryGapBucket {
  category: string;
  count: number;
  avg_gap_pct: number;
  median_gap_pct: number;
  min_gap_pct: number;
  max_gap_pct: number;
  overrun_rate_pct: number;
}

export interface PoFeedbackSummary {
  project_id: string;
  total_lines_compared: number;
  overall_avg_gap_pct: number;
  categories: CategoryGapBucket[];
  histogram: HistogramBucket[];
  generated_at: string;
}

// ───────── Wave 30.1 · Trena Confidence types (minimal client copy) ────
// Kept in sync with apps/api/src/services/confidence-engine.service.ts.
// Re-declared instead of imported to avoid a backend → frontend import.
export type Tipologia =
  | 'residencial_unifamiliar'
  | 'residencial_multifamiliar'
  | 'comercial'
  | 'industrial';
export type Padrao = 'baixo' | 'medio' | 'alto' | 'luxo' | 'luxo_extremo';

export interface Stage0Input {
  tipologia: Tipologia;
  areaConstruidaM2: number;
  padrao: Padrao;
  regiaoUf: string;
  regiaoCidade?: string | null;
  prazoMeses: number;
  /**
   * Wave 30.5.3 · quando true, backend marca estimate como preview (sem docs)
   * e bloqueia aprovação até docs serem subidos via feed-from-extraction.
   */
  previewMode?: boolean;
}

// Wave 30.5.3 · sugestão pra um único campo do Stage 0 vinda do extractor
// pre-flight. `source` é etiqueta legível ("extraído de Memorial.pdf"),
// `evidence` é citação literal do documento (mostrada num side-panel).
// Wave 40 · alternativeSources presente quando múltiplos docs deram valores distintos.
/**
 * Wave 73 · lightweight attachment preview for the Trena Stage 0 reuse panel.
 */
export interface TrenaAttachmentPreview {
  id: string;
  filename: string;
  mimeType: string;
  sizeBytes: number;
  uploadedAt: string;
  /** 'trena_stage0' when auto-registered from Trena; null when uploaded manually. */
  source: string | null;
  trenaEstimateId: string | null;
}

export interface Stage0Suggestion<T> {
  value: T;
  source: string;
  evidence?: string;
  /** Wave 40 · outros docs que sugeriram valor diferente pro mesmo campo. */
  alternativeSources?: Array<{ fileName: string; value: T; evidence?: string }>;
}

/** Wave 40 · metadados por doc processado (para UI de status). */
export interface Stage0DocSource {
  fileName: string;
  suggestionCount: number;
  processingMs: number;
}

export interface Stage0SuggestionsResponse {
  suggested: {
    tipologia?: Stage0Suggestion<Tipologia>;
    areaConstruidaM2?: Stage0Suggestion<number>;
    padrao?: Stage0Suggestion<Padrao>;
    regiaoUf?: Stage0Suggestion<string>;
    regiaoCidade?: Stage0Suggestion<string>;
    prazoMeses?: Stage0Suggestion<number>;
  };
  rawExtraction: {
    measurements: Array<{
      key: string;
      value: number;
      unit?: string;
      confidence?: 'baixa' | 'media' | 'alta';
      evidence?: string | { quote: string };
    }>;
    assumptions: Array<{
      key: string;
      value: string;
      confidence?: 'baixa' | 'media' | 'alta';
      evidence?: string | { quote: string };
    }>;
  };
  /** Wave 40 · um item por documento enviado. */
  sources?: Stage0DocSource[];
  /** null quando multi-doc (>1 arquivo). */
  sourceFilename: string | null;
  summary: string;
}

export interface UncertaintyBreakdown {
  sistemas_indefinidos: number;
  padrao_por_ambiente: number;
  volatilidade_preco_insumos: number;
  cub_regional_aproximado: number;
  outros: number;
  /** Wave 70 · Bug 8 — fração de variância vinda de omissões priority>=7 (BDI/encargos/etc.). Opcional pra compat com estimativas antigas. */
  indiretos_nao_declarados?: number;
}

export interface ConfidenceBucketSubItem {
  code: string;
  label: string;
  centralCents: number;
  cv: number;
}

export interface ConfidenceBucketEstimate {
  code:
    | 'estrutura'
    | 'alvenaria'
    | 'acabamento'
    | 'instalacoes'
    | 'externos'
    | 'bdi_canteiro';
  label: string;
  centralCents: number;
  lowerCents: number;
  upperCents: number;
  sourcesUsed: string[];
  /**
   * Wave 30.2 · default decomposition per bucket. May be empty for legacy
   * (pre-30.2 persisted) buckets where the server fell back to []; the
   * panel renders a "decomposição não disponível" placeholder in that case.
   */
  subItems?: ConfidenceBucketSubItem[];
}

// Wave 30.2 · Rule-based questions generated by the confidence engine
// (distinct from Wave 29.4 reflection questions which come from the LLM
// pass over the PDF). When the engine refreshes the estimate, it also
// returns a fresh list — ordered DESC by estimatedBandReductionCents.
export type ConfidenceEngineQuestionCategory =
  | 'sistema_construtivo'
  | 'acabamento'
  | 'estrutura'
  | 'instalacao'
  | 'ambiente'
  | 'outro';

export interface ConfidenceEngineQuestionOption {
  value: string;
  label: string;
  impact?: string;
}

export interface ConfidenceEngineQuestion {
  id: string;
  category: ConfidenceEngineQuestionCategory;
  question: string;
  options: ConfidenceEngineQuestionOption[];
  /** Estimated half-band reduction in cents if PM answers ANY option. */
  estimatedBandReductionCents: number;
  source: 'confidence-engine';
}

export interface ConfidenceEstimate {
  estimateId: string;
  centralCents: number;
  lowerCents: number;
  upperCents: number;
  confidenceLevel: number;
  uncertaintyBreakdown: UncertaintyBreakdown;
  methodWeights: { top_down: number; bottom_up: number; analogical: number };
  buckets: ConfidenceBucketEstimate[];
  computedAt: string;
  /** Wave 30.2 · attached by GET/POST confidence endpoints. May be []. */
  contextualQuestions?: ConfidenceEngineQuestion[];
  /**
   * Wave 30.5.3 · true quando estimate foi criada via Stage 0 sem docs (Porta B).
   * Frontend mostra banner persistente e bloqueia botão "aprovar".
   * Backend zera essa flag automaticamente quando feed-from-extraction cria assertions.
   * Lido do estimate row (não vem do confidence_engine).
   */
  previewMode?: boolean;
  /**
   * Wave 43 · Bug 4 — área construída do Stage 0, em m². Incluída pelo
   * GET /confidence pra que TrenaAnalogicalNeighbors possa calcular o
   * R$/m² atual do PM e comparar com vizinhos análogos.
   * Null quando Stage 0 não foi iniciado (não deveria acontecer em Stage 2).
   */
  areaConstruidaM2?: number | null;
  /**
   * Wave 70.1 · Debt 1 — valor da assertion `padrao_global` se o PM já
   * respondeu a pergunta consolidada (ex: "luxo"). Null quando ainda não
   * respondida. Frontend usa pra mostrar "Customizar por ambiente (4)".
   */
  padraoGlobalAssertion?: string | null;
  /**
   * Wave 70.1 · Debt 1 — assertions per-room já salvas (subset das 4 keys:
   * acabamento_cozinha, acabamento_banheiros, acabamento_dormitorios,
   * acabamento_areas_sociais). Vazio quando PM ainda não customizou.
   */
  padraoAmbienteAssertions?: Record<string, string>;
}

// ── Wave 30.3 · Omission detector types ──────────────────────────────
// Em sync com apps/api/src/services/omission-detector.service.ts.
export type OmissionStatusKind = 'presente' | 'ausente' | 'ambiguo' | 'nao_aplicavel';

export interface OmissionStatus {
  category_key: string;
  display_name: string;
  status: OmissionStatusKind;
  detectedAssertion?: string;
  pmQuestion?: string;
  warning?: string;
  bucketAffected: string;
  typicalImpactReais?: { min: number; max: number };
  priority: number;
}

export interface OmissionReport {
  totalCategories: number;
  presente: number;
  ausente: number;
  ambiguo: number;
  naoAplicavel: number;
  topOmissions: OmissionStatus[];
  estimatedHiddenCostReais: { lower: number; upper: number };
}

// ── Wave 30.4 · Telemetria + aprendizado types ───────────────────────
// Em sync com apps/api/src/services/confidence-telemetry.service.ts e
// apps/api/src/services/analogical-lookup.service.ts.

export interface ConfidenceTelemetryBandHistoryEntry {
  at: string;
  centralCents: number;
  lowerCents: number;
  upperCents: number;
}

export interface ConfidenceTelemetrySummary {
  estimateId: string;
  totalInferences: number;
  totalOverrides: number;
  bandHistoryReais: ConfidenceTelemetryBandHistoryEntry[];
  overridesByKind: Record<string, number>;
}

export type AnalogicalStatus = 'projected' | 'in_progress' | 'closed_actual';
export type AnalogicalConfidence = 'baixa' | 'media' | 'alta';

export interface AnalogicalNeighbor {
  id: string;
  tipologia: string;
  padrao: string;
  regionUf: string;
  regionCity?: string | null;
  areaConstruidaM2: number;
  prazoMeses: number;
  costPerM2Cents: number;
  centralCentsEstimated: number;
  centralCentsActual?: number | null;
  bandPct?: number | null;
  status: AnalogicalStatus;
  snapshotAt: string;
  distance: number;
  weightingPct: number;
}

export interface AnalogicalLookupResult {
  k: number;
  estimatedCostPerM2Cents?: number;
  estimatedCentralCents?: number;
  bandHintPct?: number;
  neighbors: AnalogicalNeighbor[];
  confidence: AnalogicalConfidence;
  reasoning: string;
}

export type ConfidenceOverrideKind =
  | 'central_value'
  | 'bucket_value'
  | 'assertion_value'
  | 'sub_item_value'
  | 'reject_estimate';

export type ConfidenceOverrideReasonCategory =
  | 'preco_diferente'
  | 'escopo_diferente'
  | 'experiencia_obra_passada'
  | 'cotacao_recente'
  | 'outro';

export interface ConfidenceOverrideRequest {
  overrideKind: ConfidenceOverrideKind;
  targetPath: string;
  beforeValue?: unknown;
  afterValue?: unknown;
  reasonText?: string;
  reasonCategory?: ConfidenceOverrideReasonCategory;
}

// ─────────────────────────────────────── Obra Hoje · operating queue
export interface ObraHojeQueueItem {
  id: string;
  kind:
    | 'rdo_overdue'
    | 'po_pending_approval'
    | 'compra_backlog'
    | 'po_pending_receipt'   // must_receive — PO ordered/confirmed without receipt
    | 'payment_due'          // must_pay — pending payment near/past due
    | 'blocked'
    | 'fvs_overdue'
    | 'rfi_unresolved'
    | 'budget_delta_warn'
    | 'doc_expiring'
    | 'recent_change'
    // FEATURE C1 (H2.4) · 2026-05-21 — foreman quick-photo capture.
    // Emitted by the obra-hoje aggregator when an estimate room/key still
    // lacks measurements. The QueueItemRow renders an inline "Medir agora"
    // camera CTA that bypasses the Trena wizard entirely.
    | 'measure_pending'
    | 'open_issue'
    | 'budget_pending_approval';
  severity: 'critical' | 'warn' | 'info';
  title: string;
  subtitle?: string;
  context?: Record<string, unknown>;
  href?: string;
  cta?: { label: string; href: string };
  occurredAt?: string | null;
  dueAt?: string | null;
}

export interface ObraHojeBucket {
  id: 'approve_today' | 'must_buy' | 'must_receive' | 'must_pay' | 'late' | 'blocked' | 'changed' | 'background' | 'open_issues' | 'budget_approval';
  title: string;
  items: ObraHojeQueueItem[];
  itemCount: number;
}

/**
 * W123.5 · NextAction — verdade real do estado da obra. Espelha
 * `apps/api/src/services/next-action.service.ts`. Não é só um item
 * dentro de uma bucket; é a UMA ação primária da obra hoje.
 */
export type ProjectNextActionKind =
  | 'generate-stages'
  | 'trena-or-budget'
  | 'create-rdo'
  | 'resolve-pending'
  | 'review-budget'
  | 'send-budget-approval'
  | 'await-approval'
  | 'mark-purchases'
  | 'review-quotation'
  | 'start-obra'
  | 'none';

export interface ProjectNextAction {
  kind: ProjectNextActionKind;
  label: string;
  why: string;
  ctaLabel: string;
  ctaTo: string;
  severity: 'info' | 'warn' | 'crit';
}

export interface ObraHojeQueue {
  projectId: string;
  buckets: ObraHojeBucket[];
  totalItems: number;
  recommendedNextAction: ObraHojeQueueItem | null;
  generatedAt: string;
  /**
   * P0 anti-fantasma · 2026-05-20 · lifecycle gate. ObraHojePage renders
   * a "Obra pronta pra começar" empty state instead of bucket cards when
   * lifecycle !== 'active'. See apps/api/src/lib/project-lifecycle.ts
   * for the source of truth.
   */
  lifecycle: 'active' | 'not_started' | 'completed' | 'archived' | 'missing';
}

// ─────────────────────────── FEATURE B1 · `/hoje cross-obra` (H2.2)
/**
 * Per-project summary inside the cross-obra hub. Surfaces totalItems,
 * the most-loaded bucket and the recommendedNextAction so the PM can
 * scan their portfolio at a glance.
 */
export interface HojeCrossObraProjectSummary {
  projectId: string;
  name: string;
  status: string;
  totalItems: number;
  topBucket: string | null;
  topBucketLabel: string | null;
  recommended: ObraHojeQueueItem | null;
}

/** A single top-3 action ranked across ALL the user's active obras. */
export interface HojeCrossObraTopAction {
  projectId: string;
  projectName: string;
  action: ObraHojeQueueItem;
  /** 1–10 composite urgency score. >=8 red, 5–7 yellow, <5 neutral. */
  urgencyScore: number;
  /** Humanized monetary impact line: "R$ 12,3k bloqueado" / "" if N/A. */
  valueImpact: string;
}

export interface HojeCrossObra {
  generatedAt: string;
  projects: HojeCrossObraProjectSummary[];
  topActionsAcrossAll: HojeCrossObraTopAction[];
  activeProjectCount: number;
  pendingItemsAcrossAll: number;
  truncated: boolean;
}

// ─────────────────────────── FEATURE B2 · Owner Netflix timeline (H3.2)
export type OwnerTimelineEntryType = 'milestone' | 'approval' | 'rnc' | 'photo';

export interface OwnerTimelineEntry {
  id: string;
  type: OwnerTimelineEntryType;
  date: string;
  title: string;
  description: string | null;
  photoUrl?: string | null;
}

export interface OwnerProjectHeader {
  id: string;
  name: string;
  address: string;
  city: string | null;
  status: string;
  coverPhotoUrl: string | null;
  /**
   * Wave geo · 2026-05-21 — Geocoded project coordinates surfaced by the
   * backend so the owner hero can show a static map below the cover
   * photo. `null` until the address has been geocoded successfully.
   */
  lat?: number | null;
  lng?: number | null;
  state?: string | null;
}

export interface OwnerProjectMetrics {
  progressPercent: number;
  realizedCents: number;
  plannedCents: number;
  daysToPromisedEnd: number | null;
}

export interface OwnerNextMilestone {
  id: string;
  title: string;
  plannedDate: string | null;
  daysAway: number | null;
  status: string;
}

export interface OwnerMonthlyReport {
  period: string;
  slug: string;
  hasPdf: boolean;
}

/**
 * W128.3 · 2026-05-26 — Budget approval status surfaced to the owner.
 */
export type OwnerBudgetApprovalStatus =
  | 'none'
  | 'pending'
  | 'expired'
  | 'approved'
  | 'rejected';

export interface OwnerBudgetApproval {
  status: OwnerBudgetApprovalStatus;
  publicUrl: string | null;
  expiresAt: string | null;
  requestedAt: string | null;
}

/**
 * W128.3 · 2026-05-26 — PM contact for the owner's "Falar com PM" CTA.
 */
export interface OwnerProjectPM {
  name: string;
  whatsappNumber: string | null;
  email: string | null;
}

export interface OwnerProjectTimeline {
  project: OwnerProjectHeader;
  metrics: OwnerProjectMetrics;
  timeline: OwnerTimelineEntry[];
  nextMilestone: OwnerNextMilestone | null;
  pendingApprovals: number;
  monthlyReports: OwnerMonthlyReport[];
  /** W128.3 · budget approval status (none/pending/expired/approved/rejected). */
  budgetApproval?: OwnerBudgetApproval;
  /** W128.3 · PM/admin contact for WhatsApp deeplink. */
  pm?: OwnerProjectPM | null;
  generatedAt: string;
}

// ─────────────────────────────────────── Project Activity Feed (Wave FFFFF)

export type ProjectActivityKind =
  | 'rdo_created'
  | 'po_created'
  | 'po_received'
  | 'estimate_edited'
  | 'estimate_committed'
  | 'fvs_passed'
  | 'fvs_failed'
  | 'rfi_opened'
  | 'rfi_answered'
  | 'rnc_created'
  | 'photo_uploaded'
  | 'override_committed'
  | 'doc_uploaded'
  | 'stage_completed'
  | 'budget_changed';

export interface ProjectActivityActor {
  userId: string;
  userName?: string;
  role?: string;
}

export interface ProjectActivityItem {
  id: string;
  occurredAt: string;
  actor: ProjectActivityActor | null;
  kind: ProjectActivityKind;
  summary: string;
  href?: string;
  details?: Record<string, unknown>;
}

export interface ProjectActivityPage {
  items: ProjectActivityItem[];
  hasMore: boolean;
  cursor: string | null;
}

// ─────────────────────────────────────── Project Photo Gallery (Wave NNNNN)

export interface GalleryPhoto {
  id: string;
  r2Key: string;
  url: string; // signed URL valid 1h
  takenAt: string;
  rdoId: string | null;
  rdoDate: string | null;
  rdoNarrativeSnippet: string | null;
  roomId: string | null;
  roomName: string | null;
  uploadedByName: string | null;
  caption: string | null;
  width: number | null;
  height: number | null;
}

export interface GalleryPhotoPage {
  items: GalleryPhoto[];
  nextCursor: string | null;
}

// ─────────────────────────────────────── Trena Estimate Compare (Wave MMMMM)

export interface TrenaCompareEstimateSummary {
  id: string;
  name: string;
  total_cents: number;
  row_count: number;
  created_at: string;
}

export interface TrenaCompareMeasurementEntry {
  key: string;
  before: number | null;
  after: number | null;
  unit: string | null;
  delta: number | null;
  evidence_doc_id?: string | null;
}

export interface TrenaCompareMeasurementChange {
  key: string;
  before: number;
  after: number;
  unit: string;
  delta: number;
  delta_pct: number | null;
  evidence_doc_id?: string | null;
}

export interface TrenaCompareAssumptionEntry {
  key: string;
  before: string | null;
  after: string | null;
  unit: string | null;
  evidence_doc_id?: string | null;
}

export interface TrenaCompareAssumptionChange {
  key: string;
  before: string;
  after: string;
  unit: string | null;
  evidence_doc_id?: string | null;
}

export interface TrenaCompareCompositionEntry {
  code: string;
  description: string;
  quantity: number;
  total_cents: number;
}

export interface TrenaCompareCompositionChange {
  code: string;
  description: string;
  before_qty: number;
  after_qty: number;
  before_cents: number;
  after_cents: number;
  delta_qty: number;
  delta_cents: number;
}

// ─────────────────────────────────────── Wave QQQQQ · revision snapshots

export type TrenaSnapshotTriggerKind = 'calculate' | 'commit' | 'manual' | 'status_change';

export interface TrenaSnapshotDelta {
  totalCentsDelta: number;
  addedRowKeys: string[];
  removedRowKeys: string[];
  changedRowKeys: string[];
}

export interface TrenaSnapshotEntry {
  id: string;
  estimateId: string;
  snapshotAt: string;
  capturedBy: string | null;
  triggerKind: TrenaSnapshotTriggerKind;
  totalCents: number;
  measurementsCount: number;
  assumptionsCount: number;
  compositionsCount: number;
  status: string;
  note: string | null;
  deltaFromPrevious: TrenaSnapshotDelta | null;
}

export interface TrenaSnapshotFull extends TrenaSnapshotEntry {
  payload: {
    version: 1;
    totalCents: number;
    status: string;
    measurements: Array<{ key: string; value: number; unit: string }>;
    assumptions: Array<{ key: string; value: string; unit: string | null }>;
    compositions: Array<{
      code: string;
      description: string;
      quantity: number;
      totalCents: number;
    }>;
  };
}

export interface TrenaEstimateCompareResult {
  left: TrenaCompareEstimateSummary;
  right: TrenaCompareEstimateSummary;
  measurements: {
    added: TrenaCompareMeasurementEntry[];
    removed: TrenaCompareMeasurementEntry[];
    changed: TrenaCompareMeasurementChange[];
  };
  assumptions: {
    added: TrenaCompareAssumptionEntry[];
    removed: TrenaCompareAssumptionEntry[];
    changed: TrenaCompareAssumptionChange[];
  };
  compositions: {
    added: TrenaCompareCompositionEntry[];
    removed: TrenaCompareCompositionEntry[];
    changed: TrenaCompareCompositionChange[];
  };
  totals: {
    left_cents: number;
    right_cents: number;
    delta_cents: number;
    delta_pct: number | null;
  };
  bom_per_m2: {
    left: number | null;
    right: number | null;
    delta: number | null;
  };
}

// ─────────────────────────────────────── Qualidade Hub (Wave UUUU)

export type HandoffRisk = 'baixo' | 'medio' | 'alto';

export interface QualidadeHubSummary {
  openRncCount: number;
  overdueFvsCount: number;
  overdueRfiCount: number;
  blockedStageCount: number;
  recentRejectionsCount: number;
}

export interface QualidadeHubProjectRow {
  projectId: string;
  projectName: string;
  openRncBySeverity: { critical: number; major: number; minor: number };
  overdueFvsCount: number;
  overdueRfiCount: number;
  blockedStageCount: number;
  handoffRisk: HandoffRisk;
}

export interface QualidadeHubRejection {
  entityType: string;
  entityId: string;
  projectId: string;
  projectName: string;
  rejectedAt: string;
  rejectedBy: string;
}

export interface QualidadeHubData {
  summary: QualidadeHubSummary;
  byProject: QualidadeHubProjectRow[];
  recentRejections: QualidadeHubRejection[];
  generatedAt: string;
}

// ─────────────────────────────────────── Feedback (Wave 314L)
export interface FeedbackItem {
  id: string;
  userId: string;
  userFullName: string | null;
  userEmail: string | null;
  kind: 'elogio' | 'problema' | 'sugestao';
  text: string;
  routeContext: string | null;
  status: 'new' | 'accepted' | 'rejected' | 'done';
  curatorNote: string | null;
  curatedAt: string | null;
  curatedByUserId: string | null;
  createdAt: string;
}

// ─────────────────────────────────────── Autonomy types
export interface AutonomyRule {
  id: string;
  tenantId: string;
  kind: string;
  name: string;
  predicate: unknown;
  enabled: boolean;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

// ─────────────────────────────────────── Prumo types
export interface PrumoSource {
  label: string;
  kind: string;
  id?: string;
  url?: string;
}
export interface PrumoAlternative {
  label: string;
  whyRejected: string;
}
export type PrumoKind =
  | 'po_approve'
  | 'rdo_draft'
  | 'medicao_draft'
  | 'rfq_recommend'
  | 'anomaly_resolve';
export type PrumoStatus = 'proposed' | 'approved' | 'declined' | 'expired' | 'executed';
export interface PrumoProposal {
  id: string;
  kind: PrumoKind;
  projectId: string | null;
  /** Wave 322d — server-joined project name; null for cross-project. */
  projectName?: string | null;
  title: string;
  subtitle: string | null;
  payload: Record<string, unknown>;
  sources: PrumoSource[];
  risks: string[];
  alternatives: PrumoAlternative[];
  tier: 1 | 2 | 3;
  accuracyPct: number | null;
  status: PrumoStatus;
  expiresAt: string;
  decidedAt?: string | null;
  decidedBy?: string | null;
  decisionNote?: string | null;
  lastOutcome?: Record<string, unknown> | null;
  createdAt: string;
}

export type SearchKind =
  | 'project'
  | 'daily_log'
  | 'issue'
  | 'supplier'
  | 'purchase_order';

export interface SearchHit {
  kind: SearchKind;
  id: string;
  projectId: string | null;
  title: string;
  subtitle: string | null;
  score: number;
  occurredAt: string;
}

// ─── Wave 391 · Trena sanity-warning + budget total ──────────────────────────

/**
 * Append an admin-override sanity_warning to an estimate.
 * Fire-and-forget safe: the backend is idempotent; errors are non-fatal.
 */
export async function appendEstimateSanityWarning(
  projectId: string,
  estimateId: string,
  body: {
    code: string;
    severity: 'block' | 'warn' | 'info';
    key: string;
    msg: string;
    redBlockerCount: number;
  },
): Promise<{ ok: true; skipped: boolean }> {
  return request<{ ok: true; skipped: boolean }>(
    `/v1/projects/${projectId}/trena/estimates/${estimateId}/sanity-warnings`,
    { method: 'POST', body: JSON.stringify(body) },
  );
}

/**
 * Fetches the planned budget total for a project.
 * Used by the Ready-to-Commit checklist to show delta vs estimate total.
 */
export async function getProjectBudgetTotal(
  projectId: string,
): Promise<{ totalCents: number; itemCount: number }> {
  return request<{ totalCents: number; itemCount: number }>(
    `/v1/projects/${projectId}/budget/total`,
  );
}

// ── Wave 392 · Trena structured evidence types ───────────────────────────────
//
// Canonical TypeScript types for the Trena extraction response and commit payload.
// Defined here (client.ts) as the integration boundary between the wizard and the
// API. The parallel `TrenaStructuredEvidence` type in trena-consolidation.ts is
// intentionally shape-identical; this copy lets consumers import from `@/api/client`
// without pulling in the full consolidation module.

/**
 * Structured evidence object attached to each extracted measurement / assumption
 * in the extract-pdf / extract-ifc response AND the commit payload body.
 *
 * `quote` is mandatory. All other fields are optional:
 *   - `docId`     — R2 object key of the source document.
 *   - `page`      — 1-based PDF page number (absent for IFC).
 *   - `bbox`      — [x0,y0,x1,y1] bounding box in PDF points (absent for IFC).
 *   - `textHash`  — SHA-1 hex of the surrounding text block, or 'unverified'.
 *   - `globalIds` — IFC PRODUCT GlobalIds that contributed (IFC only).
 */
export interface TrenaEvidencePayload {
  quote: string;
  docId?: string;
  page?: number;
  bbox?: [number, number, number, number];
  textHash?: string;
  globalIds?: string[];
}

/** One measurement entry in the extract response and commit payload. */
export interface TrenaExtractMeasurementEntry {
  key: string;
  value: number;
  unit: string;
  confidence: 'alta' | 'media' | 'baixa';
  evidence: TrenaEvidencePayload;
}

/** One assumption entry in the extract response and commit payload. */
export interface TrenaExtractAssumptionEntry {
  key: string;
  value: string;
  unit?: string | null;
  confidence: 'alta' | 'media' | 'baixa';
  evidence: TrenaEvidencePayload;
}

/**
 * Wave 392 — fetch a Trena evidence document's signed URL from R2.
 *
 * Returns a short-lived (~5 min) signed URL for the PDF/IFC/image stored
 * in the TRENA_DOCS R2 bucket. The URL is valid for display via pdfjs or
 * the IFC viewer without an Authorization header (the proxy validates the
 * HMAC token in `?sig=`).
 *
 * Use the `useEvidenceDoc` react-query hook instead of calling this directly.
 */
// WAVE 28.3 cleanup · request() em client.ts:492 já faz unwrap do `data:` envelope.
// Caller agora recebe shape direto (sem precisar de .data).
export async function getTrenaEvidenceDoc(
  projectId: string,
  estimateId: string,
  docKey: string,
): Promise<{ signedUrl: string; docType: 'pdf' | 'ifc' | 'image'; expiresAt: string }> {
  return request<{ signedUrl: string; docType: 'pdf' | 'ifc' | 'image'; expiresAt: string }>(
    `/v1/projects/${projectId}/trena/estimates/${estimateId}/documents/${docKey}`,
  );
}

// ───────────────────────────── FEATURE C1 · Trena quick-photo (H2.4)
//
// Bypass do wizard pra capturar foto direto da câmera (PWA intent
// `capture="environment"`). O backend cria uma estimate dedicada,
// roda OCR via Workers AI, extrai medidas e devolve a lista. O cliente
// usa o `useAuth` token padrão; o body é multipart/form-data porque
// `request<T>()` só faz JSON.

export interface QuickPhotoExtractedMeasurement {
  id: string;
  key: string;
  value: number;
  unit: string;
  confidence: string;
  evidence: string;
}

export interface QuickPhotoUploadResponse {
  photoId: string;
  estimateId: string;
  docKey: string | null;
  extractedMeasurements: QuickPhotoExtractedMeasurement[];
  ocrChars: number;
}

export async function uploadTrenaQuickPhoto(
  projectId: string,
  file: File | Blob,
): Promise<QuickPhotoUploadResponse> {
  const state = useAuth.getState();
  const token = state.token;
  if (!token) {
    throw new ApiError(401, 'session_expired', 'Sua sessão expirou. Faça login novamente.');
  }
  const form = new FormData();
  // O backend só lê `file`; nome do arquivo é meramente cosmético.
  const filename = (file as File).name ?? `quick-photo-${Date.now()}.jpg`;
  form.append('file', file, filename);

  const res = await fetch(
    `${API_BASE}/v1/projects/${encodeURIComponent(projectId)}/trena/quick-photo`,
    {
      method: 'POST',
      headers: { authorization: `Bearer ${token}` },
      body: form,
    },
  );
  if (!res.ok) {
    let code = 'http_error';
    let message = `HTTP ${res.status}`;
    try {
      const body = await res.json() as { error?: string | { code?: string; message?: string } };
      if (typeof body.error === 'string') { code = body.error; message = body.error; }
      else if (body.error && typeof body.error === 'object') {
        code = body.error.code ?? code;
        message = body.error.message ?? message;
      }
    } catch { /* noop */ }
    throw new ApiError(res.status, code, message);
  }
  const json = await res.json() as { data: QuickPhotoUploadResponse };
  return json.data;
}

export async function confirmTrenaQuickPhoto(
  projectId: string,
  estimateId: string,
  measurementIds?: string[],
): Promise<{ estimateId: string; confirmed: number }> {
  return request<{ estimateId: string; confirmed: number }>(
    `/v1/projects/${encodeURIComponent(projectId)}/trena/quick-photo/${encodeURIComponent(estimateId)}/confirm`,
    {
      method: 'POST',
      body: JSON.stringify(measurementIds ? { measurementIds } : {}),
    },
  );
}

export async function uploadPhotoFile(
  signed: RequestUploadResponse,
  file: File | Blob,
): Promise<void> {
  const res = await fetch(signed.uploadUrl, {
    method: signed.method,
    headers: signed.headers,
    body: file,
  });
  if (!res.ok) {
    let message = `Upload failed (${res.status})`;
    try {
      const body = await res.json() as { error?: { message?: string } };
      if (body.error?.message) message = body.error.message;
    } catch {
      /* noop */
    }
    throw new ApiError(res.status, 'upload_failed', message);
  }
}

// ─── Broker / Supplier auction API (LR-W4) ───────────────────────────

export interface BrokerInvitation {
  invite_id: string;
  round_id: string;
  invite_status: string;
  channel: string;
  invited_at: string | null;
  viewed_at: string | null;
  accepted_terms_at: string | null;
  response_token: string | null;
  token_expires_at: string | null;
  modality: string;
  round_status: string;
  started_at: string | null;
  ended_at: string | null;
  intent_title: string;
  freight_terms: string | null;
  project_name: string | null;
  tokenExpired: boolean;
}

export interface BrokerBid {
  bid_id: string;
  round_id: string;
  item_id: string;
  price_cents: number;
  freight_cents: number | null;
  delivery_days: number | null;
  notes: string | null;
  placed_at: string;
  rejected_at: string | null;
  rejection_reason: string | null;
  item_description: string;
  item_quantity: number;
  item_unit: string;
  round_modality: string;
  round_status: string;
  round_started_at: string | null;
  round_ended_at: string | null;
  intent_title: string;
  project_name: string | null;
}

export interface BrokerHistoryRow {
  round_id: string;
  modality: string;
  round_status: string;
  started_at: string | null;
  ended_at: string | null;
  intent_title: string;
  freight_terms: string | null;
  project_name: string | null;
  invite_status: string;
  response_token: string | null;
  was_awarded: number;
  my_best_price_cents: number | null;
  market_best_price_cents: number | null;
  bid_count: number;
  won: boolean;
  outcome: 'won' | 'lost' | 'cancelled';
  deltaToWinnerCents: number | null;
  deltaToWinnerPercent: number | null;
}

export interface BrokerHistorySummary {
  totalRounds: number;
  wonRounds: number;
  winRate: number;
  totalWonCents: number;
  supplierId: string;
}

export interface BrokerRoundItem {
  id: string;
  description: string;
  quantity: number;
  unit: string;
  ceiling_price_cents: number | null;
  best_price_cents: number | null;
  my_latest_price_cents: number | null;
  myRank: number | null;
}

export interface BrokerRoundDetail {
  invite: {
    id: string;
    status: string;
    responseToken: string | null;
    tokenExpired: boolean;
    acceptedTermsAt: string | null;
  };
  round: {
    id: string;
    title: string;
    modality: string;
    status: string;
    startedAt: string | null;
    endedAt: string | null;
    configuration: Record<string, unknown>;
    freightTerms: string;
    projectName: string | null;
  };
  items: BrokerRoundItem[];
  award: {
    id: string;
    status: string;
    totalCents: number;
    awardedAt: string | null;
  } | null;
  supplierId: string;
}

export const brokerApi = {
  /** List invitations for the authenticated supplier */
  listInvitations: (status: 'all' | 'pending' | 'responded' | 'expired' = 'all') =>
    request<{ rows: BrokerInvitation[]; supplierId: string }>(
      `/v1/supplier/auctions/invitations?status=${status}`,
    ),

  /** List bids placed by the authenticated supplier */
  listBids: (roundId?: string) =>
    request<{ rows: BrokerBid[]; supplierId: string }>(
      `/v1/supplier/auctions/bids${roundId ? `?roundId=${roundId}` : ''}`,
    ),

  /** Win/loss history with summary stats */
  getHistory: (from?: string, to?: string) => {
    const params = new URLSearchParams();
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    const qs = params.toString();
    return request<{ rows: BrokerHistoryRow[]; summary: BrokerHistorySummary }>(
      `/v1/supplier/auctions/history${qs ? `?${qs}` : ''}`,
    );
  },

  /** Supplier-scoped view of one round */
  getRound: (roundId: string) =>
    request<BrokerRoundDetail>(
      `/v1/supplier/auctions/rounds/${roundId}`,
    ),
};

// ── Wave 47 · Generative Engineering API client ───────────────────

export interface GenerativeDraft {
  id: string;
  tenantId: string;
  createdBy: string;
  prompt: string;
  status: 'pending' | 'drafting' | 'done' | 'error';
  outputPlanta: string | null;
  outputMemorialMd: string | null;
  outputOrcamento: {
    tipologia: string;
    area_m2: number;
    padrao: 'simples' | 'medio' | 'alto' | 'luxo';
    total_estimado_cents: number;
    aviso: string;
    buckets: Array<{ categoria: string; percentual: number; valor_cents: number }>;
  } | null;
  outputCronograma: {
    meses: number;
    aviso: string;
    atividades: Array<{ mes: number; nome: string; duracao_dias: number }>;
  } | null;
  outputRfqs: Array<{ item: string; qtd: number; unidade: string }> | null;
  llmCostCents: number;
  durationMs: number | null;
  errorMessage: string | null;
  createdAt: string;
  completedAt: string | null;
}

export const generativeApi = {
  /** POST /v1/generative/draft-project — V1 deprecated (Wave 47). */
  draftProject: (prompt: string) =>
    request<GenerativeDraft>('/v1/generative/draft-project', {
      method: 'POST',
      body: JSON.stringify({ prompt }),
    }),

  /** GET /v1/generative/drafts — lista recente do tenant. */
  listDrafts: () =>
    request<GenerativeDraft[]>('/v1/generative/drafts'),

  /** GET /v1/generative/drafts/:id — busca draft específico. */
  getDraft: (id: string) =>
    request<GenerativeDraft>(`/v1/generative/drafts/${id}`),

  /**
   * Wave 64 · POST /v1/generative/draft-project-v2
   *
   * V2: LLM EXTRAI semântica do prompt, CONFIDENCE-ENGINE calcula valor real
   * (top-down CUB SINDUSCON + analogical k-NN). Memorial NUNCA inventa R$.
   */
  draftProjectV2: (prompt: string) =>
    request<GenerativeDraftV2>('/v1/generative/draft-project-v2', {
      method: 'POST',
      body: JSON.stringify({ prompt }),
    }),

  /**
   * Wave 67 F2.4 · GET /v1/generative/admin/history
   *
   * Lista últimos 50 drafts V2 (admin only).
   * @param promptSimilar substring match no prompt original
   * @param status 'converted' | 'preview' — filtra por status de promoção
   */
  listHistory: (params?: { promptSimilar?: string; status?: 'converted' | 'preview' }) => {
    const qs = new URLSearchParams();
    if (params?.promptSimilar) qs.set('promptSimilar', params.promptSimilar);
    if (params?.status) qs.set('status', params.status);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return request<GenerativeDraftHistoryItem[]>(`/v1/generative/admin/history${suffix}`);
  },

  /**
   * Wave 67 F2.4 · GET /v1/generative/admin/history/:draftId
   *
   * Detalhe completo de um draft V2 (admin only).
   */
  getHistoryDetail: (draftId: string) =>
    request<GenerativeDraftHistoryDetail>(`/v1/generative/admin/history/${draftId}`),

  /**
   * Wave 68 F2.2 · POST /v1/generative/recompute-from-brief
   *
   * PM editou campos do Brief inline. Engine roda sem nova LLM call.
   * Memorial permanece pinned. Retorna estimativa atualizada.
   */
  recomputeFromBrief: (draftId: string, brief: GenerativeBriefV2) =>
    request<{ estimate: GenerativeEstimateV2 }>('/v1/generative/recompute-from-brief', {
      method: 'POST',
      body: JSON.stringify({ draftId, brief }),
    }),
};

// ── Wave 64 · Generative V2 types ─────────────────────────────────

export interface GenerativeBriefV2 {
  tipologia: 'residencial_unifamiliar' | 'residencial_multifamiliar' | 'comercial' | 'industrial';
  areaConstruidaM2: number;
  padrao: 'baixo' | 'medio' | 'alto' | 'luxo' | 'luxo_extremo';
  regiaoUf: string;
  regiaoCidade: string | null;
  prazoMeses: number;
  sistemasEspeciais: string[];
  descricaoArquitetonica: string;
  comodos: string[];
  premissasDeclaradas: Array<{ chave: string; valor: string; razao: string }>;
  lacunas: string[];
  termosCitados: string[];
}

export interface GenerativeLayoutHintV2 {
  nome: string;
  areaEstimadaM2: number;
  adjacencia: string;
  notaAcabamento: string;
}

export interface GenerativeEstimateBucketV2 {
  code: string;
  label: string;
  centralCents: number;
  lowerCents: number;
  upperCents: number;
}

export interface GenerativeEstimateV2 {
  estimateId: string;
  centralCents: number;
  lowerCents: number;
  upperCents: number;
  confidenceLevel: number;
  uncertaintyBreakdown: Record<string, number>;
  methodWeights: { top_down: number; bottom_up: number; analogical: number };
  buckets: GenerativeEstimateBucketV2[];
  computedAt: string;
}

export interface GenerativeDraftV2 {
  id: string;
  tenantId: string;
  createdBy: string;
  prompt: string;
  status: 'done' | 'error';
  brief: GenerativeBriefV2;
  estimate: GenerativeEstimateV2;
  memorialMd: string;
  layout: GenerativeLayoutHintV2[];
  durationMs: number;
  errorMessage: string | null;
  createdAt: string;
  completedAt: string;
}

// ── Wave 67 F2.4 · Generative History types ────────────────────────

export interface GenerativeDraftHistoryItem {
  id: string;
  promptTruncated: string;
  tipologia: string | null;
  padrao: string | null;
  areaConstruidaM2: number | null;
  centralCents: number | null;
  bandLowCents: number | null;
  bandHighCents: number | null;
  confidencePctBand: number | null;
  engineEstimateId: string | null;
  status: 'converted' | 'preview';
  createdAt: string;
}

export interface GenerativeDraftHistoryDetail {
  id: string;
  prompt: string;
  brief: GenerativeBriefV2 | null;
  estimate: GenerativeEstimateV2 | null;
  layout: GenerativeLayoutHintV2[] | null;
  memorialMd: string | null;
  engineEstimateId: string | null;
  status: 'converted' | 'preview';
  durationMs: number | null;
  errorMessage: string | null;
  createdAt: string;
  completedAt: string | null;
}

// ── Wave chat-mvp · DM chat API client ────────────────────────────

export interface ChatConversation {
  conversationId: string;
  peerId: string;
  peerName: string;
  peerAvatarUrl: string | null;
  lastMessageAt: string;
  lastMessagePreview: string;
  unreadCount: number;
  peerOnline: boolean;
}

export interface ChatMessage {
  id: string;
  authorUserId: string | null;
  authorName: string;
  body: string;
  createdAt: string;
  readAt: string | null;
  isMe: boolean;
}

export interface ChatThread {
  conversationId: string | null;
  messages: ChatMessage[];
}

export const chatApi = {
  listConversations: () =>
    request<ChatConversation[]>('/v1/me/conversations'),

  getThread: (userId: string) =>
    request<ChatThread>(`/v1/me/messages/thread/${encodeURIComponent(userId)}`),

  sendMessage: (recipientUserId: string, body: string) =>
    request<{ messageId: string; conversationId: string }>('/v1/me/messages/send', {
      method: 'POST',
      body: JSON.stringify({ recipientUserId, body }),
    }),

  markRead: (userId: string) =>
    request<{ markedCount: number }>(`/v1/me/conversations/${encodeURIComponent(userId)}/mark-read`, {
      method: 'POST',
    }),
};
